import { contextLatencyHint, metersEnabled, scheduleLookaheadSeconds } from '../audio/playbackProfile';
import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useWebSocket } from './WebSocketContext';
import { API_URL } from '../config/api';
import { normalizeStorageKey, storageKeyFromUrl } from '../utils/storageKey';
import { isTrackInSessionScope } from '../utils/trackSessionScope';
import assetResolver from '../utils/AssetResolver';
import { getSessionVersion, setSessionVersion } from '../utils/sessionVersion';
import { fetchCanonicalSessionSnapshot } from '../utils/sessionSave';
import {
  assessTimelineSaveSafety,
  mergeTimelinePersistenceSnapshots,
  persistenceValueEqual
} from '../utils/timelinePersistence';
import { transportClock } from '../audio/transportClock';
import { encodeWav, interleaveChannels, DEFAULT_BIT_DEPTH, SUPPORTED_BIT_DEPTHS } from '../audio/wavEncode';
import { eventPitches } from '../utils/noteEvents';
import { instrumentNotesByTrack } from '../instrument/hostedNotes';
import { planSessionEdit } from '../ai/sessionEditPlan';
import { resolveLoopRegion, wrapTransportBeat, clampVelocity, contextTimeAtPerformanceTime, transportBeatAtContextTime, scheduleClockWindow, audibleContextTime } from '../audio/transportMath';
import {
  AUTOMATION_PARAMS,
  normalizeAutomationPoints,
  valueAtBeat,
  pointsInRange
} from '../audio/automationMath';
import { MicRecorder } from '../audio/micRecorder';
import { clearSchedulerInterval, setSchedulerInterval } from '../audio/schedulerClock';
import {
  clampZoom,
  scaleZoom,
  snapResolution,
  snapBeat,
  gridDivisionForZoom,
  DEFAULT_PIXELS_PER_BEAT,
  ZOOM_STEP
} from '../audio/zoomModel';
import { migrateClipsToSamples, rebaseClipsToTempo, getTimelineStartSample, getLengthSamples, positionPatch } from '../timeline/clipPosition';
import { resolveTrimStart, resolveTrimEnd } from '../timeline/clipTrim';
import { deleteAudioRangeFromClips } from '../timeline/rangeDelete';
import { splitPatternClipData } from '../timeline/patternSplit';
import { normalizeLaneClipOverlaps, resolveLaneClipPlacement } from '../timeline/clipCollision';
import { patternVoices } from '../audio/patternVoices';
import { detectLoopBpm } from '../audio/bpmDetection';
import {
  pitchShiftChannelsPreserveDuration,
  stretchChannelsPreservePitch,
  limitChannelsTruePeak,
  limitChannelsSamplePeakFallback
} from '../audio/samplePreparationClient';
import { extendPatternClipToData, extendDrumPatternDataToDuration } from '../utils/patternClip';
import { collectDrumLaneSounds, drumLaneTrackId, expandGroupedDrumClip, expandGroupedDrumClips, patchDrumLaneClipData } from '../utils/drumLaneClips';
import { drumSampleBufferKey, resolveDrumSampleBuffer } from '../utils/drumSamplePersistence';
import {
  DRUM_VOICE_GAIN,
  applyDrumVoiceEnvelope,
  configureDrumBus,
  drumLaneRole,
  drumVoiceEnvelope,
  fadeAndStopDrumVoice,
  placeScheduledChokeVoice,
  shouldChokeDrumLane
} from '../audio/drumSignalChain';
import { createTempoMap, DEFAULT_PPQ, beatToSampleExact, sampleToBeatExact } from '../timeline/timebase';
import {
  normalizeBuses,
  normalizeSends,
  resolveOutputTarget,
  routingTargetChanged,
  nextBusName,
  clampSendLevel,
  MASTER_OUTPUT
} from '../audio/busRouting';
import NativeInstrumentEngine from '../instrument/NativeInstrumentEngine';
import { createDefaultInstrumentPatch } from '../instrument/patchSchema';
import { partitionLiveEditSchedule } from '../audio/auditionInvalidation';
import { analyzeRenderedMix } from '../audio/renderedMixAnalysis';
import { activateAudioContext } from '../audio/audioContextActivation';
import { persistedLoopFields } from '../ai/libraryLoops';
import { preparePeakPyramid } from '../audio/peakPyramid';
import { createSamplePlaybackPool } from '../audio/samplePlaybackNode';
import { drumHitVariation, HUMANIZE_DEFAULT } from '../audio/hitVariation';
import { previewWindow } from '../audio/previewWindow';
import { duckTargetsFor, normalizeDuck, resetDuck, scheduleDuck } from '../audio/sidechainDuck';
import { automationLanesForRender, renderAutomation } from '../audio/automationMath';

const AudioContext = createContext();
const TIMEBASE_PPQ = 96;
const SESSION_PATCH_SOFT_LIMIT_BYTES = 4.5 * 1024 * 1024;
// Keep enough audio queued to survive an occasional browser layout/GC pause.
// This does not add output latency: Web Audio still starts each source at its
// exact clock time, and live edits cancel/re-prime their affected schedule.
const SCHEDULE_LOOKAHEAD_SECONDS = scheduleLookaheadSeconds();
const CLIP_STOP_FADE_SECONDS = 0.008; // Declick when a sounding clip is stopped early
const SCHEDULER_INTERVAL_MS = 25; // Scheduler tick cadence (window is refilled each tick)
const DEFAULT_PLAYBACK_BUFFER_FRAMES = 512;
const PLAYBACK_BUFFER_STORAGE_KEY = 'cookup_playback_buffer_frames';
const VALID_PLAYBACK_BUFFER_FRAMES = new Set([256, 512]);

const rustSamplerEnabled = () => {
  try {
    return globalThis.__COOKUP_RUST_SAMPLER__ !== false
      && localStorage.getItem('cookup_rust_sampler') !== 'disabled';
  } catch (_) {
    return true;
  }
};

const stableSamplerToken = (value) => {
  const text = String(value || 'event');
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) || 1;
};

const uniqueDecodedBuffers = (buffers) => [...new Set(Object.values(buffers || {}).filter(Boolean))];

const createRustScheduledSource = (pool, owner, endTime, context) => {
  let ended = false;
  let timer = null;
  const source = {
    onended: null,
    disconnect: () => {},
    stop: () => {
      if (ended) return;
      pool.cancelOwner(owner);
      ended = true;
      if (timer !== null) clearTimeout(timer);
      source.onended?.();
    }
  };
  const delayMs = Math.max(0, (endTime - context.currentTime) * 1000) + 25;
  timer = setTimeout(() => {
    if (ended) return;
    ended = true;
    source.onended?.();
  }, delayMs);
  return source;
};

const readPlaybackBufferFrames = () => {
  try {
    const stored = Number(localStorage.getItem(PLAYBACK_BUFFER_STORAGE_KEY));
    return VALID_PLAYBACK_BUFFER_FRAMES.has(stored) ? stored : DEFAULT_PLAYBACK_BUFFER_FRAMES;
  } catch (_) {
    return DEFAULT_PLAYBACK_BUFFER_FRAMES;
  }
};
const MIN_PLAYBACK_RATE = 0.25;
const MAX_PLAYBACK_RATE = 4.0;
const DRUM_CHOKE_FADE_MS = 8;
const SESSION_PAYLOAD_DROP_KEYS = new Set([
  'audioBuffer',
  'buffer',
  'sourceBuffer',
  'decodedBuffer',
  'offlineBuffer',
  'waveform',
  'waveformData',
  'peaks',
  'channelData',
  'arrayBuffer',
  'blob',
  'file',
  'fileData',
  'rawData',
  'pcmData',
  'analyserData',
  'renderCache'
]);

const serializeSessionPayload = (value) => JSON.stringify(value, (key, currentValue) => {
  if (SESSION_PAYLOAD_DROP_KEYS.has(key)) return undefined;
  return currentValue;
});

const getSerializedBytes = (serialized) => {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(serialized).length;
  }
  return serialized.length;
};

const analyserPeak = (analyser) => {
  if (!analyser) return null;
  const samples = new Float32Array(analyser.fftSize);
  analyser.getFloatTimeDomainData(samples);
  let peak = 0;
  for (let i = 0; i < samples.length; i += 1) {
    peak = Math.max(peak, Math.abs(samples[i]));
  }
  return peak;
};

const pickPrimitive = (value) => (
  value === null
  || typeof value === 'string'
  || typeof value === 'number'
  || typeof value === 'boolean'
);

const CLIP_PERSIST_ALLOWLIST = [
  'id',
  'name',
  'clipType',
  'trackId',
  'sourceTrackId',
  'startBeat',
  'durationBeats',
  'audioOffset',
  // Sample-authoritative positions for audio clips (decimal strings).
  'timelineStartSample',
  'lengthSamples',
  'sourceStartSample',
  'color',
  'muted',
  'volume',
  'gain',
  'pan',
  'rate',
  'pitch',
  'pitchSemitones',
  'pitchCents',
  'stretchRatio',
  'tempoMatched',
  'sourceBpm',
  'bpmConfidence',
  'clipGainDb',
  'fadeInBeats',
  'fadeOutBeats',
  'normalizeMode',
  'reverse',
  'isReversed',
  'loop',
  'loopStartBeat',
  'loopEndBeat',
  'fileUrl',
  'storageKey',
  'sourceType',
  'assetId',
  'assetVersion',
  'sampleName',
  'sampleId',
  'midiKey',
  'createdAt',
  'updatedAt',
  'clipData'
];

const clampNumber = (value, min, max, fallback = min) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return fallback;
  return Math.max(min, Math.min(max, num));
};

const dbToGain = (db) => {
  const safeDb = clampNumber(db, -60, 24, 0);
  return Math.pow(10, safeDb / 20);
};

const createHannWindow = (size) => {
  const window = new Float32Array(size);
  for (let i = 0; i < size; i += 1) {
    window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / Math.max(1, size - 1)));
  }
  return window;
};

const linearResampleChannel = (input, ratio) => {
  if (!input || input.length === 0) return new Float32Array(0);
  const safeRatio = Math.max(0.125, Math.min(8, ratio || 1));
  const outputLength = Math.max(1, Math.floor(input.length / safeRatio));
  const output = new Float32Array(outputLength);
  for (let i = 0; i < outputLength; i += 1) {
    const sourceIndex = i * safeRatio;
    const indexA = Math.floor(sourceIndex);
    const indexB = Math.min(input.length - 1, indexA + 1);
    const frac = sourceIndex - indexA;
    output[i] = (input[indexA] * (1 - frac)) + (input[indexB] * frac);
  }
  return output;
};

const linearResizeChannel = (input, targetLength) => {
  const safeTarget = Math.max(1, Math.round(Number(targetLength) || input?.length || 1));
  if (!input || input.length === 0) return new Float32Array(safeTarget);
  if (safeTarget === input.length) return new Float32Array(input);
  const output = new Float32Array(safeTarget);
  const scale = (input.length - 1) / Math.max(1, safeTarget - 1);
  for (let i = 0; i < safeTarget; i += 1) {
    const sourceIndex = i * scale;
    const indexA = Math.floor(sourceIndex);
    const indexB = Math.min(input.length - 1, indexA + 1);
    const fraction = sourceIndex - indexA;
    output[i] = (input[indexA] * (1 - fraction)) + (input[indexB] * fraction);
  }
  return output;
};

const computeOverlapCorrelation = (signal, aStart, bStart, overlapLength) => {
  let dot = 0;
  let aEnergy = 0;
  let bEnergy = 0;
  for (let i = 0; i < overlapLength; i += 1) {
    const a = signal[aStart + i] || 0;
    const b = signal[bStart + i] || 0;
    dot += a * b;
    aEnergy += a * a;
    bEnergy += b * b;
  }
  const denom = Math.sqrt(aEnergy * bEnergy) + 1e-9;
  return dot / denom;
};

const computeWsolaSegments = (reference, stretchFactor, targetLength, grainSize = 2048, overlapRatio = 0.75) => {
  const safeTarget = Math.max(1, targetLength || reference.length || 1);
  const safeStretch = Math.max(0.25, Math.min(4, stretchFactor || 1));
  const overlap = Math.max(128, Math.min(grainSize - 64, Math.floor(grainSize * overlapRatio)));
  const synthesisHop = Math.max(64, grainSize - overlap);
  const analysisHopNominal = Math.max(16, Math.floor(synthesisHop / safeStretch));
  const searchRadius = Math.max(64, Math.floor(analysisHopNominal * 0.5));
  const maxInStart = Math.max(0, (reference?.length || 0) - grainSize);

  const segments = [];
  if (!reference || reference.length === 0 || maxInStart <= 0) {
    segments.push({ inPos: 0, outPos: 0 });
    return { segments, grainSize };
  }

  let prevInPos = 0;
  let outPos = 0;
  segments.push({ inPos: 0, outPos: 0 });

  while (true) {
    outPos += synthesisHop;
    if (outPos >= safeTarget) break;

    const predicted = prevInPos + analysisHopNominal;
    const minCandidate = Math.max(0, Math.min(maxInStart, predicted - searchRadius));
    const maxCandidate = Math.max(0, Math.min(maxInStart, predicted + searchRadius));
    const prevOverlapStart = prevInPos + synthesisHop;

    let bestInPos = Math.max(0, Math.min(maxInStart, predicted));
    let bestScore = -Infinity;

    for (let candidate = minCandidate; candidate <= maxCandidate; candidate += 16) {
      const score = computeOverlapCorrelation(reference, prevOverlapStart, candidate, overlap);
      if (score > bestScore) {
        bestScore = score;
        bestInPos = candidate;
      }
    }

    prevInPos = bestInPos;
    segments.push({ inPos: prevInPos, outPos });
    if (prevInPos >= maxInStart) break;
  }

  return { segments, grainSize };
};

const synthesizeWithSegments = (input, segments, targetLength, grainSize) => {
  const safeTarget = Math.max(1, targetLength || input.length || 1);
  const output = new Float32Array(safeTarget);
  const normalizer = new Float32Array(safeTarget);
  const window = createHannWindow(grainSize);

  segments.forEach(({ inPos, outPos }) => {
    for (let i = 0; i < grainSize; i += 1) {
      const srcIndex = inPos + i;
      const dstIndex = outPos + i;
      if (srcIndex >= input.length || dstIndex >= safeTarget) break;
      const w = window[i];
      output[dstIndex] += input[srcIndex] * w;
      normalizer[dstIndex] += w;
    }
  });

  for (let i = 0; i < safeTarget; i += 1) {
    const norm = normalizer[i];
    if (norm > 1e-7) {
      output[i] /= norm;
    }
  }

  return output;
};

const createPitchShiftedBufferPreserveDuration = (audioCtx, sourceBuffer, pitchRatio) => {
  if (!audioCtx || !sourceBuffer) return sourceBuffer;
  const safeRatio = Math.max(0.125, Math.min(8, pitchRatio || 1));
  if (Math.abs(safeRatio - 1) < 0.0001) return sourceBuffer;

  const targetLength = sourceBuffer.length;
  const output = audioCtx.createBuffer(
    sourceBuffer.numberOfChannels,
    targetLength,
    sourceBuffer.sampleRate
  );

  const reference = linearResampleChannel(sourceBuffer.getChannelData(0), safeRatio);
  if (!reference || reference.length === 0) return sourceBuffer;
  const { segments, grainSize } = computeWsolaSegments(reference, safeRatio, targetLength);

  for (let channel = 0; channel < sourceBuffer.numberOfChannels; channel += 1) {
    const src = sourceBuffer.getChannelData(channel);
    const resampled = linearResampleChannel(src, safeRatio);
    const stretched = synthesizeWithSegments(resampled, segments, targetLength, grainSize);
    output.copyToChannel(stretched, channel);
  }

  return output;
};

const createTimeStretchedBufferPreservePitch = (audioCtx, sourceBuffer, stretchRatio) => {
  if (!audioCtx || !sourceBuffer) return sourceBuffer;
  const safeStretch = Math.max(0.25, Math.min(4, Number(stretchRatio) || 1));
  const targetLength = Math.max(1, Math.round(sourceBuffer.length * safeStretch));
  if (targetLength === sourceBuffer.length) return sourceBuffer;
  const output = audioCtx.createBuffer(
    sourceBuffer.numberOfChannels,
    targetLength,
    sourceBuffer.sampleRate
  );
  // Sub-half-percent corrections are chiefly decoder/sample-boundary drift.
  // Exact-length interpolation avoids putting an almost-unity signal through
  // WSOLA's grain search (which can move individual transients unnecessarily).
  if (Math.abs(safeStretch - 1) < 0.005) {
    for (let channel = 0; channel < sourceBuffer.numberOfChannels; channel += 1) {
      output.copyToChannel(linearResizeChannel(sourceBuffer.getChannelData(channel), targetLength), channel);
    }
    return output;
  }

  const reference = sourceBuffer.getChannelData(0);
  const { segments, grainSize } = computeWsolaSegments(reference, safeStretch, targetLength);
  for (let channel = 0; channel < sourceBuffer.numberOfChannels; channel += 1) {
    const stretched = synthesizeWithSegments(
      sourceBuffer.getChannelData(channel),
      segments,
      targetLength,
      grainSize
    );
    output.copyToChannel(stretched, channel);
  }
  return output;
};

const getAudioClipPlaybackSettings = (clip, beatsPerSecond) => {
  const pitchSemitones = clampNumber(clip?.pitchSemitones, -36, 36, 0);
  const pitchCents = clampNumber(clip?.pitchCents, -100, 100, 0);
  const sessionBpm = Math.max(1, beatsPerSecond * 60);
  const detectedStretch = clip?.tempoMatched && Number(clip?.sourceBpm) > 0
    ? (Number(clip.sourceBpm) / sessionBpm) * clampNumber(clip?.stretchRatio, 0.25, 4, 1)
    : clip?.stretchRatio;
  const stretchRatio = clampNumber(detectedStretch, 0.25, 4, 1);
  const baseRate = clampNumber(clip?.rate, MIN_PLAYBACK_RATE, MAX_PLAYBACK_RATE, 1);
  const pitchRatio = Math.pow(2, (pitchSemitones + (pitchCents / 100)) / 12);
  const preserveTempoPitch = Boolean(clip?.tempoMatched);
  const playbackRate = clampNumber(
    preserveTempoPitch ? baseRate : (baseRate / stretchRatio),
    MIN_PLAYBACK_RATE,
    MAX_PLAYBACK_RATE,
    1
  );
  const clipGain = dbToGain(clip?.clipGainDb ?? 0);
  const fadeInBeats = clampNumber(clip?.fadeInBeats, 0, 128, 0);
  const fadeOutBeats = clampNumber(clip?.fadeOutBeats, 0, 128, 0);
  const fadeInSeconds = beatsPerSecond > 0 ? (fadeInBeats / beatsPerSecond) : 0;
  const fadeOutSeconds = beatsPerSecond > 0 ? (fadeOutBeats / beatsPerSecond) : 0;
  const reverse = Boolean(clip?.reverse || clip?.isReversed);
  return {
    playbackRate,
    stretchRatio,
    preserveTempoPitch,
    pitchRatio,
    clipGain,
    fadeInSeconds,
    fadeOutSeconds,
    reverse
  };
};

const getDrumRowPlaybackSettings = (row = {}) => {
  const tuneSemitones = clampNumber(row?.tuneSemitones, -24, 24, 0);
  const trimStartPct = clampNumber(row?.trimStartPct, 0, 0.95, 0);
  const trimEndPctRaw = clampNumber(row?.trimEndPct, 0.05, 1, 1);
  const trimEndPct = Math.max(trimEndPctRaw, Math.min(1, trimStartPct + 0.01));
  const attackMs = clampNumber(row?.attackMs, 0, 200, 2);
  const releaseMs = clampNumber(row?.releaseMs, 1, 500, 10);
  const playbackMode = row?.playbackMode === 'oneshot' ? 'oneshot' : 'drum';
  // Round-robin emulation strength for this lane (0 turns it off).
  const humanize = clampNumber(row?.humanize, 0, 1, HUMANIZE_DEFAULT);
  return {
    playbackMode,
    humanize,
    tuneSemitones,
    trimStartPct,
    trimEndPct,
    attackMs,
    releaseMs
  };
};

const sanitizePatternClipData = (clipData) => {
  if (!clipData || typeof clipData !== 'object') return null;

  const sanitized = {};
  if (clipData.kind === 'instrument') sanitized.kind = 'instrument';
  if (typeof clipData.instrumentTrackId === 'string') sanitized.instrumentTrackId = clipData.instrumentTrackId;
  if (Number.isInteger(clipData.drumLaneIndex)) sanitized.drumLaneIndex = clipData.drumLaneIndex;
  if (typeof clipData.drumLaneKey === 'string') sanitized.drumLaneKey = clipData.drumLaneKey;
  if (typeof clipData.name === 'string') sanitized.name = clipData.name.slice(0, 120);
  if (clipData.pattern && typeof clipData.pattern === 'object' && !Array.isArray(clipData.pattern)) {
    sanitized.pattern = clipData.pattern;
  }
  if (clipData.noteData && typeof clipData.noteData === 'object' && !Array.isArray(clipData.noteData)) {
    sanitized.noteData = clipData.noteData;
  }
  if (Number.isFinite(Number(clipData.patternLength))) {
    sanitized.patternLength = Math.max(16, Math.floor(Number(clipData.patternLength)));
  }
  if (clipData.customSamples && typeof clipData.customSamples === 'object' && !Array.isArray(clipData.customSamples)) {
    sanitized.customSamples = clipData.customSamples;
  }
  if (clipData.rowViewModes && typeof clipData.rowViewModes === 'object' && !Array.isArray(clipData.rowViewModes)) {
    sanitized.rowViewModes = clipData.rowViewModes;
  }
  if (clipData.drumCutMode && typeof clipData.drumCutMode === 'object' && !Array.isArray(clipData.drumCutMode)) {
    sanitized.drumCutMode = clipData.drumCutMode;
  }
  if (Array.isArray(clipData.drumSounds)) {
    sanitized.drumSounds = clipData.drumSounds;
  }
  if (Array.isArray(clipData.noteEvents)) {
    sanitized.noteEvents = clipData.noteEvents;
  }
  if (clipData.noteEventsAuthoritative === true) sanitized.noteEventsAuthoritative = true;
  if (Number.isFinite(Number(clipData.ppq))) {
    sanitized.ppq = Math.max(24, Math.floor(Number(clipData.ppq)));
  }
  if (Number.isFinite(Number(clipData.bpm))) {
    sanitized.bpm = Math.max(1, Math.floor(Number(clipData.bpm)));
  }

  return Object.keys(sanitized).length > 0 ? sanitized : null;
};

const sanitizeClipForSession = (clip) => {
  if (!clip || typeof clip !== 'object') return null;
  const sanitized = {};
  for (const key of CLIP_PERSIST_ALLOWLIST) {
    const value = clip[key];
    if (key === 'clipData') {
      if (clip.clipType !== 'pattern') continue;
      const compactClipData = sanitizePatternClipData(value);
      if (compactClipData) sanitized.clipData = compactClipData;
      continue;
    }
    if (!pickPrimitive(value)) continue;
    if (value === undefined) continue;
    sanitized[key] = value;
  }
  if (!sanitized.id || !sanitized.clipType) return null;
  if (sanitized.clipType === 'pattern' && !sanitized.clipData) return null;
  if (!Number.isFinite(Number(sanitized.startBeat))) sanitized.startBeat = 0;
  if (!Number.isFinite(Number(sanitized.durationBeats))) sanitized.durationBeats = 1;
  return sanitized;
};

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

export const AudioProvider = ({ children }) => {
  const [audioContext, setAudioContext] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [micArmed, setMicArmed] = useState(false); // Mic capture armed (records on play)
  const [isMicRecording, setIsMicRecording] = useState(false);
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState('default');
  const selectedAudioOutputIdRef = useRef('default');
  const [audioOutputError, setAudioOutputError] = useState('');
  const [selectedAudioInputId, setSelectedAudioInputIdState] = useState(() => {
    try { return localStorage.getItem('cookup_audio_input') || 'default'; } catch (_) { return 'default'; }
  });
  const [micInputError, setMicInputError] = useState('');
  const [bpm, setBpmState] = useState(120);
  const [tracks, setTracks] = useState([]);
  const [isMuted, setIsMuted] = useState(false); // Timeline mute state
  const [playbackBufferFrames, setPlaybackBufferFramesState] = useState(readPlaybackBufferFrames);
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [countInEnabled, setCountInEnabled] = useState(true);
  const [isCountInActive, setIsCountInActive] = useState(false);
  const [playheadPosition, setPlayheadPosition] = useState(0); // Current playback position in beats
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopStartBeat, setLoopStartBeat] = useState(0);
  const [loopEndBeat, setLoopEndBeat] = useState(4);
  const [timelineClips, setTimelineClips] = useState([]); // Clips on timeline

  // Refs to persist data across StrictMode remounts (like DrumContext does)
  const tracksRef = useRef([]);
  const timelineClipsRef = useRef([]);

  // Mix buses: return buses (fed by post-fader sends) and group buses (tracks
  // route their whole output in). Both always land on master.
  const [buses, setBusesState] = useState([]);
  const busesRef = useRef([]);
  const sendNodesRef = useRef(new Map()); // `${trackId}->${busId}` → GainNode

  const setBuses = useCallback((updater) => {
    setBusesState((prev) => {
      const raw = typeof updater === 'function' ? updater(prev) : updater;
      const next = normalizeBuses(raw);
      busesRef.current = next;
      return next;
    });
  }, []);

  // Automation lanes: { trackId: { paramId: { points: [{ beat, value }] } } }
  const [automation, setAutomationState] = useState({});
  const automationRef = useRef({});
  useEffect(() => {
    automationRef.current = automation;
  }, [automation]);

  // Derives from the ref (single writer) and updates it synchronously, so a
  // save fired right after a commit persists the new points.
  const setTrackAutomationPoints = useCallback((trackId, paramId, points) => {
    if (!trackId || !AUTOMATION_PARAMS[paramId]) return;
    const normalized = normalizeAutomationPoints(paramId, points);
    const prev = automationRef.current || {};
    const trackLanes = { ...(prev[trackId] || {}) };
    if (normalized.length === 0) {
      delete trackLanes[paramId];
    } else {
      trackLanes[paramId] = { points: normalized };
    }
    const next = { ...prev };
    if (Object.keys(trackLanes).length === 0) {
      delete next[trackId];
    } else {
      next[trackId] = trackLanes;
    }
    automationRef.current = next;
    setAutomationState(next);
  }, []);

  // These setters update refs before React renders. Persistence reads the refs,
  // so a save triggered in the same tick as an edit cannot serialize the
  // previous render's timeline.
  const setTracksSynced = useCallback((updater) => {
    const previous = tracksRef.current;
    const next = typeof updater === 'function' ? updater(previous) : updater;
    tracksRef.current = next;
    setTracks(next);
  }, []);

  const setTimelineClipsSynced = useCallback((updater) => {
    const previous = timelineClipsRef.current;
    const next = typeof updater === 'function' ? updater(previous) : updater;
    if (next === previous) return;
    const normalized = normalizeLaneClipOverlaps(expandGroupedDrumClips(next)).map((clip) => {
      if (clip?.clipType !== 'audio' || clip.lengthSamples == null) return clip;
      const map = sessionTempoMap();
      const visibleLength = getLengthSamples({ ...clip, lengthSamples: null }, map);
      const position = positionPatch({
        startSample: getTimelineStartSample(clip, map),
        lengthSamples: visibleLength
      }, map);
      const unchanged = Object.keys(position).every((key) => position[key] === clip[key]);
      return unchanged ? clip : {
        ...clip,
        ...position
      };
    });
    timelineClipsRef.current = normalized;
    setTimelineClips(normalized);
  }, []);

  const cloneClipForHistory = useCallback((clip) => {
    if (!clip || typeof clip !== 'object') return clip;
    let clipData = clip.clipData;
    if (clip.clipType === 'pattern' && clipData && typeof clipData === 'object') {
      try {
        clipData = JSON.parse(JSON.stringify(clipData));
      } catch (_) {
        clipData = clip.clipData;
      }
    }
    return { ...clip, clipData };
  }, []);

  const cloneTimelineSnapshot = useCallback((clips = timelineClipsRef.current || []) => (
    (Array.isArray(clips) ? clips : []).map(cloneClipForHistory)
  ), [cloneClipForHistory]);

  const clearTimelineHistory = useCallback(() => {
    if (timelineGestureHistoryRef.current.timer) {
      clearTimeout(timelineGestureHistoryRef.current.timer);
    }
    timelineGestureHistoryRef.current = { token: null, timer: null };
    timelineUndoStackRef.current = [];
    timelineRedoStackRef.current = [];
  }, []);

  const pushTimelineUndoSnapshot = useCallback(() => {
    if (timelineHistoryLockRef.current) return;
    timelineUndoStackRef.current.push(cloneTimelineSnapshot());
    if (timelineUndoStackRef.current.length > MAX_TIMELINE_HISTORY_ENTRIES) {
      timelineUndoStackRef.current.shift();
    }
    timelineRedoStackRef.current = [];
  }, [cloneTimelineSnapshot]);

  const captureTimelineHistoryForGesture = useCallback((token) => {
    if (!token) return;
    const state = timelineGestureHistoryRef.current;
    if (state.token !== token) {
      pushTimelineUndoSnapshot();
      state.token = token;
    }
    if (state.timer) {
      clearTimeout(state.timer);
    }
    state.timer = setTimeout(() => {
      if (timelineGestureHistoryRef.current.token === token) {
        timelineGestureHistoryRef.current.token = null;
      }
      timelineGestureHistoryRef.current.timer = null;
    }, 220);
  }, [pushTimelineUndoSnapshot]);

  const applyTimelineSnapshot = useCallback((snapshot) => {
    if (!Array.isArray(snapshot)) return false;
    timelineHistoryLockRef.current = true;
    try {
      setTimelineClipsSynced(cloneTimelineSnapshot(snapshot));
      return true;
    } finally {
      timelineHistoryLockRef.current = false;
    }
  }, [cloneTimelineSnapshot, setTimelineClipsSynced]);

  const undoTimeline = useCallback(() => {
    if (timelineUndoStackRef.current.length === 0) return false;
    const previous = timelineUndoStackRef.current.pop();
    timelineRedoStackRef.current.push(cloneTimelineSnapshot());
    return applyTimelineSnapshot(previous);
  }, [applyTimelineSnapshot, cloneTimelineSnapshot]);

  const redoTimeline = useCallback(() => {
    if (timelineRedoStackRef.current.length === 0) return false;
    const next = timelineRedoStackRef.current.pop();
    timelineUndoStackRef.current.push(cloneTimelineSnapshot());
    return applyTimelineSnapshot(next);
  }, [applyTimelineSnapshot, cloneTimelineSnapshot]);

  const [snapEnabled, setSnapEnabled] = useState(true); // Grid snapping on/off
  const [snapValue, setSnapValue] = useState(1); // Default to a quarter note (1 beat); users can select finer/coarser divisions.
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true); // Auto-scroll during playback
  const [returnToStartEnabled, setReturnToStartEnabledState] = useState(() => {
    try {
      const saved = localStorage.getItem('cookup_return_to_start');
      return saved == null ? true : saved === 'true';
    } catch (_) {
      return true;
    }
  });
  const [transportStartBeat, setTransportStartBeat] = useState(0);
  const returnToStartEnabledRef = useRef(returnToStartEnabled);
  const transportStartBeatRef = useRef(0);
  const [pixelsPerBeat, setPixelsPerBeat] = useState(DEFAULT_PIXELS_PER_BEAT); // Zoom level (px per beat)
  const [trackHeight, setTrackHeight] = useState(60); // Track row height
  const [masterVolume, setMasterVolume] = useState(1.0); // Master fader level (0-1)
  const audioBuffers = useRef(new Map());
  const pendingLoads = useRef([]);
  const queuedTrackWarnedRef = useRef(new Set());
  const missingBufferWarnedRef = useRef(new Set());
  const sessionLoadGenerationRef = useRef(0);
  const masterGainRef = useRef(null); // Master gain for timeline
  const masterLimiterRef = useRef(null); // Brickwall-style safety limiter on the master bus
  const masterAnalyserRef = useRef(null); // Master analyser for level metering
  const drumBusInputRef = useRef(null); // Pattern drums: DC blocker → bus compressor → master
  const metronomeGainRef = useRef(null); // Metronome click gain (independent bus)
  const metronomeTimerRef = useRef(null);
  const metronomeClicksRef = useRef([]); // Queued clicks: { osc, time }
  const metronomeNextBeatRef = useRef(0);
  const countInTimeoutRef = useRef(null);
  const trackAnalysersRef = useRef(new Map()); // Per-track analysers: trackId → analyserNode
  const trackNodesRef = useRef(new Map()); // Persistent per-track nodes: trackId → { audibilityGain, userGain, panNode }
  const playbackStartTime = useRef(null); // When playback started
  const playbackStartBeat = useRef(0); // Beat position when playback started
  const playbackRebasedRef = useRef(false); // Anchor moved by a live tempo/loop change
  // Transport shape the running scheduler works in. Tempo, loop and timeline
  // mute changes rebase it in place rather than restarting the scheduler, so a
  // tempo nudge no longer stops everything that is sounding.
  const transportShapeRef = useRef({ bps: 2, region: null, muted: false });
  const transportDiagRef = useRef({ armed: false }); // one-shot playback-latency probe
  const bpmRef = useRef(120);
  const scheduledSources = useRef([]); // Active audio sources
  const scheduledPatternSourcesRef = useRef(new Map()); // clipId -> scheduled sources
  const scheduledPatternHitsRef = useRef(new Set()); // Dedup realtime pattern scheduling per transport run
  const scheduledDrumLaneVoicesRef = useRef(new Map()); // Click-safe mono/choke state across scheduler slices
  const patternEventOrderRef = useRef(new WeakMap()); // CookUp noteEvents array -> whether startTick order supports binary range lookup
  const schedulerStateRef = useRef(null); // Rolling lookahead scheduler horizon { horizonTime, horizonBeat, loopIteration, primed }
  const schedulerTickRef = useRef(null); // Live tick fn — lets seek/live-edit re-prime synchronously (no audible gap)
  const audioPerformanceProbeRef = useRef({ active: false });
  const playStartPendingRef = useRef(false); // Safari: prevent duplicate starts while AudioContext.resume() settles
  const micRecorderRef = useRef(null); // Armed MicRecorder instance (worklet PCM capture)
  const micRecordAnchorRef = useRef(null); // { time, beat, bps } the take was performed against
  const micRecordStartBeatRef = useRef(0);
  const selectedAudioInputIdRef = useRef(selectedAudioInputId);
  const trackFxLatencyRef = useRef(new Map()); // trackId → FX chain latency in seconds (plugin delay compensation)
  const drumBuffersRef = useRef(null); // Reference to drum buffers
  const samplePlaybackPoolRef = useRef(null); // Rust sample renderer; Web Audio remains per-hit fallback
  const drumLanePoolsRef = useRef(new Map()); // One output per drum lane, through its mixer/FX strip
  const drumLanePoolsPendingRef = useRef(new Map());
  const samplePlaybackReadyRef = useRef(false); // Prevent mixed Rust/Web Audio choke groups
  const samplePlaybackUsageRef = useRef({ rustPatternHits: 0, webAudioPatternHits: 0 });
  // Instrument sample buffers, published by InstrumentContext (which renders
  // inside this provider and so cannot be read from here). The offline mixdown
  // needs them: without registering them on its own engines, sample-layer
  // instruments render silent in an export.
  const instrumentSampleBuffersRef = useRef(new Map()); // trackId -> Map(bufferId -> AudioBuffer)
  const aiCandidateBufferCacheRef = useRef(new Map()); // durable asset identity -> AudioBuffer
  // The exact timelineClips array produced by the most recent live-recording
  // write. Audition invalidation skips it: a captured note sits at a beat the
  // transport has already passed, so rebuilding the schedule for it only
  // stutters playback.
  const recordingWriteClipsRef = useRef(null);
  const drumVolumesRef = useRef({});
  const timelineUndoStackRef = useRef([]);
  const timelineRedoStackRef = useRef([]);
  const timelineHistoryLockRef = useRef(false);
  const timelineGestureHistoryRef = useRef({ token: null, timer: null });
  const reversedAudioBuffersRef = useRef(new Map());
  const pitchedAudioBuffersRef = useRef(new Map());
  const stretchedAudioBuffersRef = useRef(new Map());
  const samplePreparationJobsRef = useRef(new Map());
  const lastPersistedTimelineCountsRef = useRef({ clips: 0, tracks: 0 });
  const lastPersistedTimelineSnapshotRef = useRef({
    initialized: false,
    snapshot: { timelineClips: [], tracks: [], automation: {}, buses: [] }
  });
  const MAX_TIMELINE_HISTORY_ENTRIES = 80;
  const { subscribeMessages, sessionId: wsSessionId, sendSessionUpdate } = useWebSocket();

  const isStaleSessionLoad = (expectedLoadGeneration) => (
    Number.isFinite(expectedLoadGeneration) && expectedLoadGeneration !== sessionLoadGenerationRef.current
  );

  // Initialize once; listener wiring is intentionally mount-scoped.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    bpmRef.current = bpm;
  }, [bpm]);

  useEffect(() => { selectedAudioInputIdRef.current = selectedAudioInputId; }, [selectedAudioInputId]);

  const refreshAudioInputs = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) {
      setAudioInputDevices([]);
      return [];
    }
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const outputs = allDevices.filter(device => device.kind === 'audiooutput');
      setAudioOutputDevices(outputs.map((device, index) => ({ id: device.deviceId, label: device.label || `Audio output ${index + 1}` })));
      const ctx = audioContextRef.current;
      if (ctx && typeof ctx.setSinkId === 'function') {
        let id = selectedAudioOutputIdRef.current;
        if (id !== 'default' && !outputs.some(device => device.deviceId === id)) {
          id = 'default'; selectedAudioOutputIdRef.current = id; setSelectedAudioOutputId(id);
        }
        // Resolve the default alias to its concrete device so a system output
        // change updates an already-running context instead of keeping an old route.
        const systemDefault = outputs.find(device => device.deviceId === 'default');
        const concreteDefault = systemDefault?.groupId && outputs.find(device => device.groupId === systemDefault.groupId && !['default', 'communications'].includes(device.deviceId));
        try {
          await ctx.setSinkId(id === 'default' ? concreteDefault?.deviceId || '' : id);
          setAudioOutputError('');
        } catch (error) { setAudioOutputError(error.message || 'Could not switch audio output.'); }
      }
      const devices = allDevices
        .filter((device) => device.kind === 'audioinput')
        .map((device, index) => ({
          id: device.deviceId,
          label: device.label || `Audio input ${index + 1}`,
          groupId: device.groupId || ''
        }));
      setAudioInputDevices(devices);
      return devices;
    } catch (error) {
      setMicInputError(error?.message || 'Could not read audio inputs.');
      return [];
    }
  }, []);

  useEffect(() => {
    void refreshAudioInputs();
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener) return undefined;
    const handleDeviceChange = () => { void refreshAudioInputs(); };
    mediaDevices.addEventListener('devicechange', handleDeviceChange);
    return () => mediaDevices.removeEventListener('devicechange', handleDeviceChange);
  }, [refreshAudioInputs]);

  const audioContextRef = useRef(null);
  useEffect(() => {
    audioContextRef.current = audioContext;
  }, [audioContext]);

  const setAudioOutputDevice = useCallback(async (id) => {
    const ctx = audioContextRef.current;
    if (!ctx || typeof ctx.setSinkId !== 'function') {
      setAudioOutputError('This browser uses the output selected in your system sound settings.');
      return;
    }
    try {
      await ctx.setSinkId(id === 'default' ? '' : id);
      selectedAudioOutputIdRef.current = id;
      setSelectedAudioOutputId(id);
      setAudioOutputError('');
    } catch (error) { setAudioOutputError(error.message || 'Could not switch audio output.'); }
  }, []);

  const chooseAudioOutput = useCallback(async () => {
    if (!navigator.mediaDevices?.selectAudioOutput) {
      setAudioOutputError('Choose an available output here, or select headphones in your system sound settings.');
      void refreshAudioInputs();
      return;
    }
    try {
      const device = await navigator.mediaDevices.selectAudioOutput();
      await setAudioOutputDevice(device.deviceId);
      await refreshAudioInputs();
    } catch (error) {
      if (error.name !== 'NotAllowedError') setAudioOutputError(error.message || 'Could not choose audio output.');
    }
  }, [refreshAudioInputs, setAudioOutputDevice]);

  useEffect(() => { if (audioContext) void refreshAudioInputs(); }, [audioContext, refreshAudioInputs]);

  const setBpm = useCallback((value, options = {}) => {
    const nextBpmRaw = Number(value);
    if (!Number.isFinite(nextBpmRaw) || nextBpmRaw <= 0) return;
    const nextBpm = Math.max(40, Math.min(300, Math.round(nextBpmRaw)));
    const prevBpm = bpmRef.current || 120;
    const shouldScaleAudioClips = options.scaleAudioClips !== false;

    if (nextBpm === prevBpm) return;

    // Audio clips are anchored in TIME, not in beats: keep their sample
    // positions and re-derive the beat fields against the new tempo. This
    // replaces the old ratio-scaling of beats, which accumulated float error
    // on every tempo change. Pattern clips are musical and follow the tempo.
    if (shouldScaleAudioClips && timelineClipsRef.current.length > 0) {
      const sampleRate = audioContextRef.current?.sampleRate || 48000;
      const previousMap = createTempoMap({
        sampleRate, ppq: DEFAULT_PPQ, events: [{ tick: 0, bpm: prevBpm }]
      });
      const nextMap = createTempoMap({
        sampleRate, ppq: DEFAULT_PPQ, events: [{ tick: 0, bpm: nextBpm }]
      });
      const rebasedClips = rebaseClipsToTempo(timelineClipsRef.current, previousMap, nextMap);
      timelineClipsRef.current = rebasedClips;
      setTimelineClips(rebasedClips);
    }

    // Rebase transport refs to the audible beat before effects re-run with the new
    // tempo, so the metronome and scheduler stay continuous through a bpm change.
    if (playbackStartTime.current !== null && audioContextRef.current) {
      playbackStartTime.current = audioContextRef.current.currentTime;
      playbackStartBeat.current = transportClock.getBeat();
    }

    bpmRef.current = nextBpm;
    setBpmState(nextBpm);
  }, []);

  // Listen for timeline updates from other users. Each message is applied as
  // it arrives through a subscription: reading the `messages` array made
  // every socket message (presence, cursors, chat) re-render this provider and
  // all of its consumers, and applied only the last message of a batched
  // render, silently dropping any other remote edit that arrived with it.
  const applyRemoteMessageRef = useRef(null);
  applyRemoteMessageRef.current = (lastMessage) => {

    if (lastMessage && lastMessage.type === 'session_update') {
      const data = lastMessage;

      // Imported audio is broadcast as one exact track/clip operation. The
      // track bytes are loaded by DAWInterface; applying the persisted clip
      // here avoids every collaborator independently reconstructing a slightly
      // different clip from duration/BPM while that asynchronous load runs.
      if (data.payload && data.payload.type === 'track_added' && data.payload.clip) {
        const { clip } = data.payload;
        setTimelineClipsSynced((prev) => (
          prev.some((entry) => entry.id === clip.id) ? prev : [...prev, clip]
        ));
      }

      // Handle timeline clip added by other user
      if (data.payload && data.payload.type === 'timeline_add_clip') {
        const { clip } = data.payload;
        // console.log('📥 Received timeline clip from other user:', clip);

        // Add clip to local state (avoid duplicates)
        setTimelineClipsSynced(prev => {
          if (prev.find(c => c.id === clip.id)) {
            return prev; // Already have this clip
          }
          return [...prev, clip];
        });
      }

      // Handle clips pasted by other user
      if (data.payload && data.payload.type === 'timeline_clips_pasted') {
        const { clips } = data.payload;
        // console.log(`📥 Received ${clips.length} pasted clip(s) from other user`);

        setTimelineClipsSynced(prev => {
          // Add new clips that don't already exist
          const newClips = clips.filter(clip => !prev.find(c => c.id === clip.id));
          return [...prev, ...newClips];
        });
      }

      // Handle clips duplicated by other user
      if (data.payload && data.payload.type === 'timeline_clips_duplicated') {
        const { clips } = data.payload;
        // console.log(`📥 Received ${clips.length} duplicated clip(s) from other user`);

        setTimelineClipsSynced(prev => {
          // Add new clips that don't already exist
          const newClips = clips.filter(clip => !prev.find(c => c.id === clip.id));
          return [...prev, ...newClips];
        });
      }

      // Handle clips split by other user
      if (data.payload && data.payload.type === 'timeline_clips_split') {
        const { clips, removedClipIds } = data.payload;
        // console.log(`📥 Received split operation from other user at beat ${splitPosition}`);

        setTimelineClipsSynced(prev => {
          // Remove original clips using the explicit removedClipIds
          let updated = prev.filter(c => !removedClipIds.includes(c.id));

          // Add split clips that don't already exist
          const newClips = clips.filter(clip => !prev.find(c => c.id === clip.id));
          return [...updated, ...newClips];
        });
      }

      // Handle clip trimmed by other user
      if (data.payload && data.payload.type === 'timeline_clip_trimmed') {
        const { clipId, clip } = data.payload;
        // console.log(`📥 Received trim operation from other user: ${clipId} (${edge})`);

        setTimelineClipsSynced(prev =>
          prev.map(c => c.id === clipId ? clip : c)
        );
      }

      // Handle clips deleted by other user
      if (data.payload && data.payload.type === 'timeline_clips_deleted') {
        const { clipIds } = data.payload;
        // console.log(`📥 Received delete operation from other user: ${clipIds.length} clip(s)`);

        setTimelineClipsSynced(prev =>
          prev.filter(c => !clipIds.includes(c.id))
        );
      }

      // Handle pattern clip updates by other user
      if (data.payload && data.payload.type === 'timeline_pattern_updated') {
        const { clipId, clipData } = data.payload;
        // console.log(`📥 Received pattern update for clip ${clipId}`);

        setTimelineClipsSynced(prev => {
          const source = prev.find((clip) => clip.id === clipId);
          if (!source) return prev;
          if (source.clipData?.drumLaneIndex === 0 && !Number.isInteger(clipData?.drumLaneIndex)) {
            const split = expandGroupedDrumClip({ ...source, trackId: 'pattern-track', clipData });
            const dataById = new Map(split.map((clip) => [clip.id, clip.clipData]));
            return prev.map((clip) => dataById.has(clip.id)
              ? { ...clip, clipData: dataById.get(clip.id) } : clip);
          }
          return prev.map((clip) => clip.id === clipId ? { ...clip, clipData } : clip);
        });
      }

      // Handle audio clip patch updates by other users
      if (data.payload && data.payload.type === 'timeline_clip_audio_patch') {
        const { clipId, patch } = data.payload;
        if (clipId && patch && typeof patch === 'object') {
          const normalizedPatch = { ...patch };
          if (Object.prototype.hasOwnProperty.call(normalizedPatch, 'reverse')) {
            normalizedPatch.isReversed = Boolean(normalizedPatch.reverse);
          }
          setTimelineClipsSynced(prev =>
            prev.map(c => (
              c.id === clipId && c.clipType === 'audio'
                ? { ...c, ...normalizedPatch }
                : c
            ))
          );
        }
      }

      if (data.payload && data.payload.type === 'mix_buses') {
        const { buses: remoteBuses } = data.payload;
        if (Array.isArray(remoteBuses)) setBuses(remoteBuses);
      }

      if (data.payload && data.payload.type === 'track_routing') {
        const { trackId, output, sends, duck } = data.payload;
        if (trackId) {
          setTracksSynced((prev) => prev.map((t) => (t.id === trackId
            ? {
              ...t,
              ...(output !== undefined ? { output } : {}),
              ...(sends !== undefined ? { sends } : {}),
              ...(duck !== undefined ? { duck: normalizeDuck(duck) } : {})
            }
            : t)));
        }
      }

      if (data.payload && data.payload.type === 'automation_lane') {
        const { trackId, paramId, points } = data.payload;
        if (trackId && AUTOMATION_PARAMS[paramId]) {
          setTrackAutomationPoints(trackId, paramId, Array.isArray(points) ? points : []);
        }
      }

      if (data.payload && data.payload.type === 'drum_lane_patch') {
        const { patternClipId, drumIndex, patch } = data.payload;
        const laneIdx = Number(drumIndex);
        if (patternClipId && Number.isInteger(laneIdx) && patch && typeof patch === 'object') {
          setTimelineClipsSynced(prev => {
            const target = prev.find((clip) => clip.id === patternClipId);
            if (!target) return prev;
            return prev.map((clip) => clip.trackId === target.trackId && clip.clipType === 'pattern'
              ? { ...clip, clipData: patchDrumLaneClipData(clip.clipData, laneIdx, patch) }
              : clip);
          });
        }
      }
    }

    // Handle track lock events
    if (lastMessage && lastMessage.type === 'track_lock_applied') {
      const { trackId, lock, objectType = 'track', objectId = trackId } = lastMessage.payload || {};
      const lockKey = `${objectType}:${objectId}`;
      setTracksSynced(prev => prev.map((t) => {
        if (t.id !== trackId) return t;
        const nextLocks = {
          ...(t.locks || {}),
          [lockKey]: lock
        };
        // Backward compatibility for existing UI paths still reading track.lock.
        const legacyLock = objectType === 'param' && objectId === `${trackId}:volume`
          ? lock
          : (t.lock || null);
        return { ...t, locks: nextLocks, lock: legacyLock };
      }));
    }

    if (lastMessage && lastMessage.type === 'track_unlock_applied') {
      const { trackId, objectType = 'track', objectId = trackId } = lastMessage.payload || {};
      const lockKey = `${objectType}:${objectId}`;
      setTracksSynced(prev => prev.map((t) => {
        if (t.id !== trackId) return t;
        const nextLocks = { ...(t.locks || {}) };
        delete nextLocks[lockKey];
        const legacyLock = objectType === 'param' && objectId === `${trackId}:volume`
          ? null
          : (t.lock || null);
        return { ...t, locks: nextLocks, lock: legacyLock };
      }));
    }

    if (lastMessage && lastMessage.type === 'track_updated') {
      const { trackId, patch } = lastMessage.payload;
      setTracksSynced(prev => prev.map(t => t.id === trackId ? { ...t, ...patch } : t));
    }

    if (lastMessage && lastMessage.type === 'track_lock_denied') {
      const { lock } = lastMessage.payload;
      console.warn(`🔒 Track locked by ${lock.lockedByName || 'another user'}`);
      // Could show a toast notification here
    }
  };
  useEffect(() => {
    if (typeof subscribeMessages !== 'function') return undefined;
    return subscribeMessages((message) => applyRemoteMessageRef.current?.(message));
  }, [subscribeMessages]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let contextRef = null;
    let disposed = false;
    const trackNodes = trackNodesRef.current;
    const trackAnalysers = trackAnalysersRef.current;
    const drumLanePools = drumLanePoolsRef.current;
    const drumLanePoolsPending = drumLanePoolsPendingRef.current;

    // Initialize Web Audio API context
    const initAudioContext = () => {
      const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
      let ctx;
      try {
        // Web Audio accepts a latency target in seconds rather than a Pro Tools
        // style frame count. Convert the user's 256/512 frame choice at the
        // production-standard 48 kHz reference rate. The browser and device
        // still own the final hardware buffer and expose it through baseLatency.
        ctx = new AudioContextConstructor({ latencyHint: contextLatencyHint(playbackBufferFrames) });
      } catch (_) {
        // Older Safari builds accepted no constructor options. Audio must stay
        // available even when the latency hint is unsupported.
        ctx = new AudioContextConstructor();
      }

      // Create master gain node for timeline
      const masterGain = ctx.createGain();
      masterGain.gain.value = masterVolume;

      // Safety limiter: inter-track summing must never hard-clip the destination.
      // DynamicsCompressor at 20:1 with zero knee behaves as a brickwall-style ceiling.
      const masterLimiter = ctx.createDynamicsCompressor();
      masterLimiter.threshold.value = -1.0;
      masterLimiter.knee.value = 0;
      masterLimiter.ratio.value = 20;
      masterLimiter.attack.value = 0.001;
      masterLimiter.release.value = 0.1;

      // Create master analyser for level metering
      const masterAnalyser = ctx.createAnalyser();
      masterAnalyser.fftSize = 2048;
      masterAnalyser.smoothingTimeConstant = 0; // No smoothing - we want raw data for meters

      // Connect: masterGain → masterLimiter → masterAnalyser → destination
      masterGain.connect(masterLimiter);
      masterLimiter.connect(masterAnalyser);
      masterAnalyser.connect(ctx.destination);

      masterGainRef.current = masterGain;
      masterLimiterRef.current = masterLimiter;
      masterAnalyserRef.current = masterAnalyser;
      drumBusInputRef.current = configureDrumBus(ctx, masterGain).input;
      if (rustSamplerEnabled()) {
        void createSamplePlaybackPool(ctx, drumBusInputRef.current).then((pool) => {
          if (!pool) return;
          if (disposed || ctx.state === 'closed') {
            pool.destroy();
            return;
          }
          samplePlaybackPoolRef.current?.destroy();
          samplePlaybackPoolRef.current = pool;
          const buffers = uniqueDecodedBuffers(drumBuffersRef.current);
          if (buffers.length > 0 && playbackStartTime.current === null) {
            void pool.preload(buffers).then((slots) => {
              if (samplePlaybackPoolRef.current === pool) {
                samplePlaybackReadyRef.current = slots.length === buffers.length
                  && slots.every(Number.isInteger);
              }
            });
          }
        });
      }

      const metronomeGain = ctx.createGain();
      metronomeGain.gain.value = 0.2;
      metronomeGain.connect(ctx.destination);
      metronomeGainRef.current = metronomeGain;

      contextRef = ctx;
      // Publish the live engine synchronously. On a fast mobile tap, playback
      // can run before React commits the state update and its follow-up ref
      // effect; leaving the ref null made that first Play press a no-op.
      audioContextRef.current = ctx;
      setAudioContext(ctx);
      // console.log('Audio context initialized');

      // Process any pending audio loads
      if (pendingLoads.current.length > 0) {
        // console.log(`Processing ${pendingLoads.current.length} pending audio loads`);
        pendingLoads.current.forEach((pending) => {
          const { trackId, fileName, fileUrl } = pending;
          loadAudioFromUrlInternal(ctx, trackId, fileName, fileUrl, {
            ...pending
          });
        });
        pendingLoads.current = [];
      }
    };

    // User gesture fallback/resume path (for browsers that suspend autoplay audio).
    const handleUserGesture = () => {
      if (!contextRef || contextRef.state === 'closed') {
        initAudioContext();
      }
      if (contextRef?.state !== 'running') activateAudioContext(contextRef).catch(() => {});
    };

    // Initialize immediately so session restore can decode/load tracks without waiting for a click.
    // Playback may still require a gesture on some browsers, but loading should not be blocked.
    initAudioContext();

    document.addEventListener('click', handleUserGesture);
    document.addEventListener('pointerdown', handleUserGesture);
    document.addEventListener('touchend', handleUserGesture);
    document.addEventListener('keydown', handleUserGesture);

    return () => {
      disposed = true;
      document.removeEventListener('click', handleUserGesture);
      document.removeEventListener('pointerdown', handleUserGesture);
      document.removeEventListener('touchend', handleUserGesture);
      document.removeEventListener('keydown', handleUserGesture);

      // Disconnect all persistent track nodes
      trackNodes.forEach((nodes) => {
        nodes.audibilityGain.disconnect();
        nodes.userGain.disconnect();
        nodes.panNode.disconnect();
        try { nodes.outputGain?.disconnect(); } catch (_) {}
        try { nodes.splitter.disconnect(); } catch (_) {}
        try { nodes.analyserL.disconnect(); } catch (_) {}
        try { nodes.analyserR.disconnect(); } catch (_) {}
        try { nodes.meterMerger?.disconnect(); } catch (_) {}
      });
      trackNodes.clear();
      trackAnalysers.clear();

      samplePlaybackPoolRef.current?.destroy();
      samplePlaybackPoolRef.current = null;
      drumLanePools.forEach((pool) => pool.destroy());
      drumLanePools.clear();
      drumLanePoolsPending.clear();
      samplePlaybackReadyRef.current = false;

      if (contextRef && contextRef.state !== 'closed') {
        contextRef.close().catch(err => {
          console.warn('Error closing audio context:', err);
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  const setPlaybackBufferFrames = useCallback((frames) => {
    const next = Number(frames);
    if (!VALID_PLAYBACK_BUFFER_FRAMES.has(next)) return false;
    setPlaybackBufferFramesState(next);
    try { localStorage.setItem(PLAYBACK_BUFFER_STORAGE_KEY, String(next)); } catch (_) {}
    return true;
  }, []);

  // Keep the actual master node derived from both fader and Song/Pattern mute
  // state. Writing only on state transitions can leave the graph stuck silent
  // when React state and the AudioParam become temporarily out of sync.
  useEffect(() => {
    if (masterGainRef.current && audioContext) {
      const now = audioContext.currentTime;
      masterGainRef.current.gain.cancelScheduledValues(now);
      masterGainRef.current.gain.setTargetAtTime(isMuted ? 0 : masterVolume, now, 0.01);
    }
  }, [masterVolume, isMuted, audioContext]);

  const loadAudioFile = async (file, sessionId = null, fileOptions = {}) => {
    if (!audioContext) {
      throw new Error('Audio context not initialized');
    }

    try {
      const trackId = Date.now().toString();

      // 1. Load locally first (instant preview)
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      void preparePeakPyramid(audioBuffer);
      audioBuffers.current.set(trackId, audioBuffer);

      const newTrack = {
        id: trackId,
        name: file.name,
        buffer: audioBuffer,
        volume: 1.0,
        muted: false,
        solo: false,
        uploading: true,
        fileUrl: null,
        storageKey: null
      };

      setTracksSynced(prev => [...prev, newTrack]);

      // Create persistent audio nodes for this track
      createTrackNodes(trackId, newTrack);

      // Automatically add to timeline at beat 0
      // Calculate duration in beats based on audio buffer duration and BPM
      const durationInSeconds = audioBuffer.duration;
      const beatsPerSecond = bpm / 60;
      const tempoAnalysis = detectLoopBpm(audioBuffer, file.name);
      const durationInBeats = tempoAnalysis?.beats || (durationInSeconds * beatsPerSecond);

      const clipStartBeat = Math.max(0, Number(fileOptions.startBeat) || 0);
      const audioClip = {
        id: `clip-${trackId}`,
        trackId: trackId,
        startBeat: clipStartBeat,
        durationBeats: durationInBeats,
        clipType: 'audio',
        clipData: null,
        ...(tempoAnalysis ? {
          tempoMatched: true,
          sourceBpm: tempoAnalysis.bpm,
          bpmConfidence: tempoAnalysis.confidence,
          stretchRatio: 1
        } : {})
      };

      setTimelineClipsSynced(prev => [...prev, audioClip]);
      // console.log(`Added audio clip to timeline: ${newTrack.name}, duration: ${durationInBeats.toFixed(2)} beats`);

      // 2. Upload to server in background
      if (sessionId) {
        const formData = new FormData();
        formData.append('audio', file);
        formData.append('sessionId', sessionId);
        formData.append('trackId', trackId);

        const token = sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          // console.log('File uploaded:', data.fileUrl);

          // Update track with server URL
          newTrack.fileUrl = data.fileUrl;
          newTrack.storageKey = data.storageKey || null;
          newTrack.uploading = false;

          setTracksSynced(prev => prev.map(t => (
            t.id === trackId
              ? { ...t, fileUrl: data.fileUrl, storageKey: data.storageKey || null, uploading: false }
              : t
          )));

          // The upload is not durable until its track metadata and exact clip
          // are committed together. Do not rely on the next autosave tick:
          // another collaborator may save or start transport before then.
          await saveTimelineToSession(sessionId, {
            forceCanonical: true,
            preventEmptyOverwrite: true
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 413) {
            // Storage quota exceeded
            alert(errorData.message || 'Storage quota exceeded! Please upgrade to premium.');
          }
          console.error('Upload failed:', errorData);
          newTrack.uploading = false;
        }
      }

      return { ...newTrack, timelineClip: audioClip };
    } catch (error) {
      console.warn('⚠️ Skipping audio file due to error:', error.message);
      // Don't throw - gracefully skip and continue
      return null;
    }
  };

  const loadAudioFromUrlInternal = async (ctx, trackId, fileName, fileUrl, options = {}) => {
    try {
      // console.log('Downloading audio from:', fileUrl);

      let finalUrl = fileUrl;
      const { storageKey, addToTimeline = true, expectedLoadGeneration, onStage,
              sourceType, assetId, assetVersion } = options;
      let signedByKey = false;

      if (isStaleSessionLoad(expectedLoadGeneration)) {
        return null;
      }

      // Prefer signing by storageKey (stable, survives URL expiration)
      if (storageKey) {
        try {
          const normalizedKey = normalizeStorageKey(storageKey);
          if (!normalizedKey) {
            throw new Error('Invalid storage key');
          }
          const token = sessionStorage.getItem('token');
          const signedByKeyResponse = await fetch(`${API_URL}/api/signed-url-by-key?key=${encodeURIComponent(normalizedKey)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (signedByKeyResponse.ok) {
            const { signedUrl } = await signedByKeyResponse.json();
            finalUrl = signedUrl;
            signedByKey = true;
            // console.log('Using signed URL by storage key');
          }
        } catch (error) {
          console.warn('Failed to get signed URL by key, falling back to URL signing');
        }
      }

      // Get signed URL for S3 files (but not if already signed)
      const isAlreadySigned = fileUrl.includes('X-Amz-Signature');
      const isS3Url = fileUrl.includes('s3.amazonaws.com') || fileUrl.includes('.s3.');

      if ((!signedByKey) && isS3Url && !isAlreadySigned) {
        try {
          const token = sessionStorage.getItem('token');
          const signedUrlResponse = await fetch(`${API_URL}/api/signed-url?url=${encodeURIComponent(fileUrl)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (signedUrlResponse.ok) {
            const { signedUrl } = await signedUrlResponse.json();
            finalUrl = signedUrl;
            // console.log('Using signed URL for S3 track');
          }
        } catch (error) {
          console.warn('Failed to get signed URL, trying direct access');
        }
      }

      // Download file from server with one fallback re-sign attempt
      let response = await fetch(finalUrl);
      if (!response.ok && fileUrl && finalUrl !== fileUrl) {
        try {
          const token = sessionStorage.getItem('token');
          const retrySignResponse = await fetch(`${API_URL}/api/signed-url?url=${encodeURIComponent(fileUrl)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          if (retrySignResponse.ok) {
            const { signedUrl } = await retrySignResponse.json();
            response = await fetch(signedUrl);
          }
        } catch (retryError) {
          console.warn('Signed URL retry failed:', retryError?.message || retryError);
        }
      }

      if (!response.ok) {
        throw new Error(`Audio fetch failed (${response.status}) for track ${trackId}`);
      }
      onStage?.(0.25); // signed + connected
      const arrayBuffer = await response.arrayBuffer();
      onStage?.(0.7); // downloaded

      // Decode audio data
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      onStage?.(1); // decoded

      if (isStaleSessionLoad(expectedLoadGeneration)) {
        return null;
      }

      void preparePeakPyramid(audioBuffer);
      audioBuffers.current.set(trackId, audioBuffer);

      const newTrack = {
        id: trackId,
        name: fileName,
        type: 'audio',
        buffer: audioBuffer,
        ...persistedLoopFields({ ...options, loopSource: options.loopSource }),
        volume: 1.0,
        muted: false,
        solo: false,
        fileUrl: fileUrl,
        uploading: false,
        storageKey: storageKey || null,
        // Preserve stock-asset identity so isTrackInSessionScope can short-circuit
        // without falling through to a CDN URL check that would flag it as stale.
        ...(sourceType ? { sourceType } : {}),
        ...(assetId ? { assetId } : {}),
        ...(assetVersion != null ? { assetVersion } : {}),
      };

      setTracksSynced(prev => {
        if (isStaleSessionLoad(expectedLoadGeneration)) {
          return prev;
        }
        // Final check before adding (prevent race conditions)
        let next;
        if (prev.find(t => t.id === trackId)) {
          // console.log('Track already in state, updating instead of adding');
          next = prev.map(t => t.id === trackId ? newTrack : t);
          return next;
        }
        next = [...prev, newTrack];
        return next;
      });

      let createdClip = null;
      if (addToTimeline) {
        // Automatically add to timeline at beat 0
        const durationInSeconds = audioBuffer.duration;
        const beatsPerSecond = bpm / 60;
        const tempoAnalysis = detectLoopBpm(audioBuffer, fileName);
        const durationInBeats = tempoAnalysis?.beats || (durationInSeconds * beatsPerSecond);

        setTimelineClipsSynced(prev => {
          if (isStaleSessionLoad(expectedLoadGeneration)) {
            return prev;
          }
          // Check if clip already exists for this track
          if (prev.find(c => c.trackId === trackId)) {
            return prev;
          }

          const audioClip = {
            id: `clip-${trackId}`,
            trackId: trackId,
            startBeat: 0,
            durationBeats: durationInBeats,
            clipType: 'audio',
            clipData: null,
            ...(tempoAnalysis ? {
              tempoMatched: true,
              sourceBpm: tempoAnalysis.bpm,
              bpmConfidence: tempoAnalysis.confidence,
              stretchRatio: 1
            } : {})
          };

          createdClip = audioClip;
          return [...prev, audioClip];
        });
      }

      // console.log('Audio loaded from URL:', fileName);

      return { track: newTrack, clip: createdClip };
    } catch (error) {
      console.warn('⚠️ Skipping audio from URL due to error:', error.message);
      // Don't throw - gracefully skip and continue
      return null;
    }
  };

  const extractStorageKeyFromUrl = (url) => storageKeyFromUrl(url);

  const loadAudioFromUrl = async (trackId, fileName, fileUrl, options = {}) => {
    const { storageKey, addToTimeline = true, expectedLoadGeneration } = options;
    if (isStaleSessionLoad(expectedLoadGeneration)) {
      return null;
    }

    const existingTrack = tracksRef.current.find(t => t.id === trackId);

    if (existingTrack && audioBuffers.current.has(trackId)) {
      return { track: existingTrack, clip: null };
    }

    if (!audioContext) {
      if (!queuedTrackWarnedRef.current.has(trackId)) {
        queuedTrackWarnedRef.current.add(trackId);
        console.warn('Audio context not ready yet, queuing track:', fileName);
      }
      // Queue this load for when audio context is ready
      pendingLoads.current.push({
        trackId,
        fileName,
        fileUrl,
        storageKey,
        addToTimeline,
        expectedLoadGeneration,
        sourceType: options.sourceType,
        assetId: options.assetId,
        assetVersion: options.assetVersion,
        loopSource: options.loopSource,
        audioWaveform: options.audioWaveform
      });

      // Add placeholder track
      setTracksSynced(prev => {
        if (isStaleSessionLoad(expectedLoadGeneration)) {
          return prev;
        }
        // Double-check before adding placeholder
        if (prev.find(t => t.id === trackId)) {
          return prev;
        }
        const next = [...prev, {
          id: trackId,
          name: fileName,
          buffer: null,
          type: 'audio',
          ...persistedLoopFields(options),
          volume: 1.0,
          muted: false,
          solo: false,
          fileUrl: fileUrl,
          uploading: true, // Show as loading
          storageKey: storageKey || null,
          ...(options.sourceType ? { sourceType: options.sourceType } : {}),
          ...(options.assetId ? { assetId: options.assetId } : {}),
          ...(options.assetVersion != null ? { assetVersion: options.assetVersion } : {})
        }];
        return next;
      });

      return;
    }

    return loadAudioFromUrlInternal(audioContext, trackId, fileName, fileUrl, {
      storageKey,
      addToTimeline,
      expectedLoadGeneration,
      onStage: options.onStage,
      sourceType: options.sourceType,
      assetId: options.assetId,
      assetVersion: options.assetVersion,
      loopSource: options.loopSource,
      audioWaveform: options.audioWaveform,
    });
  };

  const playTrack = (trackId) => {
    if (!audioContext || !masterGainRef.current) return;

    const buffer = audioBuffers.current.get(trackId);
    if (!buffer) return;

    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(masterGainRef.current); // Connect to master gain instead of destination
    source.start(0);

    return source;
  };

  // Set timeline mute state (for remote updates)
  const setTimelineMute = (muted) => {
    const nextMuted = Boolean(muted);
    setIsMuted(nextMuted);
    if (masterGainRef.current && audioContext) {
      const now = audioContext.currentTime;
      masterGainRef.current.gain.cancelScheduledValues(now);
      masterGainRef.current.gain.setValueAtTime(nextMuted ? 0 : masterVolume, now);
    }
  };

  // Toggle timeline mute (for local user)
  const toggleTimelineMute = () => {
    setTimelineMute(!isMuted);
  };

  const stopAllScheduledSources = useCallback(() => {
    const now = audioContextRef.current?.currentTime || 0;
    scheduledSources.current.forEach((source) => {
      try {
        if (source._cookupDrumVoice && fadeAndStopDrumVoice(source._cookupDrumVoice, now)) return;
        const gain = source._cookupFadeGain?.gain;
        if (gain) {
          // Declick sounding audio clips on stop/pause/seek.
          gain.cancelScheduledValues(now);
          gain.setValueAtTime(gain.value, now);
          gain.linearRampToValueAtTime(0, now + CLIP_STOP_FADE_SECONDS);
          source.stop(now + CLIP_STOP_FADE_SECONDS + 0.002);
          return;
        }
        source.stop();
      } catch (_) {
        // Already stopped.
      }
    });
    scheduledSources.current = [];
    scheduledPatternSourcesRef.current.clear();
    scheduledPatternHitsRef.current.clear();
    scheduledDrumLaneVoicesRef.current.clear();
    samplePlaybackPoolRef.current?.stop();
    drumLanePoolsRef.current.forEach((pool) => pool.stop());
    // Scheduled ducking dips belong to hits that will no longer sound.
    trackNodesRef.current.forEach((nodes) => resetDuck(nodes?.duckGain?.gain, now));

    // Release automated params back to manual (slider) control. During playback
    // the scheduler re-anchors them on its next slice, so this is also the
    // clean reset point for seeks and live reschedules.
    const ctx = audioContextRef.current;
    if (ctx) {
      Object.entries(automationRef.current || {}).forEach(([trackId, lanes]) => {
        const nodes = trackNodesRef.current.get(trackId);
        if (!nodes) return;
        const track = tracksRef.current.find((t) => t.id === trackId);
        Object.keys(lanes || {}).forEach((paramId) => {
          const param = paramId === 'volume'
            ? nodes.userGain?.gain
            : (paramId === 'pan' ? nodes.panNode?.pan : null);
          if (!param) return;
          try {
            param.cancelScheduledValues(ctx.currentTime);
            const manual = paramId === 'volume' ? (track?.volume ?? 1) : (track?.pan ?? 0);
            param.setTargetAtTime(manual, ctx.currentTime, 0.01);
          } catch (_) {
            // Param may belong to a torn-down node — best effort.
          }
        });
      });
    }
  }, []);

  // Arm/disarm the microphone. Arming requests permission and builds the
  // capture graph so recording starts instantly on Play.
  const toggleMicArm = useCallback(async () => {
    if (micRecorderRef.current) {
      micRecorderRef.current.dispose();
      micRecorderRef.current = null;
      setMicArmed(false);
      setIsMicRecording(false);
      setMicInputError('');
      return false;
    }
    const ctx = audioContextRef.current;
    if (!ctx) return false;
    try {
      if (ctx.state === 'suspended') await ctx.resume();
      const recorder = new MicRecorder(ctx, { deviceId: selectedAudioInputIdRef.current });
      await recorder.init();
      micRecorderRef.current = recorder;
      setMicArmed(true);
      setMicInputError('');
      void refreshAudioInputs();
      return true;
    } catch (err) {
      console.error('🎙️ Mic arm failed (permission denied or no device):', err);
      setMicArmed(false);
      setMicInputError(err?.name === 'NotAllowedError'
        ? 'Microphone permission was denied in the browser.'
        : (err?.message || 'The selected audio input could not be opened.'));
      return false;
    }
  }, [refreshAudioInputs]);

  const setAudioInputDevice = useCallback(async (deviceId = 'default') => {
    const next = String(deviceId || 'default');
    selectedAudioInputIdRef.current = next;
    setSelectedAudioInputIdState(next);
    try { localStorage.setItem('cookup_audio_input', next); } catch (_) {}
    setMicInputError('');

    // If input was already armed, hot-swap it now so the selector is truthful.
    if (!micRecorderRef.current) return true;
    micRecorderRef.current.dispose();
    micRecorderRef.current = null;
    setMicArmed(false);
    setIsMicRecording(false);
    const ctx = audioContextRef.current;
    if (!ctx) return false;
    try {
      const recorder = new MicRecorder(ctx, { deviceId: next });
      await recorder.init();
      micRecorderRef.current = recorder;
      setMicArmed(true);
      void refreshAudioInputs();
      return true;
    } catch (error) {
      setMicInputError(error?.message || 'The selected audio input could not be opened.');
      return false;
    }
  }, [refreshAudioInputs]);

  // Stop capture and drop the take onto a new track at the beat where
  // recording started. Upload runs in the background via loadAudioFile.
  // The take is placed with round-trip latency removed: a sample captured at
  // context time T was performed against audio the engine rendered at
  // T - (output + input latency). The anchor is the one pinned when the
  // scheduler started the transport for this take.
  const finalizeMicRecording = () => {
    const recorder = micRecorderRef.current;
    if (!recorder || !recorder.active) return;
    setIsMicRecording(false);
    const requestedStartBeat = micRecordStartBeatRef.current;
    const anchor = micRecordAnchorRef.current;
    micRecordAnchorRef.current = null;
    const anchorTime = anchor ? anchor.time : playbackStartTime.current;
    const anchorBeat = anchor ? anchor.beat : playbackStartBeat.current;
    const bps = anchor ? anchor.bps : Math.max(0.001, bpmRef.current / 60);
    let placedBeat = requestedStartBeat;
    const leadingTrimSeconds = (takeStartTime, roundTripSeconds) => {
      if (!Number.isFinite(takeStartTime) || !Number.isFinite(anchorTime) || !Number.isFinite(anchorBeat)) return 0;
      const firstSampleBeat = anchorBeat + (takeStartTime - roundTripSeconds - anchorTime) * bps;
      if (firstSampleBeat >= requestedStartBeat) {
        placedBeat = firstSampleBeat;
        return 0;
      }
      return (requestedStartBeat - firstSampleBeat) / bps;
    };

    void recorder.stop({ leadingTrimSeconds }).then((result) => {
      if (!result || result.durationSeconds < 0.05) return;
      const stamp = new Date();
      const name = `Recording ${stamp.getHours()}${String(stamp.getMinutes()).padStart(2, '0')}${String(stamp.getSeconds()).padStart(2, '0')}.wav`;
      const file = new File([result.blob], name, { type: 'audio/wav' });
      return loadAudioFile(file, wsSessionId || null, { startBeat: placedBeat });
    }).catch((err) => console.error('🎙️ Failed to import recording:', err));
  };

  const play = async () => {
    // Already rolling: re-entering would rewind to the stale paused position and
    // double-schedule audio on top of the playing sources.
    if (isPlaying) return true;
    if (playStartPendingRef.current) return false;
    playStartPendingRef.current = true;
    const context = audioContextRef.current || audioContext;

    try {
      // iOS may move the live context to `interrupted` while CookUp privately
      // renders an AI draft. Resume and unlock it within this Play gesture;
      // never claim playback began while the hardware clock is still frozen.
      if (!await activateAudioContext(context)) {
        console.warn(`⚠️ Audio context did not enter running state (state: ${context?.state || 'missing'})`);
        return false;
      }

      const requestedStartBeat = returnToStartEnabledRef.current
        ? transportStartBeatRef.current
        : Math.max(0, Number(transportClock.getBeat()) || Number(playheadPosition) || 0);
      if (returnToStartEnabledRef.current) {
        setPlayheadPosition(requestedStartBeat);
        transportClock.setBeat(requestedStartBeat);
      }

      scheduledPatternHitsRef.current.clear();
      playbackStartTime.current = context.currentTime;
      playbackStartBeat.current = requestedStartBeat;
      transportClock.setBeat(requestedStartBeat);
      // The lookahead scheduler effect primes itself from the transport refs.
      schedulerStateRef.current = null;
      transportDiagRef.current.armed = true; // probe the first scheduled audio

      // Armed mic starts capturing with the transport.
      if (micRecorderRef.current && !micRecorderRef.current.active) {
        // Use the resolved transport start (including Return-to-start and seeks),
        // so the single continuous WAV take lands at the same boundary as MIDI.
        micRecordStartBeatRef.current = requestedStartBeat;
        try {
          micRecorderRef.current.start();
          setIsMicRecording(true);
        } catch (err) {
          console.error('🎙️ Failed to start mic capture:', err);
        }
      }

      setIsPlaying(true);
      return true;
    } catch (error) {
      console.error('❌ Failed to start playback:', error);
      return false;
    } finally {
      // A thrown node/ref error must never make every later Play press return
      // early. The next explicit gesture always gets a clean recovery attempt.
      playStartPendingRef.current = false;
    }
  };

  const pause = () => {
    playStartPendingRef.current = false;
    finalizeMicRecording();
    stopAllScheduledSources();
    schedulerStateRef.current = null;
    playbackStartTime.current = null;
    const pausedPool = samplePlaybackPoolRef.current;
    const pausedBuffers = uniqueDecodedBuffers(drumBuffersRef.current);
    if (pausedPool) void pausedPool.preload(pausedBuffers).then((slots) => {
      if (samplePlaybackPoolRef.current === pausedPool) {
        samplePlaybackReadyRef.current = slots.length === pausedBuffers.length
          && slots.every(Number.isInteger);
      }
    });
    const pausedAt = returnToStartEnabledRef.current
      ? transportStartBeatRef.current
      : transportClock.getBeat();
    setPlayheadPosition(pausedAt);
    transportClock.setBeat(pausedAt);
    playbackStartBeat.current = pausedAt;
    setIsPlaying(false);
  };

  const stop = () => {
    playStartPendingRef.current = false;
    finalizeMicRecording();
    stopAllScheduledSources();
    schedulerStateRef.current = null;
    playbackStartTime.current = null;
    const stoppedPool = samplePlaybackPoolRef.current;
    const stoppedBuffers = uniqueDecodedBuffers(drumBuffersRef.current);
    if (stoppedPool) void stoppedPool.preload(stoppedBuffers).then((slots) => {
      if (samplePlaybackPoolRef.current === stoppedPool) {
        samplePlaybackReadyRef.current = slots.length === stoppedBuffers.length
          && slots.every(Number.isInteger);
      }
    });
    const stoppedAt = returnToStartEnabledRef.current
      ? transportStartBeatRef.current
      : transportClock.getBeat();
    setPlayheadPosition(stoppedAt);
    transportClock.setBeat(stoppedAt);
    playbackStartBeat.current = stoppedAt;
    setIsPlaying(false);
    setIsRecording(false);
    if (countInTimeoutRef.current) {
      clearTimeout(countInTimeoutRef.current);
      countInTimeoutRef.current = null;
    }
    setIsCountInActive(false);
  };

  // Seek that stays correct during playback: rebase the transport refs and let the
  // scheduler re-prime from the new position. Exposed to UI as setPlayheadPosition.
  const seekTo = useCallback((beat, options = {}) => {
    const safeBeat = Math.max(0, Number.isFinite(Number(beat)) ? Number(beat) : 0);
    if (options.setStart !== false) {
      transportStartBeatRef.current = safeBeat;
      setTransportStartBeat(safeBeat);
    }
    setPlayheadPosition(safeBeat);
    transportClock.setBeat(safeBeat);
    const ctx = audioContextRef.current;
    if (ctx && playbackStartTime.current !== null) {
      playbackStartTime.current = ctx.currentTime;
      playbackStartBeat.current = safeBeat;
      stopAllScheduledSources();
      schedulerStateRef.current = null;
      // Re-prime synchronously so seeking during playback has no audible gap.
      schedulerTickRef.current?.();
    }
  }, [stopAllScheduledSources]);

  const setReturnToStartEnabled = useCallback((enabled) => {
    const next = Boolean(enabled);
    returnToStartEnabledRef.current = next;
    setReturnToStartEnabledState(next);
    try { localStorage.setItem('cookup_return_to_start', String(next)); } catch (_) {}
  }, []);

  const scheduleMetronomeClick = useCallback((time, beatNumber) => {
    if (!audioContext || !metronomeGainRef.current) return;
    const downbeat = beatNumber % 4 === 0;
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();

    osc.type = 'triangle';
    osc.frequency.value = downbeat ? 1700 : 1100;

    gain.gain.setValueAtTime(0.0001, time);
    gain.gain.linearRampToValueAtTime(downbeat ? 0.32 : 0.22, time + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.0001, time + 0.03);

    osc.connect(gain);
    gain.connect(metronomeGainRef.current);
    osc.start(time);
    osc.stop(time + 0.035);
    // Tracked so a live tempo change can withdraw clicks queued at the old tempo.
    const pending = metronomeClicksRef.current;
    pending.push({ osc, time });
    osc.onended = () => {
      const index = pending.findIndex((entry) => entry.osc === osc);
      if (index !== -1) pending.splice(index, 1);
      try { gain.disconnect(); } catch (_) { /* already disconnected */ }
    };
  }, [audioContext]);

  // Withdraw clicks queued after `cutoff` and restart the beat walk from the
  // rebased transport anchor.
  const rebaseMetronome = (cutoff) => {
    metronomeClicksRef.current = metronomeClicksRef.current.filter(({ osc, time }) => {
      if (time <= cutoff) return true;
      try { osc.stop(cutoff); } catch (_) { /* already stopped */ }
      return false;
    });
    metronomeNextBeatRef.current = Math.ceil(playbackStartBeat.current - 1e-6);
  };

  useEffect(() => {
    if (!audioContext || !isPlaying || !metronomeEnabled) {
      if (metronomeTimerRef.current) {
        clearSchedulerInterval(metronomeTimerRef.current);
        metronomeTimerRef.current = null;
      }
      return;
    }

    const lookAhead = 0.12;
    const startBeat = playbackStartBeat.current ?? playheadPosition;
    metronomeNextBeatRef.current = Math.ceil(startBeat - 1e-6);

    const scheduler = () => {
      if (!playbackStartTime.current) return;
      // Tempo is read live: a tempo change rebases the anchor instead of
      // restarting this walk (see rebaseMetronome).
      const beatsPerSecond = transportShapeRef.current.bps;
      const now = audioContext.currentTime;
      while (true) {
        const beat = metronomeNextBeatRef.current;
        const beatTime = playbackStartTime.current + ((beat - playbackStartBeat.current) / beatsPerSecond);
        if (beatTime > now + lookAhead) break;
        if (beatTime >= now - 0.02) {
          scheduleMetronomeClick(beatTime, beat);
        }
        metronomeNextBeatRef.current += 1;
      }
    };

    scheduler();
    metronomeTimerRef.current = setSchedulerInterval(scheduler, 25);

    return () => {
      if (metronomeTimerRef.current) {
        clearSchedulerInterval(metronomeTimerRef.current);
        metronomeTimerRef.current = null;
      }
    };
  // Tempo is read live from transportShapeRef, so it is not a dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext, isPlaying, metronomeEnabled, playheadPosition, scheduleMetronomeClick]);

  const toggleMetronome = () => {
    setMetronomeEnabled(prev => !prev);
  };

  const toggleCountIn = () => {
    setCountInEnabled(prev => !prev);
  };

  const cancelCountIn = () => {
    if (countInTimeoutRef.current) {
      clearTimeout(countInTimeoutRef.current);
      countInTimeoutRef.current = null;
    }
    setIsCountInActive(false);
  };

  const startCountIn = (onDone, beats = 3) => {
    if (!audioContext || typeof onDone !== 'function') {
      onDone?.();
      return;
    }

    cancelCountIn();
    setIsCountInActive(true);

    const secondsPerBeat = 60 / Math.max(1, bpmRef.current || 120);
    const startAt = audioContext.currentTime + 0.05;

    for (let i = 0; i < beats; i += 1) {
      const beatTime = startAt + i * secondsPerBeat;
      scheduleMetronomeClick(beatTime, i);
    }

    const waitMs = Math.max(0, beats * secondsPerBeat * 1000);
    countInTimeoutRef.current = setTimeout(() => {
      countInTimeoutRef.current = null;
      setIsCountInActive(false);
      onDone();
    }, waitMs + 10);
  };

  const toggleRecording = () => {
    setIsRecording(prev => !prev);
  };

  const getPlayableClipBuffer = (trackId, sourceBuffer, reverse = false) => {
    if (!sourceBuffer) return null;
    if (!reverse) return sourceBuffer;
    const cacheKey = String(trackId || 'unknown');
    const cached = reversedAudioBuffersRef.current.get(cacheKey);
    if (cached && cached.source === sourceBuffer && cached.reversed) {
      return cached.reversed;
    }

    const reversed = audioContext.createBuffer(
      sourceBuffer.numberOfChannels,
      sourceBuffer.length,
      sourceBuffer.sampleRate
    );

    for (let channel = 0; channel < sourceBuffer.numberOfChannels; channel += 1) {
      const src = sourceBuffer.getChannelData(channel);
      const dst = reversed.getChannelData(channel);
      for (let i = 0, j = src.length - 1; i < src.length; i += 1, j -= 1) {
        dst[i] = src[j];
      }
    }

    reversedAudioBuffersRef.current.set(cacheKey, { source: sourceBuffer, reversed });
    return reversed;
  };

  const getPitchedClipBuffer = (trackId, sourceBuffer, pitchRatio = 1) => {
    if (!sourceBuffer) return null;
    const safePitchRatio = Math.max(0.125, Math.min(8, pitchRatio || 1));
    if (Math.abs(safePitchRatio - 1) < 0.0001) return sourceBuffer;

    const trackKey = String(trackId || 'unknown');
    const ratioKey = safePitchRatio.toFixed(5);
    const cacheKey = `${trackKey}:${ratioKey}:${sourceBuffer.numberOfChannels}:${sourceBuffer.length}:${sourceBuffer.sampleRate}`;
    const cached = pitchedAudioBuffersRef.current.get(cacheKey);
    if (cached && cached.source === sourceBuffer && cached.buffer) {
      return cached.buffer;
    }

    const shifted = createPitchShiftedBufferPreserveDuration(audioContext, sourceBuffer, safePitchRatio);
    pitchedAudioBuffersRef.current.set(cacheKey, {
      source: sourceBuffer,
      buffer: shifted,
      quality: 'realtime-fallback'
    });
    return shifted;
  };

  const getTempoStretchedClipBuffer = (trackId, sourceBuffer, stretchRatio = 1) => {
    if (!sourceBuffer) return null;
    const safeStretch = Math.max(0.25, Math.min(4, Number(stretchRatio) || 1));
    const targetLength = Math.max(1, Math.round(sourceBuffer.length * safeStretch));
    if (targetLength === sourceBuffer.length) return sourceBuffer;
    const cacheKey = `${String(trackId || 'unknown')}:${targetLength}:${sourceBuffer.length}:${sourceBuffer.sampleRate}`;
    const cached = stretchedAudioBuffersRef.current.get(cacheKey);
    if (cached?.source === sourceBuffer && cached.buffer) return cached.buffer;
    const buffer = createTimeStretchedBufferPreservePitch(audioContext, sourceBuffer, safeStretch);
    stretchedAudioBuffersRef.current.set(cacheKey, {
      source: sourceBuffer,
      buffer,
      quality: 'realtime-fallback'
    });
    return buffer;
  };

  const preparedAudioBuffer = (channels, sampleRate) => {
    if (!audioContext || !channels?.length || !channels[0]?.length) return null;
    const prepared = audioContext.createBuffer(channels.length, channels[0].length, sampleRate);
    channels.forEach((channel, index) => prepared.copyToChannel(channel, index));
    return prepared;
  };

  const queueSamplePreparation = (type, cache, cacheKey, sourceBuffer, ratio) => {
    if (!audioContext || !sourceBuffer || !cacheKey) return Promise.resolve(sourceBuffer);
    const cached = cache.current.get(cacheKey);
    if (cached?.source === sourceBuffer && cached?.quality === 'high') {
      return Promise.resolve(cached.buffer);
    }
    const jobKey = `${type}:${cacheKey}`;
    const existing = samplePreparationJobsRef.current.get(jobKey);
    if (existing) return existing;
    const channels = Array.from(
      { length: sourceBuffer.numberOfChannels },
      (_, channel) => sourceBuffer.getChannelData(channel)
    );
    let operation;
    try {
      operation = type === 'tempo'
        ? stretchChannelsPreservePitch(channels, ratio, { taps: 32 })
        : pitchShiftChannelsPreserveDuration(channels, ratio, { taps: 32 });
    } catch (_) {
      return Promise.resolve(sourceBuffer);
    }
    const job = operation.then((result) => {
      const buffer = preparedAudioBuffer(result.channels, sourceBuffer.sampleRate);
      if (buffer) cache.current.set(cacheKey, { source: sourceBuffer, buffer, quality: 'high' });
      return buffer || sourceBuffer;
    }).catch(() => sourceBuffer).finally(() => {
      samplePreparationJobsRef.current.delete(jobKey);
    });
    samplePreparationJobsRef.current.set(jobKey, job);
    return job;
  };

  // Prepare every edited/imported audio clip before the scheduler asks for it.
  // The existing synchronous path remains an audible compatibility fallback,
  // but normal playback consumes the worker-prepared high-quality cache and
  // performs no resampling or WSOLA work near the play command.
  useEffect(() => {
    if (!audioContext) return;
    const beatsPerSecond = bpm / 60;
    timelineClips.forEach((clip) => {
      if (clip?.clipType !== 'audio') return;
      const source = audioBuffers.current.get(clip.trackId);
      if (!source) return;
      const settings = getAudioClipPlaybackSettings(clip, beatsPerSecond);
      const trackKey = String(clip.trackId || 'unknown');
      const stretchKey = `${trackKey}:${Math.max(1, Math.round(source.length * settings.stretchRatio))}:${source.length}:${source.sampleRate}`;
      const tempoJob = settings.preserveTempoPitch && Math.abs(settings.stretchRatio - 1) >= 1 / source.length
        ? queueSamplePreparation('tempo', stretchedAudioBuffersRef, stretchKey, source, settings.stretchRatio)
        : Promise.resolve(source);
      if (Math.abs(settings.pitchRatio - 1) < 0.0001) return;
      tempoJob.then((tempoBuffer) => {
        const pitchKey = `${trackKey}:${settings.pitchRatio.toFixed(5)}:${tempoBuffer.numberOfChannels}:${tempoBuffer.length}:${tempoBuffer.sampleRate}`;
        return queueSamplePreparation(
          'pitch', pitchedAudioBuffersRef, pitchKey, tempoBuffer, settings.pitchRatio
        );
      });
    });
  // Cache refs and preparation helpers are stable for the provider lifetime.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext, bpm, timelineClips, tracks]);

  // Track scheduled sources with automatic cleanup so long sessions don't
  // accumulate ended nodes in the array.
  const pushScheduledSource = (source, extraNodes = [], patternClipId = null, schedule = {}) => {
    source._cookupSchedule = {
      ...schedule,
      patternClipId: patternClipId || schedule.patternClipId || null
    };
    scheduledSources.current.push(source);
    if (patternClipId) {
      const clipSources = scheduledPatternSourcesRef.current.get(patternClipId) || new Set();
      clipSources.add(source);
      scheduledPatternSourcesRef.current.set(patternClipId, clipSources);
    }
    source.onended = () => {
      const idx = scheduledSources.current.indexOf(source);
      if (idx !== -1) scheduledSources.current.splice(idx, 1);
      if (patternClipId) {
        const clipSources = scheduledPatternSourcesRef.current.get(patternClipId);
        clipSources?.delete(source);
        if (clipSources?.size === 0) scheduledPatternSourcesRef.current.delete(patternClipId);
      }
      try { source.disconnect(); } catch (_) { /* already disconnected */ }
      extraNodes.forEach((node) => {
        try { node.disconnect(); } catch (_) { /* already disconnected */ }
      });
    };
  };

  // Rebuild only the not-yet-audible lookahead after a live edit. Sources that
  // are already playing stay attached to the graph, eliminating the hard
  // stop/restart discontinuity that caused DAW-wide clicks and brief blanks.
  const cancelFutureScheduledSources = useCallback((now) => {
    const entries = scheduledSources.current.map((source) => ({
      source,
      startTime: source?._cookupSchedule?.startTime
    }));
    const partition = partitionLiveEditSchedule(entries, now);
    const cancelled = new Set(partition.cancel.map((entry) => entry.source));

    partition.cancel.forEach(({ source }) => {
      try { source.stop(now); } catch (_) { /* already stopped */ }
    });
    scheduledSources.current = partition.keep.map((entry) => entry.source);

    // Rebuild clip/source and event-key indexes from the preserved voices. This
    // lets the refill scheduler replace cancelled events without duplicating
    // an imminent hit that was inside the protected render window.
    scheduledPatternSourcesRef.current.clear();
    scheduledPatternHitsRef.current.clear();
    scheduledSources.current.forEach((source) => {
      const meta = source?._cookupSchedule || {};
      if (meta.patternClipId) {
        const sources = scheduledPatternSourcesRef.current.get(meta.patternClipId) || new Set();
        sources.add(source);
        scheduledPatternSourcesRef.current.set(meta.patternClipId, sources);
      }
      if (meta.eventKey) scheduledPatternHitsRef.current.add(meta.eventKey);
    });
    [...scheduledDrumLaneVoicesRef.current.entries()].forEach(([key, voices]) => {
      const remaining = (Array.isArray(voices) ? voices : [voices])
        .filter((voice) => voice && !cancelled.has(voice.source));
      if (remaining.length) scheduledDrumLaneVoicesRef.current.set(key, remaining);
      else scheduledDrumLaneVoicesRef.current.delete(key);
    });
    return partition.cutoff;
  }, []);

  // A hard source.stop() on a sounding clip is a click. Audio clips ramp their
  // own fade gain to zero over a few ms instead; drum voices use their choke fade.
  const fadeStopSource = (source, when) => {
    try {
      if (source._cookupDrumVoice && fadeAndStopDrumVoice(source._cookupDrumVoice, when)) return;
      const gain = source._cookupFadeGain?.gain;
      if (gain) {
        if (typeof gain.cancelAndHoldAtTime === 'function') gain.cancelAndHoldAtTime(when);
        else {
          gain.cancelScheduledValues(when);
          gain.setValueAtTime(gain.value, when);
        }
        gain.linearRampToValueAtTime(0, when + CLIP_STOP_FADE_SECONDS);
        source.stop(when + CLIP_STOP_FADE_SECONDS + 0.002);
        return;
      }
      source.stop(when);
    } catch (_) {
      // Already stopped.
    }
  };

  // Mute: everything goes quiet (with the fade), and the scheduler keeps
  // walking the transport without queuing anything until unmuted.
  const fadeStopAllScheduledSources = (now) => {
    scheduledSources.current.forEach((source) => fadeStopSource(source, now));
    scheduledSources.current = [];
    scheduledPatternSourcesRef.current.clear();
    scheduledPatternHitsRef.current.clear();
    scheduledDrumLaneVoicesRef.current.clear();
    samplePlaybackPoolRef.current?.stop();
    drumLanePoolsRef.current.forEach((pool) => pool.stop());
  };

  // After a tempo or loop change a sounding audio clip is playing at the old
  // rate towards the old end. Fade it out at the cutoff; the rebased scheduler
  // resumes it mid-clip from the same point with a matching fade-in.
  const fadeStopSoundingAudioClips = (cutoff) => {
    scheduledSources.current = scheduledSources.current.filter((source) => {
      if (source?._cookupSchedule?.kind !== 'audio') return true;
      fadeStopSource(source, cutoff);
      return false;
    });
  };

  // Automation ramps queued past the cutoff were laid out for the old tempo.
  // The rebased scheduler re-anchors each lane at the cutoff on its next slice.
  const cancelAutomationFrom = (cutoff) => {
    Object.entries(automationRef.current || {}).forEach(([trackId, lanes]) => {
      const nodes = trackNodesRef.current.get(trackId);
      if (!nodes) return;
      Object.keys(lanes || {}).forEach((paramId) => {
        const param = paramId === 'volume'
          ? nodes.userGain?.gain
          : (paramId === 'pan' ? nodes.panNode?.pan : null);
        try { param?.cancelScheduledValues(cutoff); } catch (_) { /* torn down */ }
      });
    });
  };

  const cancelScheduledPatternClip = (clipId) => {
    if (!clipId) return;
    const clipSources = scheduledPatternSourcesRef.current.get(clipId);
    const now = audioContextRef.current?.currentTime || 0;
    clipSources?.forEach((source) => {
      try {
        if (!source._cookupDrumVoice || !fadeAndStopDrumVoice(source._cookupDrumVoice, now)) {
          source.stop();
        }
      } catch (_) { /* already stopped */ }
    });
    scheduledPatternSourcesRef.current.delete(clipId);
    scheduledPatternHitsRef.current = new Set(
      [...scheduledPatternHitsRef.current].filter((key) => !String(key).startsWith(`${clipId}:`))
    );
  };

  // Drum buffers can be replaced while the transport is rolling. Web Audio
  // sources capture their buffer when they are created, so hits already queued
  // by the look-ahead scheduler would otherwise keep playing the old sample.
  // Cancel pattern sources only (leave audio clips and the transport running),
  // clear their dedupe keys, then synchronously rebuild the look-ahead window.
  const reschedulePatternsForDrumSampleChange = useCallback(() => {
    [...scheduledPatternSourcesRef.current.keys()].forEach(cancelScheduledPatternClip);
    schedulerStateRef.current = null;
    schedulerTickRef.current?.();
  }, []);

  const getTrackFxLatencySeconds = (trackId) => (
    trackFxLatencyRef.current.get(String(trackId)) || 0
  );

  // FxContext reports per-track FX chain latency (e.g. the VST bridge prebuffer)
  // so the scheduler can start latent tracks early — plugin delay compensation.
  const registerTrackFxLatency = useCallback((trackId, seconds) => {
    const key = String(trackId);
    const safeSeconds = Math.max(0, Number(seconds) || 0);
    if (safeSeconds > 0) {
      trackFxLatencyRef.current.set(key, safeSeconds);
    } else {
      trackFxLatencyRef.current.delete(key);
    }
  }, []);

  // FxContext owns the effect graphs; export borrows its builder so a bounce
  // carries the same insert chains as the mixer.
  const offlineFxBuilderRef = useRef(null);
  const registerOfflineFxBuilder = useCallback((builder) => {
    offlineFxBuilderRef.current = typeof builder === 'function' ? builder : null;
  }, []);

  // The active loop region: an explicit loop when enabled, otherwise the implicit
  // whole-song loop (restart at the end of the last clip). Null = free run.
  // Pure math lives in audio/transportMath.js (unit-tested).
  const getActiveLoopRegion = () => resolveLoopRegion({
    loopEnabled,
    loopStartBeat,
    loopEndBeat,
    clips: timelineClipsRef.current
  });
  // Long-lived callbacks (the scheduler timer, memoised transport readers)
  // must see the current loop, not the loop of the render that created them.
  const getActiveLoopRegionRef = useRef(getActiveLoopRegion);
  getActiveLoopRegionRef.current = getActiveLoopRegion;
  const currentLoopRegion = () => getActiveLoopRegionRef.current();

  // Authoritative clip transport position in BEATS, derived from the SAME sample
  // fields the timeline DISPLAY uses (getTimelineStartSample/getLengthSamples).
  // The scheduler must agree with what's drawn: if it read clip.startBeat while the
  // display read clip.timelineStartSample, a clip whose two fields disagree would be
  // heard offset from where the playhead crosses it (e.g. a fixed 1/4-bar lag).
  // Clips are immutable state objects, so the result is cached per clip and
  // tempo map: the scheduler asks for every clip on every 25 ms tick.
  const clipBeatsCacheRef = useRef(new WeakMap());
  const authoritativeClipBeats = (clip, map = sessionTempoMap()) => {
    const cached = clipBeatsCacheRef.current.get(clip);
    if (cached && cached.map === map) return cached.beats;
    const startSample = getTimelineStartSample(clip, map);
    const lengthSamples = getLengthSamples(clip, map);
    const startBeat = Math.max(0, sampleToBeatExact(startSample, map));
    const endBeat = Math.max(startBeat, sampleToBeatExact(startSample + lengthSamples, map));
    const beats = Object.freeze({ startBeat, durationBeats: endBeat - startBeat });
    if (clip && typeof clip === 'object') clipBeatsCacheRef.current.set(clip, { map, beats });
    return beats;
  };

  // Schedule one playback instance of an audio clip as if the transport is at
  // `atBeat` when the audio clock reads `atTime`. `midClip` resumes partway in.
  const scheduleAudioClipInstance = (clip, {
    atBeat, atTime, bps, hardStopBeat = Infinity, midClip = false, crossfadeIn = false
  }) => {
    if (!audioContext) return;
    const { startBeat: clipStartBeat, durationBeats: clipDurationBeats } = authoritativeClipBeats(clip);

    const sourceBuffer = audioBuffers.current.get(clip.trackId);
    if (!sourceBuffer) {
      if (!missingBufferWarnedRef.current.has(clip.trackId)) {
        missingBufferWarnedRef.current.add(clip.trackId);
        console.warn(`  ⚠️ No buffer found for track ${clip.trackId}`);
      }
      return;
    }

    const track = tracksRef.current.find(t => t.id === clip.trackId);
    if (!track) return;
    // Mute/solo are intentionally NOT checked at schedule time: the persistent
    // audibilityGain silences muted tracks, so unmuting mid-playback is instant.

    const clipSettings = getAudioClipPlaybackSettings(clip, bps);
    const tempoBuffer = clipSettings.preserveTempoPitch
      ? getTempoStretchedClipBuffer(clip.trackId, sourceBuffer, clipSettings.stretchRatio)
      : sourceBuffer;
    const reversedBuffer = getPlayableClipBuffer(clip.trackId, tempoBuffer, clipSettings.reverse);
    const buffer = getPitchedClipBuffer(clip.trackId, reversedBuffer, clipSettings.pitchRatio);
    if (!buffer) return;

    const clipEndBeat = clipStartBeat + clipDurationBeats;
    // Clips are cut at the loop boundary so loop repeats stay sample-tight.
    const effectiveEndBeat = Math.min(clipEndBeat, hardStopBeat);
    if (effectiveEndBeat <= atBeat + 1e-9) return;

    const clipAudioOffset = (clip.audioOffset || 0) / bps;
    const playbackRate = clipSettings.playbackRate;
    // Seconds already consumed from the clip's playback stream at atBeat
    // (buffer domain — the stream direction is handled below for reversed clips).
    const elapsedInStream = midClip
      ? ((atBeat - clipStartBeat) / bps) * playbackRate
      : 0;
    const timelineDuration = (effectiveEndBeat - (midClip ? atBeat : clipStartBeat)) / bps;
    let duration = Math.max(0.001, timelineDuration) * playbackRate;
    let startTime = atTime;

    // Plugin delay compensation: start FX-latent tracks early (or skip ahead in
    // the playback stream when resuming mid-clip) so they align with dry tracks.
    // streamAdvance is applied in stream direction, so it is correct for both
    // forward and reversed clips.
    let streamAdvance = 0;
    const fxLatency = getTrackFxLatencySeconds(clip.trackId);
    if (fxLatency > 0) {
      if (midClip) {
        streamAdvance += fxLatency * playbackRate;
      } else {
        startTime -= fxLatency;
        const minStart = audioContext.currentTime + 0.002;
        if (startTime < minStart) {
          streamAdvance += (minStart - startTime) * playbackRate;
          startTime = minStart;
        }
      }
    }

    // Validate that we're not trying to read beyond buffer bounds
    const bufferDuration = Math.max(0.001, buffer.duration);
    let boundedOffset;
    if (clipSettings.reverse) {
      // The buffer is pre-reversed. The clip's forward region
      // [audioOffset, audioOffset + fullDuration] maps to the reversed region
      // starting at bufferDuration - (audioOffset + fullDuration); elapsed stream
      // time then advances forward WITHIN the reversed buffer.
      const fullStreamDuration = Math.max(0.001, clipDurationBeats / bps) * playbackRate;
      const reversedRegionStart = Math.max(0, bufferDuration - (clipAudioOffset + fullStreamDuration));
      boundedOffset = reversedRegionStart + elapsedInStream + streamAdvance;
    } else {
      boundedOffset = clipAudioOffset + elapsedInStream + streamAdvance;
    }

    if (boundedOffset >= bufferDuration) {
      console.warn(`  ⚠️ Offset ${boundedOffset.toFixed(2)}s exceeds buffer duration ${bufferDuration.toFixed(2)}s for clip ${clip.id}`);
      return;
    }
    if (duration <= 0) return;

    const maxDuration = bufferDuration - boundedOffset;
    if (duration > maxDuration) {
      duration = Math.max(0.001, maxDuration);
    }
    if (boundedOffset + duration > bufferDuration) return;

    const trackNodes = createTrackNodes(clip.trackId, track);
    if (!trackNodes) {
      console.warn(`  ⚠️ Could not create track nodes for ${clip.trackId}`);
      return;
    }

    // Create source (one-shot, disposable)
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = clipSettings.playbackRate;

    const fadeGain = audioContext.createGain();
    const clipGain = clipSettings.clipGain;
    const actualTimelineDuration = Math.max(0.001, duration / Math.max(MIN_PLAYBACK_RATE, source.playbackRate.value || 1));
    const fadeInSeconds = midClip ? 0 : Math.min(clipSettings.fadeInSeconds, actualTimelineDuration * 0.95);
    const fadeOutSeconds = Math.min(clipSettings.fadeOutSeconds, actualTimelineDuration * 0.95);

    if (midClip && crossfadeIn) {
      // Resuming after a live rebase: the previous source fades out over the
      // same span, so the pair crossfades instead of doubling or clicking.
      fadeGain.gain.setValueAtTime(0, startTime);
      fadeGain.gain.linearRampToValueAtTime(clipGain, startTime + CLIP_STOP_FADE_SECONDS);
    } else if (midClip) {
      fadeGain.gain.setValueAtTime(clipGain, startTime);
    } else {
      fadeGain.gain.setValueAtTime(0.001, startTime);
      fadeGain.gain.exponentialRampToValueAtTime(
        clipGain,
        startTime + Math.max(0.01, fadeInSeconds)
      );
    }

    if (fadeOutSeconds > 0) {
      const fadeOutStart = startTime + Math.max(0.01, actualTimelineDuration - fadeOutSeconds);
      const clipEnd = startTime + actualTimelineDuration;
      fadeGain.gain.setValueAtTime(clipGain, fadeOutStart);
      fadeGain.gain.exponentialRampToValueAtTime(0.001, clipEnd);
    }

    // Connect: source → fadeGain → persistent track nodes
    source.connect(fadeGain);
    fadeGain.connect(trackNodes.audibilityGain);
    source._cookupFadeGain = fadeGain;
    const runSignalPathProbe = transportDiagRef.current.armed;

    // One-shot playback-latency probe: the first audio scheduled after Play logs
    // how far ahead it starts vs the audio clock, plus the device's reported
    // output latency. A large scheduledLeadMs => scheduler is late (a bug); a
    // small lead but large outputLatencyMs => device/output latency (not fixable
    // in software beyond the display offset).
    if (runSignalPathProbe) {
      transportDiagRef.current.armed = false;
      const nowT = audioContext.currentTime;
      console.log('[transport-diag] first audio scheduled', {
        scheduledLeadMs: Math.round((startTime - nowT) * 1000),
        outputLatencyMs: Math.round((audioContext.outputLatency || 0) * 1000),
        baseLatencyMs: Math.round((audioContext.baseLatency || 0) * 1000),
        bpm: bpmRef.current,
        atBeat: Number(atBeat).toFixed(3),
        playheadBeat: Number(transportClock.getBeat()).toFixed(3),
        clipAudioOffsetSec: Number(clipAudioOffset).toFixed(3),
        boundedOffsetSec: Number(boundedOffset).toFixed(3),
        timelineMuted: isMuted,
        masterGain: Number(masterGainRef.current?.gain?.value ?? -1).toFixed(3),
        trackAudibilityGain: Number(trackNodes.audibilityGain?.gain?.value ?? -1).toFixed(3),
        trackUserGain: Number(trackNodes.userGain?.gain?.value ?? -1).toFixed(3),
        routedToMaster: trackNodes.outputGain?._routedTo === masterGainRef.current,
        sampleRate: audioContext.sampleRate
      });
    }

    try {
      source.start(startTime, boundedOffset, duration);
      pushScheduledSource(source, [fadeGain], null, {
        startTime,
        clipId: clip.id,
        kind: 'audio'
      });
      if (runSignalPathProbe) {
        [50, 250, 600].forEach((delayMs) => {
          setTimeout(() => {
            const trackMeters = trackAnalysersRef.current.get(clip.trackId);
            console.log('[transport-diag] signal path', {
              afterMs: delayMs,
              audioContextState: audioContext.state,
              trackPeakL: analyserPeak(trackMeters?.analyserL)?.toFixed(6) ?? null,
              trackPeakR: analyserPeak(trackMeters?.analyserR)?.toFixed(6) ?? null,
              masterPeak: analyserPeak(masterAnalyserRef.current)?.toFixed(6) ?? null,
              masterGain: Number(masterGainRef.current?.gain?.value ?? -1).toFixed(3),
              routedToMaster: trackNodes.outputGain?._routedTo === masterGainRef.current
            });
          }, delayMs);
        });
      }
    } catch (error) {
      console.error(`  ❌ Error starting audio source for clip ${clip.id}:`, error);
      try {
        source.disconnect();
        fadeGain.disconnect();
      } catch (e) {
        // Already disconnected
      }
    }
  };

  // Apply automation curves across [fromBeat, toBeat): anchor the slice-start
  // value, ramp through interior points, and ramp to the slice-end value so
  // abutting slices form a continuous piecewise-linear curve (wraps included).
  // Flattened { trackId, paramId, def, points } list, rebuilt only when the
  // automation state object changes rather than walked with Object.entries on
  // every scheduler tick.
  const automationLanesCacheRef = useRef({ source: null, lanes: [] });
  const automationLanes = () => {
    const source = automationRef.current || null;
    const cache = automationLanesCacheRef.current;
    if (cache.source === source) return cache.lanes;
    const lanes = [];
    Object.entries(source || {}).forEach(([trackId, trackLanes]) => {
      Object.entries(trackLanes || {}).forEach(([paramId, lane]) => {
        const def = AUTOMATION_PARAMS[paramId];
        const points = lane?.points;
        if (!def || !Array.isArray(points) || points.length === 0) return;
        if (paramId !== 'volume' && paramId !== 'pan') return;
        lanes.push({ trackId, paramId, def, points });
      });
    });
    automationLanesCacheRef.current = { source, lanes };
    return lanes;
  };

  const scheduleAutomationInRange = (fromBeat, toBeat, timeAtFrom, bps) => {
    if (!audioContext || toBeat <= fromBeat + 1e-9) return;
    const lanes = automationLanes();
    for (let laneIndex = 0; laneIndex < lanes.length; laneIndex += 1) {
      const { trackId, paramId, def, points } = lanes[laneIndex];
      const nodes = trackNodesRef.current.get(trackId);
      const param = paramId === 'volume' ? nodes?.userGain?.gain : nodes?.panNode?.pan;
      if (param) {
        try {
          param.setValueAtTime(valueAtBeat(points, fromBeat, def.default), timeAtFrom);
          pointsInRange(points, fromBeat, toBeat).forEach((point) => {
            param.linearRampToValueAtTime(point.value, timeAtFrom + ((point.beat - fromBeat) / bps));
          });
          param.linearRampToValueAtTime(
            valueAtBeat(points, toBeat, def.default),
            timeAtFrom + ((toBeat - fromBeat) / bps)
          );
        } catch (_) {
          // Node may be mid-teardown — skip this lane for the slice.
        }
      }
    }
  };

  // Schedule every event whose start falls inside [fromBeat, toBeat), mapping
  // beats to audio-clock times relative to `timeAtFrom`. With includeActiveAtFrom,
  // clips already sounding at fromBeat are resumed mid-clip (transport start and
  // loop wraps).
  const scheduleClipsInRange = (fromBeat, toBeat, timeAtFrom, bps, options = {}) => {
    if (!audioContext) return;
    const {
      includeActiveAtFrom = false, hardStopBeat = Infinity, loopIteration = 0, crossfadeIn = false
    } = options;
    const clips = timelineClipsRef.current || [];
    const map = sessionTempoMap();

    clips.forEach((clip) => {
      // Use the authoritative (display-matching) beats so the range gate and the
      // audio start time agree with where the clip is drawn.
      const { startBeat: clipStart, durationBeats } = authoritativeClipBeats(clip, map);
      const clipEnd = clipStart + durationBeats;

      if (clip.clipType === 'audio') {
        // Same epsilon as the start-range check below, so a clip at the boundary
        // is either resumed mid-clip or start-scheduled — never both.
        if (includeActiveAtFrom && clipStart < fromBeat - 1e-6 && clipEnd > fromBeat + 1e-9) {
          scheduleAudioClipInstance(clip, {
            atBeat: fromBeat,
            atTime: timeAtFrom,
            bps,
            hardStopBeat,
            midClip: true,
            crossfadeIn
          });
        } else if (clipStart >= fromBeat - 1e-6 && clipStart < toBeat - 1e-6) {
          scheduleAudioClipInstance(clip, {
            atBeat: clipStart,
            atTime: timeAtFrom + (Math.max(0, clipStart - fromBeat) / bps),
            bps,
            hardStopBeat,
            midClip: false
          });
        }
        return;
      }

      if (clip.clipType === 'pattern' && clip.clipData?.kind !== 'instrument' && toBeat > fromBeat + 1e-9) {
        // Only clips overlapping this slice can contribute hits. Skipping the
        // rest avoids rebuilding every clip's rows, closures and step data on
        // every 25 ms tick. A zero-length clip falls back to its pattern length
        // inside schedulePatternClipRange, so only its start bounds it here.
        if (clipStart >= toBeat - 1e-6) return;
        if (durationBeats > 0 && clipEnd <= fromBeat - 1e-6) return;
        schedulePatternClipRange(clip, fromBeat, toBeat, timeAtFrom, bps, loopIteration);
      }
    });
  };

  // Rolling lookahead scheduler: walks the transport forward in slices against the
  // Web Audio clock, scheduling clip starts and pattern hits sample-accurately.
  // Loop boundaries are part of the walk, so loop repeats are exact — not tied to
  // animation frames. Re-runs (and rebases to the audible beat) on tempo or loop
  // region changes so playback stays continuous.
  useEffect(() => {
    if (!audioContext || !isPlaying) return undefined;

    transportShapeRef.current = {
      bps: Math.max(0.001, bpm / 60),
      region: currentLoopRegion(),
      muted: isMuted
    };
    playbackRebasedRef.current = false;
    // Anchor to the currently audible beat once the transport starts rolling.
    playbackStartTime.current = audioContext.currentTime;
    playbackStartBeat.current = transportClock.getBeat();
    // A take's first sample is placed against the anchor it was performed
    // against; later tempo or loop rebases must not move it.
    micRecordAnchorRef.current = micRecorderRef.current?.active
      ? {
        time: playbackStartTime.current,
        beat: playbackStartBeat.current,
        bps: transportShapeRef.current.bps
      }
      : null;
    stopAllScheduledSources();
    schedulerStateRef.current = null;

    const tick = () => {
      const { bps, muted: isMuted } = transportShapeRef.current;
      const tickPerformanceTime = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const now = audioContext.currentTime;
      const performanceProbe = audioPerformanceProbeRef.current;
      if (performanceProbe.active) {
        performanceProbe.schedulerTicks += 1;
        if (Number.isFinite(performanceProbe.previousTickPerformanceTime)) {
          const schedulerGapMs = tickPerformanceTime - performanceProbe.previousTickPerformanceTime;
          performanceProbe.maxSchedulerGapMs = Math.max(performanceProbe.maxSchedulerGapMs, schedulerGapMs);
          if (schedulerGapMs > SCHEDULER_INTERVAL_MS * 2) performanceProbe.lateSchedulerTicks += 1;
          if (Number.isFinite(performanceProbe.previousAudioTime)) {
            const audioDeltaMs = (now - performanceProbe.previousAudioTime) * 1000;
            performanceProbe.maxAudioClockDriftMs = Math.max(
              performanceProbe.maxAudioClockDriftMs,
              Math.abs(audioDeltaMs - schedulerGapMs)
            );
          }
        }
        performanceProbe.previousTickPerformanceTime = tickPerformanceTime;
        performanceProbe.previousAudioTime = now;
      }
      let st = schedulerStateRef.current;
      if (!st) {
        const beat = transportClock.getBeat();
        // Prime a shared future boundary so the first notes are scheduled before
        // their deadline. Visuals use this exact anchor and wait for device output.
        const startTime = now + 0.03;
        playbackStartTime.current = startTime;
        playbackStartBeat.current = beat;
        st = { horizonTime: startTime, horizonBeat: beat, loopIteration: 0, primed: false };
        schedulerStateRef.current = st;
      }

      if (!st.primed) {
        const region = currentLoopRegion();
        // Resume clips already sounding at the transport position.
        if (!isMuted) {
          scheduleClipsInRange(st.horizonBeat, st.horizonBeat, st.horizonTime, bps, {
            includeActiveAtFrom: true,
            hardStopBeat: region ? region.endBeat : Infinity,
            loopIteration: st.loopIteration,
            crossfadeIn: st.crossfadeIn === true
          });
        }
        st.primed = true;
      }

      const windowEnd = now + SCHEDULE_LOOKAHEAD_SECONDS;
      let guard = 0;
      while (st.horizonTime < windowEnd && guard < 64) {
        guard += 1;
        const region = currentLoopRegion();

        // At (or past) the loop end: wrap sample-accurately at the boundary time.
        // Seeks beyond the loop end use the same modulo mapping as the playhead
        // display (wrapTransportBeat), so audio and visuals always agree.
        if (region && st.horizonBeat >= region.endBeat - 1e-9) {
          st.loopIteration += 1;
          st.horizonBeat = st.horizonBeat >= region.endBeat
            ? wrapTransportBeat(st.horizonBeat, region)
            : region.startBeat;
          // Old-iteration dedup keys are no longer needed once the horizon wraps.
          scheduledPatternHitsRef.current.clear();
          if (!isMuted) {
            scheduleClipsInRange(st.horizonBeat, st.horizonBeat, st.horizonTime, bps, {
              includeActiveAtFrom: true,
              hardStopBeat: region.endBeat,
              loopIteration: st.loopIteration
            });
          }
          continue;
        }

        const segEndBeat = region ? region.endBeat : Infinity;
        const segEndTime = Number.isFinite(segEndBeat)
          ? st.horizonTime + ((segEndBeat - st.horizonBeat) / bps)
          : Infinity;
        const sliceEndTime = Math.min(windowEnd, segEndTime);
        const sliceEndBeat = st.horizonBeat + ((sliceEndTime - st.horizonTime) * bps);

        if (!isMuted) {
          scheduleClipsInRange(st.horizonBeat, sliceEndBeat, st.horizonTime, bps, {
            hardStopBeat: segEndBeat,
            loopIteration: st.loopIteration
          });
          scheduleAutomationInRange(st.horizonBeat, sliceEndBeat, st.horizonTime, bps);
        }

        st.horizonTime = sliceEndTime;
        st.horizonBeat = sliceEndBeat;

        if (!Number.isFinite(segEndTime) || segEndTime > windowEnd) break;
        // Otherwise the loop-wrap branch handles the boundary next iteration.
      }
      if (performanceProbe.active && st) {
        const lookaheadMs = Math.max(0, (st.horizonTime - now) * 1000);
        performanceProbe.minLookaheadMs = Math.min(performanceProbe.minLookaheadMs, lookaheadMs);
        const schedulerWorkMs = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - tickPerformanceTime;
        performanceProbe.maxSchedulerWorkMs = Math.max(performanceProbe.maxSchedulerWorkMs || 0, schedulerWorkMs);
        performanceProbe.totalSchedulerWorkMs = (performanceProbe.totalSchedulerWorkMs || 0) + schedulerWorkMs;
        if (schedulerWorkMs > SCHEDULER_INTERVAL_MS) {
          performanceProbe.overBudgetSchedulerTicks = (performanceProbe.overBudgetSchedulerTicks || 0) + 1;
        }
      }
    };

    schedulerTickRef.current = tick;
    tick();
    const timer = setSchedulerInterval(tick, SCHEDULER_INTERVAL_MS);
    return () => {
      clearSchedulerInterval(timer);
      if (schedulerTickRef.current === tick) schedulerTickRef.current = null;
    };
  // Scheduling helpers read live data via refs; tempo, loop and mute changes
  // are applied by the rebase effect below without restarting this one.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext, isPlaying, stopAllScheduledSources]);

  // Tempo, loop and timeline-mute changes while playing. The old path stopped
  // every source and restarted from the audible beat: an audible dropout on
  // every tempo nudge, and a jump back by the output latency. Now only the
  // not-yet-audible lookahead is rebuilt: queued work after a tiny guard window
  // is cancelled, sounding audio clips (whose rate and length depend on tempo
  // and loop) fade out and resume mid-clip at the cutoff, and one-shot drum hits
  // already sounding simply ring out.
  useEffect(() => {
    const shape = transportShapeRef.current;
    const next = {
      bps: Math.max(0.001, bpm / 60),
      region: currentLoopRegion(),
      muted: isMuted
    };
    const tempoChanged = Math.abs(next.bps - shape.bps) > 1e-9;
    const loopChanged = (shape.region?.startBeat ?? null) !== (next.region?.startBeat ?? null)
      || (shape.region?.endBeat ?? null) !== (next.region?.endBeat ?? null);
    const muteChanged = shape.muted !== next.muted;
    if (!audioContext || !isPlaying || playbackStartTime.current === null || !schedulerTickRef.current) {
      transportShapeRef.current = next;
      return;
    }
    if (!tempoChanged && !loopChanged && !muteChanged) return;

    const now = audioContext.currentTime;
    let cutoff;
    if (next.muted) {
      fadeStopAllScheduledSources(now);
      cutoff = now;
    } else {
      cutoff = cancelFutureScheduledSources(now);
      fadeStopSoundingAudioClips(cutoff);
      cancelAutomationFrom(cutoff);
    }

    // Beat at the cutoff under the OLD shape. A tempo change keeps unwrapped
    // beat continuity (instrument dedupe keys depend on it); a loop change
    // restarts from the audible song position, then wraps into the new loop.
    const unwrapped = playbackStartBeat.current
      + Math.max(0, cutoff - playbackStartTime.current) * shape.bps;
    const startBeat = loopChanged ? wrapTransportBeat(unwrapped, shape.region) : unwrapped;
    playbackStartTime.current = cutoff;
    playbackStartBeat.current = startBeat;
    playbackRebasedRef.current = true;
    transportShapeRef.current = next;
    rebaseMetronome(cutoff);
    schedulerStateRef.current = {
      horizonTime: cutoff,
      horizonBeat: wrapTransportBeat(startBeat, next.region),
      loopIteration: schedulerStateRef.current?.loopIteration || 0,
      // Unprimed: audio clips spanning the cutoff resume mid-clip, fading in
      // against the fade-out of the source they replace.
      primed: false,
      crossfadeIn: !next.muted
    };
    schedulerTickRef.current();
  // Scheduling helpers read live data via refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioContext, isPlaying, isMuted, bpm, loopEnabled, loopStartBeat, loopEndBeat]);

  // Playhead display: writes the wrapped transport beat into transportClock every
  // frame. Deliberately does NOT touch React state — subscribers (playhead line,
  // time readout) update without re-rendering the whole provider tree.
  useEffect(() => {
    if (!isPlaying || !audioContext) return undefined;

    const bps = Math.max(0.001, bpm / 60);
    const frame = { id: null };
    const updatePlayhead = () => {
      if (playbackStartTime.current !== null) {
        const presentedTime = audibleContextTime(audioContext, performance.now());
        const rawElapsed = presentedTime - playbackStartTime.current;
        // After a live rebase the anchor sits a few ms ahead; what is heard is
        // still the lead-in, so let the playhead run up to it instead of pausing.
        const elapsed = playbackRebasedRef.current ? rawElapsed : Math.max(0, rawElapsed);
        const unwrapped = playbackStartBeat.current + (elapsed * bps);
        transportClock.setBeat(wrapTransportBeat(unwrapped, currentLoopRegion()));
      }
      frame.id = requestAnimationFrame(updatePlayhead);
    };
    updatePlayhead();

    return () => {
      if (frame.id) cancelAnimationFrame(frame.id);
    };
  // Wrap math reads loop refs/state captured by the same deps as the scheduler.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, audioContext, bpm, loopEnabled, loopStartBeat, loopEndBeat]);

  // Keep the shared clock aligned with stopped-state seeks (timeline clicks etc.).
  useEffect(() => {
    if (!isPlaying) transportClock.setBeat(playheadPosition);
  }, [playheadPosition, isPlaying]);

  // Live audition: apply clip edits to the not-yet-audible lookahead without
  // tearing down sources that are already sounding. The former global stop was
  // the source of edit-time clicks, stutters, and short full-mix dropouts.
  const lastAuditionClipsRef = useRef(null);
  useEffect(() => {
    if (!isPlaying || !audioContext || playbackStartTime.current === null) {
      lastAuditionClipsRef.current = null;
      return undefined;
    }
    if (lastAuditionClipsRef.current === null) {
      lastAuditionClipsRef.current = timelineClips;
      return undefined;
    }
    if (lastAuditionClipsRef.current === timelineClips) return undefined;
    // A live-recording write is not an edit: the note is already in the past.
    if (timelineClips === recordingWriteClipsRef.current) {
      lastAuditionClipsRef.current = timelineClips;
      return undefined;
    }
    lastAuditionClipsRef.current = timelineClips;

    const now = audioContext.currentTime;
    const cutoffTime = cancelFutureScheduledSources(now);
    const bps = Math.max(0.001, bpmRef.current / 60);
    const elapsed = Math.max(0, now - playbackStartTime.current);
    const currentUnwrappedBeat = playbackStartBeat.current + (elapsed * bps);
    const cutoffUnwrappedBeat = currentUnwrappedBeat + ((cutoffTime - now) * bps);
    const region = getActiveLoopRegion();
    const currentBeat = wrapTransportBeat(currentUnwrappedBeat, region);
    const cutoffBeat = wrapTransportBeat(cutoffUnwrappedBeat, region);
    const previousIteration = schedulerStateRef.current?.loopIteration || 0;
    const crossedLoop = Boolean(region && cutoffBeat < currentBeat - 1e-9);

    // Start refilling immediately after the protected render window. primed=true
    // deliberately avoids starting a second copy of a clip already playing.
    schedulerStateRef.current = {
      horizonTime: cutoffTime,
      horizonBeat: cutoffBeat,
      loopIteration: previousIteration + (crossedLoop ? 1 : 0),
      primed: true
    };
    schedulerTickRef.current?.();
    return undefined;
  // Transport refs and loop resolution are authoritative and read live here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineClips, isPlaying, audioContext, cancelFutureScheduledSources]);

  /**
   * Transport beat for a moment on the performance.now() timeline — the domain
   * of Web MIDI and DOM event timestamps.
   *
   * Recording uses this rather than transportClock, which is only written once
   * per animation frame and so quantizes captured notes to ~17ms. Returns null
   * when the transport is stopped or the clocks cannot be related, leaving the
   * caller to fall back.
   */
  const beatAtPerformanceTime = useCallback((performanceTime) => {
    const ctx = audioContextRef.current;
    if (!ctx || playbackStartTime.current === null) return null;
    const sample = typeof ctx.getOutputTimestamp === 'function' ? ctx.getOutputTimestamp() : null;
    const contextTime = contextTimeAtPerformanceTime(performanceTime, sample);
    if (contextTime === null) return null;
    const unwrapped = transportBeatAtContextTime({
      contextTime,
      startTime: playbackStartTime.current,
      startBeat: playbackStartBeat.current,
      bps: Math.max(0.001, bpmRef.current / 60),
      latency: ctx.outputLatency || ctx.baseLatency || 0
    });
    if (unwrapped === null) return null;
    return wrapTransportBeat(unwrapped, currentLoopRegion());
  // Reads live transport state through refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The transport's lookahead window, split at the loop boundary.
   *
   * Read from the audio clock rather than the frame-sampled playhead, and
   * loop-aware, so a scheduler using it queues the notes at the loop start
   * BEFORE the wrap instead of discovering them once they are already past.
   * Returns null when the transport is not running.
   */
  // Read presentation time directly for lightweight embedded players; avoid
  // waiting for the provider's separate animation-frame clock publication.
  const getAudiblePlayheadBeat = useCallback(() => {
    const ctx = audioContextRef.current;
    if (!ctx || playbackStartTime.current === null) return transportClock.getBeat();
    const rawElapsed = audibleContextTime(ctx, performance.now()) - playbackStartTime.current;
    const elapsed = playbackRebasedRef.current ? rawElapsed : Math.max(0, rawElapsed);
    return wrapTransportBeat(playbackStartBeat.current + elapsed * Math.max(0.001, bpmRef.current / 60), currentLoopRegion());
  // Transport anchors, tempo and loop region are all read through live refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTransportSchedule = useCallback((lookaheadSeconds = 0.12, { includeStart = false } = {}) => {
    const ctx = audioContextRef.current;
    if (!ctx || playbackStartTime.current === null) return null;
    const bps = Math.max(0.001, bpmRef.current / 60);
    const now = ctx.currentTime;
    const elapsed = Math.max(0, now - playbackStartTime.current);
    const window = scheduleClockWindow({
      startBeat: playbackStartBeat.current,
      elapsedSeconds: elapsed,
      bps,
      lookaheadSeconds: Math.max(0.001, lookaheadSeconds),
      region: currentLoopRegion(),
      includeStart
    });
    if (!window) return null;
    return { now, bps, ...window };
  // Reads live transport state through refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLoopRegion = useCallback((startBeat, endBeat) => {
    const safeStart = Math.max(0, Number.isFinite(startBeat) ? startBeat : 0);
    const safeEnd = Math.max(safeStart + 0.25, Number.isFinite(endBeat) ? endBeat : safeStart + 4);
    setLoopStartBeat(safeStart);
    setLoopEndBeat(safeEnd);
    setLoopEnabled(true);
  }, []);

  const clearLoopRegion = useCallback(() => {
    setLoopEnabled(false);
  }, []);

  // Registered from DrumContext, which owns these. Both are memoised because
  // DrumContext depends on them from effects: an identity that changed every
  // render would re-run those effects on every unrelated audio state change.
  // They only assign refs, so an empty dependency list is correct.
  const registerDrumBuffers = useCallback((buffers) => {
    drumBuffersRef.current = buffers;
    samplePlaybackReadyRef.current = false;
    if (playbackStartTime.current === null) {
      const pool = samplePlaybackPoolRef.current;
      const decoded = uniqueDecodedBuffers(buffers);
      if (pool) void pool.preload(decoded).then((slots) => {
        if (samplePlaybackPoolRef.current === pool && drumBuffersRef.current === buffers) {
          samplePlaybackReadyRef.current = slots.length === decoded.length
            && slots.every(Number.isInteger);
        }
      });
    }
  }, []);

  const registerDrumVolumes = useCallback((volumes) => {
    drumVolumesRef.current = volumes || {};
  }, []);

  const resolvePatternStepData = (clipData = {}) => {
    if (clipData?.pattern && clipData?.noteData) {
      return {
        pattern: clipData.pattern,
        noteData: clipData.noteData,
        patternLength: clipData.patternLength || 16
      };
    }

    if (!Array.isArray(clipData?.noteEvents) || clipData.noteEvents.length === 0) {
      return {
        pattern: clipData?.pattern || {},
        noteData: clipData?.noteData || {},
        patternLength: clipData?.patternLength || 16
      };
    }

    const ppq = Number(clipData.ppq) || 96;
    const ticksPerStep = ppq / 4;
    const pattern = {};
    const noteData = {};
    let maxStep = 0;

    clipData.noteEvents.forEach((event) => {
      const drumIndex = Number(event?.drumIndex);
      if (!Number.isFinite(drumIndex)) return;
      const startTick = Number(event?.startTick) || 0;
      const durationTicks = Math.max(1, Number(event?.durationTicks) || ticksPerStep || 1);
      const startStep = Math.max(0, Math.floor(startTick / ticksPerStep));
      const durationSteps = Math.max(1, Math.ceil(durationTicks / ticksPerStep));
      const key = `${drumIndex}-${startStep}`;
      const pitches = Array.isArray(event?.pitches)
        ? event.pitches
        : (Number.isFinite(event?.pitch) ? [event.pitch] : []);
      const unique = [...new Set(pitches.map(Number).filter(Number.isFinite).sort((a, b) => a - b))];
      if (unique.length === 0) return;
      pattern[key] = true;
      noteData[key] = {
        pitch: unique[0],
        ...(unique.length > 1 ? { pitches: unique } : {}),
        durationSteps,
        durationTicks,
        ppq
      };
      maxStep = Math.max(maxStep, startStep + durationSteps);
    });

    const patternLength = Math.max(16, Math.ceil(Math.max(clipData.patternLength || 16, maxStep || 16) / 16) * 16);
    return { pattern, noteData, patternLength };
  };

  const getPatternDrumRows = (clipData = {}) => {
    const rows = Array.isArray(clipData?.drumSounds) ? clipData.drumSounds : [];
    if (rows.length > 0) return rows;
    return [
      { key: 'kick', playbackMode: 'drum' },
      { key: 'snare', playbackMode: 'drum' },
      { key: 'hihat', playbackMode: 'drum' },
      { key: 'clap', playbackMode: 'drum' },
      { key: 'perc', playbackMode: 'drum' }
    ];
  };

  // Schedule pattern clip playback
  // Schedule the pattern hits whose beat falls inside [fromBeat, toBeat), with
  // beat→time mapping anchored at timeAtFrom. loopIteration keys dedup entries so
  // hits re-trigger on every loop pass.
  const schedulePatternClipRange = (clip, fromBeat, toBeat, timeAtFrom, beatsPerSecond, loopIteration = 0) => {
    if (clip.clipData?.kind === 'instrument') return;
    const noteEvents = Array.isArray(clip?.clipData?.noteEvents) ? clip.clipData.noteEvents : [];
    const hasPreciseEvents = noteEvents.length > 0 || clip.clipData?.noteEventsAuthoritative === true;
    const resolvedStepData = hasPreciseEvents ? null : resolvePatternStepData(clip.clipData);
    const pattern = resolvedStepData?.pattern || clip.clipData?.pattern || {};
    const noteData = resolvedStepData?.noteData || clip.clipData?.noteData || {};
    if (!pattern || !drumBuffersRef.current || !audioContext) {
      console.warn(`  ⚠️ Cannot play pattern: missing resources`);
      return;
    }

    // console.log(`  🥁 Scheduling pattern clip with ${Object.keys(pattern).filter(k => pattern[k]).length} active steps`);

    // Authoritative (display-matching) position so pattern hits land where the
    // clip is drawn, not at a possibly-stale clip.startBeat.
    const { startBeat: clipStartBeat, durationBeats: authoritativeDurationBeats } = authoritativeClipBeats(clip);

    // Pattern uses 16th-note resolution (4 steps per beat)
    const explicitPatternLength = Number(resolvedStepData?.patternLength || clip.clipData?.patternLength);
    const stepCount = Number.isFinite(explicitPatternLength) && explicitPatternLength > 0
      ? explicitPatternLength
      : Math.max(16, Math.ceil(Math.max(0, authoritativeDurationBeats) * 4));
    const beatsPerStep = 0.25;
    const clipDurationBeats = authoritativeDurationBeats > 0 ? authoritativeDurationBeats : (stepCount * beatsPerStep);
    const maxPatternBeats = stepCount * beatsPerStep;
    const effectiveBeats = Math.min(maxPatternBeats, clipDurationBeats);

    const drumRows = getPatternDrumRows(clip.clipData);
    const drums = drumRows.map((d) => d?.key).filter(Boolean);
    const activeDrumIndices = Number.isInteger(clip.clipData?.drumLaneIndex)
      ? [clip.clipData.drumLaneIndex] : drums.map((_, index) => index);
    const drumCutMode = clip.clipData?.drumCutMode || {};
    const registerChokedVoice = (drumName, drumIndex, playTime, voice) => {
      const row = drumRows[drumIndex] || { key: drumName };
      const sample = clip.clipData?.customSamples?.[drumName] || {};
      const explicitChoke = Object.prototype.hasOwnProperty.call(drumCutMode, drumName)
        ? Boolean(drumCutMode[drumName])
        : undefined;
      if (!shouldChokeDrumLane(row, sample, explicitChoke)) return;
      const role = drumLaneRole(row, sample);
      const chokeKey = role === 'sub' ? 'low-end:sub' : `${clip.id}:${drumName}`;
      // Independent clips can be visited out of beat order. Keep the choke
      // voices sorted by audio time so a later-scheduled earlier hit still
      // cuts its predecessor and is itself cut by the next hit.
      const currentVoices = scheduledDrumLaneVoicesRef.current.get(chokeKey);
      const voices = Array.isArray(currentVoices) ? currentVoices : (currentVoices ? [currentVoices] : []);
      const { previous, next, ordered } = placeScheduledChokeVoice(voices, voice);
      if (previous && previous.source !== voice.source) fadeAndStopDrumVoice(previous, playTime);
      if (next && next.source !== voice.source) fadeAndStopDrumVoice(voice, next.startTime);
      scheduledDrumLaneVoicesRef.current.set(chokeKey, ordered);
      voice.source.addEventListener?.('ended', () => {
        const current = scheduledDrumLaneVoicesRef.current.get(chokeKey) || [];
        const remaining = (Array.isArray(current) ? current : [current])
          .filter((candidate) => candidate.source !== voice.source);
        if (remaining.length) scheduledDrumLaneVoicesRef.current.set(chokeKey, remaining);
        else scheduledDrumLaneVoicesRef.current.delete(chokeKey);
      }, { once: true });
    };
    const tryScheduleRustHit = ({
      buffer, drumName, drumIndex, playTime, playbackRate, baseGain,
      durationSeconds, laneSettings, eventKey
    }) => {
      const laneTrack = tracksRef.current.find((track) => track.id === drumLaneTrackId(drumIndex));
      const pool = laneTrack
        ? drumLanePoolsRef.current.get(laneTrack.id)
        : samplePlaybackPoolRef.current;
      if (!pool || (!laneTrack && !samplePlaybackReadyRef.current)) return false;
      if (pool.slotFor(buffer) === null) {
        void pool.load(buffer);
        return false;
      }
      const row = drumRows[drumIndex] || { key: drumName };
      const sampleInfo = clip.clipData?.customSamples?.[drumName] || {};
      const explicitChoke = Object.prototype.hasOwnProperty.call(drumCutMode, drumName)
        ? Boolean(drumCutMode[drumName])
        : undefined;
      const shouldChoke = shouldChokeDrumLane(row, sampleInfo, explicitChoke);
      const role = drumLaneRole(row, sampleInfo);
      const chokeKey = role === 'sub' ? 'low-end:sub' : `${clip.id}:${drumName}`;
      const owner = stableSamplerToken(`event:${eventKey}`);
      const scheduled = pool.schedule(buffer, playTime, audioContext.sampleRate, {
        rate: playbackRate,
        gain: baseGain,
        startPosition: Math.floor(buffer.length * laneSettings.trimStartPct),
        endPosition: Math.ceil(buffer.length * laneSettings.trimEndPct),
        attackMs: laneSettings.attackMs,
        releaseMs: laneSettings.releaseMs,
        durationSeconds: laneSettings.playbackMode === 'oneshot' ? durationSeconds : null,
        chokeGroup: shouldChoke ? stableSamplerToken(`choke:${chokeKey}`) : 0,
        chokeMs: DRUM_CHOKE_FADE_MS,
        owner
      });
      if (!scheduled) return false;
      const naturalDuration = ((buffer.duration * laneSettings.trimEndPct)
        - (buffer.duration * laneSettings.trimStartPct)) / Math.max(MIN_PLAYBACK_RATE, playbackRate);
      const audibleDuration = laneSettings.playbackMode === 'oneshot'
        ? Math.min(naturalDuration, durationSeconds)
        : naturalDuration;
      const source = createRustScheduledSource(
        pool,
        owner,
        playTime + Math.max(0.01, audibleDuration) + (laneSettings.releaseMs / 1000),
        audioContext
      );
      pushScheduledSource(source, [], clip.id, {
        startTime: playTime,
        eventKey,
        clipId: clip.id,
        kind: 'pattern-rust'
      });
      scheduledPatternHitsRef.current.add(eventKey);
      samplePlaybackUsageRef.current.rustPatternHits += 1;
      return true;
    };

    // Prefer high-resolution note events when available so recorded timing is not pre-snapped.
    if (hasPreciseEvents) {
      const ppq = Number(clip.clipData.ppq) || 96;
      const normalizedStartTick = (event) => Math.max(0, Number(event?.startTick) || 0);
      let eventsAreSorted = patternEventOrderRef.current.get(noteEvents);
      if (eventsAreSorted === undefined) {
        eventsAreSorted = true;
        let previousTick = -Infinity;
        for (let index = 0; index < noteEvents.length; index += 1) {
          const tick = normalizedStartTick(noteEvents[index]);
          if (tick < previousTick) {
            eventsAreSorted = false;
            break;
          }
          previousTick = tick;
        }
        patternEventOrderRef.current.set(noteEvents, eventsAreSorted);
      }

      const scheduleEvent = (event, eventIndex) => {
        const drumIndex = Number(event?.drumIndex);
        if (!Number.isFinite(drumIndex) || drumIndex < 0 || drumIndex >= drums.length) return;

        const drumName = drums[drumIndex];
        const buffer = resolveDrumSampleBuffer(drumBuffersRef.current, clip.clipData, drumName);
        if (!buffer) return;

        const startTick = Math.max(0, Number(event?.startTick) || 0);
        const durationTicks = Math.max(1, Number(event?.durationTicks) || Math.round(ppq / 4));
        const eventBeat = startTick / ppq;

        if (eventBeat >= effectiveBeats) return;

        const stepBeat = clipStartBeat + eventBeat;
        if (stepBeat >= clipStartBeat + clipDurationBeats) return;
        if (stepBeat < fromBeat - 1e-6 || stepBeat >= toBeat - 1e-6) return;

        const pitchList = Array.isArray(event?.pitches) ? event.pitches : [];
        const midiNote = Number.isFinite(event?.pitch) ? event.pitch : pitchList[0] ?? null;
        const velocity = clampVelocity(event?.velocity);
        const laneSettings = getDrumRowPlaybackSettings(drumRows[drumIndex]);
        const playbackMode = laneSettings.playbackMode;

        const playTime = Math.max(timeAtFrom, timeAtFrom + ((stepBeat - fromBeat) / beatsPerSecond));
        const eventKey = `${clip.id}:${drumIndex}:evt:${event?.id || eventIndex}:${startTick}:L${loopIteration}`;
        if (scheduledPatternHitsRef.current.has(eventKey)) {
          return;
        }
        scheduleDuckingForHit(drumLaneTrackId(drumIndex), playTime, clampVelocity(event?.velocity));

        const midiSemitones = midiNote !== null ? (midiNote - 72) : 0;
        const totalSemitones = midiSemitones + laneSettings.tuneSemitones;
        // Each strike slightly different, as a played drum is (deterministic: export matches playback).
        const hitVariation = drumHitVariation({ clipId: clip.id, drumIndex, beat: stepBeat,
          pitched: midiNote !== null || laneSettings.playbackMode === 'oneshot', amount: laneSettings.humanize });
        let playbackRate = Math.pow(2, totalSemitones / 12) * hitVariation.rateFactor;
        playbackRate = Math.max(0.25, Math.min(4.0, playbackRate));

        const sourceStartOffset = buffer.duration * laneSettings.trimStartPct;
        const sourceTrimDuration = Math.max(0.01, (buffer.duration * laneSettings.trimEndPct) - sourceStartOffset);

        const laneTrack = tracksRef.current.find((track) => track.id === drumLaneTrackId(drumIndex));
        const volume = laneTrack ? 1 : (clip.clipData?.drumVolumes?.[drumName]
          ?? drumVolumesRef.current?.[drumName] ?? 1.0);
        const baseGain = DRUM_VOICE_GAIN * volume * velocity * hitVariation.gainFactor;

        const laneLimitedDuration = sourceTrimDuration / playbackRate;
        const durationSeconds = Math.min(laneLimitedDuration, Math.max(0.02, (durationTicks / ppq) / beatsPerSecond));

        if (tryScheduleRustHit({
          buffer,
          drumName,
          drumIndex,
          playTime,
          playbackRate,
          baseGain,
          durationSeconds,
          laneSettings,
          eventKey
        })) return;

        samplePlaybackUsageRef.current.webAudioPatternHits += 1;
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.playbackRate.value = playbackRate;
        const gainNode = audioContext.createGain();
        const envelope = drumVoiceEnvelope({
          startTime: playTime,
          naturalDuration: laneLimitedDuration,
          noteDuration: durationSeconds,
          attackMs: laneSettings.attackMs,
          releaseMs: laneSettings.releaseMs,
          playbackMode
        });
        applyDrumVoiceEnvelope(gainNode.gain, baseGain, envelope);

        source.connect(gainNode);
        gainNode.connect(laneTrack
          ? (getTrackInput(laneTrack.id) || drumBusInputRef.current || masterGainRef.current)
          : (drumBusInputRef.current || masterGainRef.current));

        source.start(playTime, sourceStartOffset, sourceTrimDuration);
        source.stop(envelope.sourceStop);
        const voice = { source, gainNode, startTime: playTime };
        source._cookupDrumVoice = voice;
        registerChokedVoice(drumName, drumIndex, playTime, voice);

        pushScheduledSource(source, [gainNode], clip.id, {
          startTime: playTime,
          eventKey,
          clipId: clip.id,
          kind: 'pattern'
        });
        scheduledPatternHitsRef.current.add(eventKey);
      };

      if (eventsAreSorted) {
        const firstTick = Math.max(0, (fromBeat - clipStartBeat - 1e-6) * ppq);
        let low = 0;
        let high = noteEvents.length;
        while (low < high) {
          const middle = (low + high) >>> 1;
          if (normalizedStartTick(noteEvents[middle]) < firstTick) low = middle + 1;
          else high = middle;
        }
        for (let eventIndex = low; eventIndex < noteEvents.length; eventIndex += 1) {
          const event = noteEvents[eventIndex];
          const eventBeat = normalizedStartTick(event) / ppq;
          if (eventBeat >= effectiveBeats || clipStartBeat + eventBeat >= toBeat - 1e-6) break;
          scheduleEvent(event, eventIndex);
        }
      } else {
        // Old or hand-edited sessions can contain unsorted events. Keep those
        // correct while canonical ordered CookUp notes use the fast range lookup.
        noteEvents.forEach(scheduleEvent);
      }

      return;
    }

    // Schedule each active step
    // console.log(`    Pattern keys:`, Object.keys(pattern).filter(k => pattern[k]));
    // console.log(`    Drum buffers available:`, Object.keys(drumBuffersRef.current || {}));

    const firstStep = Math.max(0, Math.floor(Math.max(0, fromBeat - clipStartBeat - 1e-6) / beatsPerStep));
    const lastStepExclusive = Math.min(
      stepCount,
      Math.ceil(Math.max(0, toBeat - clipStartBeat - 1e-6) / beatsPerStep)
    );
    for (let step = firstStep; step < lastStepExclusive; step++) {
      const patternBeat = step * beatsPerStep;
      if (patternBeat >= effectiveBeats) break; // In Song mode, timeline clip length is the authority

      const stepBeat = clipStartBeat + (step * beatsPerStep);
      if (stepBeat >= clipStartBeat + clipDurationBeats) break;

      // Only schedule steps inside this scheduling slice
      if (stepBeat < fromBeat - 1e-6) continue;
      if (stepBeat >= toBeat - 1e-6) break;

      // Check each drum on this step
      // Pattern uses drum indices (0,1,2,3,4) not names
      for (const drumIndex of activeDrumIndices) {
        const drumName = drums[drumIndex];
        const key = `${drumIndex}-${step}`;

        if (pattern[key]) {
          // console.log(`    Found active step: ${key} (${drumName})`);

          // This drum should play on this step
          const buffer = resolveDrumSampleBuffer(drumBuffersRef.current, clip.clipData, drumName);
          if (!buffer) {
            console.warn(`    ⚠️ No buffer for ${drumName}`);
            continue;
          }

          const noteInfo = noteData?.[key];
          const midiNote = noteInfo?.pitch ?? null;
          const durationSteps = Math.max(1, Number(noteInfo?.durationSteps || 1));
          const laneSettings = getDrumRowPlaybackSettings(drumRows[drumIndex]);
          const playbackMode = laneSettings.playbackMode;

          const playTime = Math.max(timeAtFrom, timeAtFrom + ((stepBeat - fromBeat) / beatsPerSecond));
          const eventKey = `${clip.id}:${drumIndex}:${stepBeat.toFixed(4)}:L${loopIteration}`;
          if (scheduledPatternHitsRef.current.has(eventKey)) {
            continue;
          }
          scheduleDuckingForHit(drumLaneTrackId(drumIndex), playTime, clampVelocity(noteInfo?.velocity));

          const midiSemitones = midiNote !== null ? (midiNote - 72) : 0;
          const totalSemitones = midiSemitones + laneSettings.tuneSemitones;
          // Each strike slightly different, as a played drum is (deterministic: export matches playback).
          const hitVariation = drumHitVariation({ clipId: clip.id, drumIndex, beat: stepBeat,
            pitched: midiNote !== null || laneSettings.playbackMode === 'oneshot', amount: laneSettings.humanize });
          let playbackRate = Math.pow(2, totalSemitones / 12) * hitVariation.rateFactor;
          playbackRate = Math.max(0.25, Math.min(4.0, playbackRate));

          const sourceStartOffset = buffer.duration * laneSettings.trimStartPct;
          const sourceTrimDuration = Math.max(0.01, (buffer.duration * laneSettings.trimEndPct) - sourceStartOffset);

          // Apply per-drum volume and per-step velocity
          const laneTrack = tracksRef.current.find((track) => track.id === drumLaneTrackId(drumIndex));
          const volume = laneTrack ? 1 : (clip.clipData?.drumVolumes?.[drumName]
            ?? drumVolumesRef.current?.[drumName] ?? 1.0);
          const velocity = clampVelocity(noteInfo?.velocity);
          const baseGain = DRUM_VOICE_GAIN * volume * velocity * hitVariation.gainFactor;

          const laneLimitedDuration = sourceTrimDuration / playbackRate;
          const durationSeconds = Math.min(laneLimitedDuration, Math.max(0.02, durationSteps * beatsPerStep / beatsPerSecond));

          if (tryScheduleRustHit({
            buffer,
            drumName,
            drumIndex,
            playTime,
            playbackRate,
            baseGain,
            durationSeconds,
            laneSettings,
            eventKey
          })) continue;

          samplePlaybackUsageRef.current.webAudioPatternHits += 1;
          const source = audioContext.createBufferSource();
          source.buffer = buffer;
          source.playbackRate.value = playbackRate;
          const gainNode = audioContext.createGain();
          const envelope = drumVoiceEnvelope({
            startTime: playTime,
            naturalDuration: laneLimitedDuration,
            noteDuration: durationSeconds,
            attackMs: laneSettings.attackMs,
            releaseMs: laneSettings.releaseMs,
            playbackMode
          });
          applyDrumVoiceEnvelope(gainNode.gain, baseGain, envelope);

          // Connect through master gain
          source.connect(gainNode);
          gainNode.connect(laneTrack
            ? (getTrackInput(laneTrack.id) || drumBusInputRef.current || masterGainRef.current)
            : (drumBusInputRef.current || masterGainRef.current));
          source.start(playTime, sourceStartOffset, sourceTrimDuration);
          source.stop(envelope.sourceStop);
          const voice = { source, gainNode, startTime: playTime };
          source._cookupDrumVoice = voice;
          registerChokedVoice(drumName, drumIndex, playTime, voice);

          pushScheduledSource(source, [gainNode], clip.id, {
            startTime: playTime,
            eventKey,
            clipId: clip.id,
            kind: 'pattern'
          });
          scheduledPatternHitsRef.current.add(eventKey);

          // console.log(`    ✓ Scheduled ${drumName} at step ${step} (beat ${stepBeat.toFixed(2)}, time ${playTime.toFixed(2)}s)`);
        }
      }
    }
  };

  // Add clip to timeline
  const addClipToTimeline = (trackId, startBeat, durationBeats, clipType = 'audio', clipData = null, options = {}) => {
    const placementTrackId = trackId === 'pattern-track' && clipType === 'pattern' && clipData?.kind !== 'instrument'
      ? drumLaneTrackId(0)
      : trackId;
    const placement = resolveLaneClipPlacement(
      timelineClipsRef.current.filter((clip) => clip.trackId === placementTrackId),
      options.id || null,
      startBeat,
      durationBeats
    );
    if (placement.durationBeats <= 0) return null;
    if (options.history !== false) pushTimelineUndoSnapshot();
    const newClip = {
      id: options.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      trackId,
      startBeat: placement.startBeat,
      durationBeats: placement.durationBeats,
      clipType, // 'audio' or 'pattern'
      clipData, // For pattern clips, this contains the pattern data
    };

    setTimelineClipsSynced(prev => [...prev, newClip]);
    return expandGroupedDrumClip(newClip)[0];
  };

  // Remove clip from timeline
  const removeClipFromTimeline = (clipId) => {
    pushTimelineUndoSnapshot();
    setTimelineClipsSynced(prev => prev.filter(c => c.id !== clipId));
  };

  // Duplicate a clip at a new position
  const duplicateClip = async (clipId, newStartBeat) => {
    const clipToDuplicate = timelineClipsRef.current.find(c => c.id === clipId);
    if (!clipToDuplicate) {
      console.error('Clip not found:', clipId);
      return null;
    }
    const placement = resolveLaneClipPlacement(
      timelineClipsRef.current.filter((clip) => clip.trackId === clipToDuplicate.trackId),
      null,
      snapToGrid(Math.max(0, newStartBeat)),
      clipToDuplicate.durationBeats
    );
    if (placement.durationBeats <= 0) return null;
    pushTimelineUndoSnapshot();

    // Create new clip with same properties but new ID and position
    const newClip = {
      ...clipToDuplicate,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      startBeat: placement.startBeat,
      durationBeats: placement.durationBeats
    };

    setTimelineClipsSynced(prev => [...prev, newClip]);
    // console.log(`📋 Duplicated clip ${clipId} → ${newClip.id} at beat ${newClip.startBeat}`);

    // Return full clip object instead of just ID
    return newClip;
  };

  // Split clip at playhead position
  const splitClipAtPlayhead = (playheadBeat) => {
    return splitClipAtBeat(playheadBeat, null);
  };

  // Split a clip (or all clips) at a specific beat position.
  // targetClipId: if set, only that clip is split. Otherwise, all clips crossing the beat are split.
  // Resulting clips carry sourceClipId so Heal Separation can find siblings.
  const splitClipAtBeat = (splitBeat, targetClipId = null) => {
    // Filter using the authoritative (display-matching) start/length, so the split
    // point compares against where the clip is actually drawn.
    const clipsToSplit = timelineClipsRef.current.filter(clip => {
      if (targetClipId && clip.id !== targetClipId) return false;
      const { startBeat, durationBeats } = authoritativeClipBeats(clip);
      return splitBeat > startBeat && splitBeat < startBeat + durationBeats;
    });

    if (clipsToSplit.length === 0) return { newClips: [], removedClipIds: [] };
    pushTimelineUndoSnapshot();

    const newClips = [];
    const removedClipIds = [];

    setTimelineClipsSynced(prev => {
      let updated = [...prev];

      clipsToSplit.forEach(clip => {
        const { startBeat, durationBeats } = authoritativeClipBeats(clip);
        const clipEnd = startBeat + durationBeats;
        const leftDuration = splitBeat - startBeat;
        const rightDuration = clipEnd - splitBeat;
        const patternSplit = clip.clipType === 'pattern' && clip.clipData
          ? splitPatternClipData(
            clip.clipData,
            leftDuration * (Number(clip.clipData.ppq) || DEFAULT_PPQ),
            durationBeats * (Number(clip.clipData.ppq) || DEFAULT_PPQ),
            { drumPattern: clip.clipData.kind !== 'instrument' }
          )
          : null;
        // sourceClipId: the original clip id before splitting, so Heal can find pairs
        const sourceId = clip.sourceClipId || clip.id;
        const ts = Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

        // Rebase both halves onto beat fields and clear the parent's sample-domain
        // fields, so display and scheduler recompute a consistent position for each
        // half (carrying the parent's stale timelineStartSample would desync them).
        const leftClip = {
          ...clip,
          id: `${ts}-L`,
          startBeat,
          durationBeats: leftDuration,
          sourceClipId: sourceId,
          splitParentId: clip.id,
          ...(patternSplit ? { clipData: patternSplit.left } : {}),
          timelineStartSample: null,
          lengthSamples: null,
          sourceStartSample: null,
        };

        const rightClip = {
          ...clip,
          id: `${ts}-R`,
          startBeat: splitBeat,
          durationBeats: rightDuration,
          ...(clip.clipType === 'audio' ? { audioOffset: (clip.audioOffset || 0) + leftDuration } : {}),
          sourceClipId: sourceId,
          splitParentId: clip.id,
          ...(patternSplit ? { clipData: patternSplit.right } : {}),
          timelineStartSample: null,
          lengthSamples: null,
          sourceStartSample: null,
        };

        updated = updated.filter(c => c.id !== clip.id);
        updated.push(leftClip, rightClip);
        newClips.push(leftClip, rightClip);
        removedClipIds.push(clip.id);
      });

      return updated;
    });

    return { newClips, removedClipIds };
  };

  // Remove a time range across chosen tracks, including audio and patterns. Each intersecting clip
  // is replaced by its surviving left/right pieces in one history operation,
  // preserving the source offset of the right-hand piece.
  const deleteAudioRange = (startBeat, endBeat, trackId = null) => {
    const result = deleteAudioRangeFromClips(
      timelineClipsRef.current,
      startBeat,
      endBeat,
      trackId,
      {
        includePatterns: true,
        getBounds: authoritativeClipBeats,
        idFactory: (index) => `${Date.now().toString(36)}-${index}-${Math.random().toString(36).slice(2, 7)}`,
      }
    );
    if (!result.changed) return result;
    pushTimelineUndoSnapshot();
    setTimelineClipsSynced(result.clips);
    return result;
  };

  // Heal (rejoin) two adjacent clips that share the same sourceClipId.
  // leftClipId must end exactly where rightClipId begins (or within 0.001 beats).
  const healClips = (leftClipId, rightClipId) => {
    const clips = timelineClipsRef.current;
    const left = clips.find(c => c.id === leftClipId);
    const right = clips.find(c => c.id === rightClipId);

    if (!left || !right) return false;
    if (left.trackId !== right.trackId) return false;

    const gap = Math.abs((left.startBeat + left.durationBeats) - right.startBeat);
    if (gap > 0.01) return false; // Not adjacent

    pushTimelineUndoSnapshot();

    const healed = {
      ...left,
      id: left.sourceClipId || left.id,
      durationBeats: left.durationBeats + right.durationBeats,
      sourceClipId: undefined,
    };

    setTimelineClipsSynced(prev =>
      prev.filter(c => c.id !== leftClipId && c.id !== rightClipId).concat(healed)
    );

    return true;
  };

  // Trim clip from left edge (adjust start position and audio offset)
  /**
   * Trim the clip start. Accepts a sample position (authoritative) or a legacy
   * beat number. Writes a full position patch: updating only the beat fields
   * would leave the sample fields stale, and readers prefer samples, so the
   * trim would appear not to happen at all.
   */
  const trimClipLeft = (clipId, target) => {
    captureTimelineHistoryForGesture(`trim-left:${clipId}`);
    const map = sessionTempoMap();
    setTimelineClipsSynced(prev => prev.map((clip) => {
      if (clip.id !== clipId) return clip;
      const targetSample = typeof target === 'bigint'
        ? target
        : beatToSampleExact(snapToGrid(Math.max(0, Number(target) || 0)), map);
      return {
        ...clip,
        ...resolveTrimStart(clip, targetSample, map, {
          canExpandLeft: clip.clipType === 'audio'
        })
      };
    }));
  };

  /** Trim the clip end. Same contract as trimClipLeft. */
  const trimClipRight = (clipId, target) => {
    captureTimelineHistoryForGesture(`trim-right:${clipId}`);
    const map = sessionTempoMap();
    setTimelineClipsSynced(prev => prev.map((clip) => {
      if (clip.id !== clipId) return clip;
      const targetSample = typeof target === 'bigint'
        ? target
        : beatToSampleExact(snapToGrid(Math.max(0, Number(target) || 0)), map);
      const trimPatch = resolveTrimEnd(clip, targetSample, map);
      const placement = resolveLaneClipPlacement(
        timelineClipsRef.current.filter((candidate) => candidate.trackId === clip.trackId),
        clip.id,
        trimPatch.startBeat ?? clip.startBeat,
        trimPatch.durationBeats
      );
      const resolvedTrimPatch = clip.clipType === 'audio'
        ? positionPatch({
          startSample: beatToSampleExact(placement.startBeat, map),
          lengthSamples: getLengthSamples({ ...clip, ...placement, lengthSamples: null }, map)
        }, map)
        : { ...trimPatch, startBeat: placement.startBeat, durationBeats: placement.durationBeats };
      const nextClipData = clip.clipType === 'pattern'
        ? extendDrumPatternDataToDuration(clip.clipData, resolvedTrimPatch.durationBeats)
        : clip.clipData;
      return {
        ...clip,
        ...resolvedTrimPatch,
        ...(nextClipData !== clip.clipData ? { clipData: nextClipData } : {})
      };
    }));
  };

  // Save timeline to session
  const saveTimelineToSession = async (sessionId, options = {}) => {
    if (!sessionId) return;

    try {
      const {
        tracksOverride,
        timelineClipsOverride,
        keepalive = false,
        preventEmptyOverwrite = false,
        forceCanonical = false,
        allowClipClear = false
      } = options;

      if (!forceCanonical) {
        return { success: false, skipped: true, reason: 'manual_save_required' };
      }
      // Use refs to get latest values (not stale closures) - same pattern as DrumContext
      const tracksToSave = tracksOverride || tracksRef.current;
      const clipsToSave = timelineClipsOverride || timelineClipsRef.current;

      // Save track metadata (not the buffer, just the metadata needed to reload)
      const trackMetadata = tracksToSave.map(track => {
        const base = {
          ...persistedLoopFields(track),
          ...(track.type === 'audio' ? { type: 'audio' } : {}),
          id: track.id,
          name: track.name,
          volume: track.volume,
          pan: track.pan ?? 0,
          muted: track.muted,
          solo: track.solo,
          fx: track.fx || [],
          output: track.output || MASTER_OUTPUT,
          sends: normalizeSends(track.sends, busesRef.current),
          timelineOrder: Number.isFinite(track.timelineOrder) ? track.timelineOrder : null,
          ...(normalizeDuck(track.duck) ? { duck: normalizeDuck(track.duck) } : {}),
        };
        if (track.type === 'instrument') {
          return {
            ...base,
            type: 'instrument',
            instrumentId: track.instrumentId || 'cookup-native',
            instrumentPatch: track.instrumentPatch || null
          };
        }
        if (track.type === 'drum-lane') return { ...base, type: 'drum-lane', removed: Boolean(track.removed) };
      if (track.sourceType === 'empty') return { ...base, type: 'audio', sourceType: 'empty' };
        if (track.sourceType === 'stock') {
          return { ...base, sourceType: 'stock', assetId: track.assetId, assetVersion: track.assetVersion || 1 };
        }
        return { ...base, fileUrl: track.fileUrl, storageKey: track.storageKey || null };
      }).filter(track => track.fileUrl || track.sourceType === 'stock' || track.type === 'instrument' || track.type === 'drum-lane' || track.sourceType === 'empty');

      // Deduplicate by track id only (never by fileUrl/storageKey).
      // Multiple tracks can intentionally reference the same file.
      const uniqueTracks = trackMetadata.reduce((acc, track) => {
        const duplicate = acc.find(t => t.id === track.id);
        if (!duplicate) {
          acc.push(track);
        }
        return acc;
      }, []).filter(track => isTrackInSessionScope(sessionId, track));

      const validTrackIds = new Set(uniqueTracks.map(t => t.id));
      const isLikelyAudioClip = (clip) => {
        if (!clip) return false;
        if (clip.clipType === 'audio') return true;
        if (clip.clipType === 'pattern') return false;
        return Boolean(clip.trackId && clip.trackId !== 'pattern-track');
      };

      const audioClipCount = clipsToSave.filter((clip) => isLikelyAudioClip(clip)).length;

      const sanitizedClips = clipsToSave.filter((clip) => {
        if (!isLikelyAudioClip(clip)) return true;
        return !!clip.trackId && validTrackIds.has(clip.trackId);
      }).map(sanitizeClipForSession).filter(Boolean);
      const sanitizedAudioClipCount = sanitizedClips.filter((clip) => isLikelyAudioClip(clip)).length;
      const allowAudioPrune = options.allowAudioPrune === true;
      const safety = assessTimelineSaveSafety({
        preventEmptyOverwrite,
        allowClipClear,
        allowAudioPrune,
        sanitizedClipCount: sanitizedClips.length,
        sanitizedAudioClipCount,
        audioClipCount,
        trackCount: uniqueTracks.length,
        previousClipCount: lastPersistedTimelineCountsRef.current.clips
      });
      if (!safety.safe) {
        console.warn(`⚠️ Skipping non-destructive timeline save: ${safety.reason}`);
        return {
          success: false,
          skipped: true,
          failed: false,
          reason: safety.reason,
          previousClipCount: lastPersistedTimelineCountsRef.current.clips
        };
      }

      const token = sessionStorage.getItem('token');

      const localSnapshot = {
        timelineClips: sanitizedClips,
        tracks: uniqueTracks,
        automation: automationRef.current || {},
        buses: busesRef.current || []
      };
      const canonicalSnapshotFromSession = (canonical) => {
        const data = canonical?.sessionData && typeof canonical.sessionData === 'object'
          ? canonical.sessionData
          : {};
        return {
          timelineClips: (Array.isArray(data.timelineClips) ? data.timelineClips : [])
            .map(sanitizeClipForSession)
            .filter(Boolean),
          tracks: (Array.isArray(data.tracks) ? data.tracks : [])
            .filter((track) => track?.id)
            .map((track) => ({ ...track })),
          automation: data.automation && typeof data.automation === 'object' ? data.automation : {},
          buses: Array.isArray(data.buses) ? data.buses : []
        };
      };
      const buildRequestPayload = (expectedVersion, snapshot) => ({
        sessionData: {
          timelineClips: snapshot.timelineClips,
          automation: snapshot.automation,
          buses: snapshot.buses,
          ...(snapshot.tracks.length > 0 || options.allowTrackClear === true
            ? { tracks: snapshot.tracks }
            : {})
        },
        ...(Number.isInteger(expectedVersion) ? { expectedVersion } : {})
      });

      const initialPayloadSerialized = serializeSessionPayload(buildRequestPayload(undefined, localSnapshot));
      const initialPayloadBytes = getSerializedBytes(initialPayloadSerialized);
      if (initialPayloadBytes > SESSION_PATCH_SOFT_LIMIT_BYTES) {
        console.warn(`⚠️ Skipping timeline save: payload too large (${initialPayloadBytes} bytes)`);
        return {
          success: false,
          failed: true,
          payloadTooLarge: true,
          status: 413,
          bytes: initialPayloadBytes
        };
      }

      const sendPatch = (expectedVersion, snapshot) => fetch(`${API_URL}/api/sessions/${sessionId}`, {
        method: 'PATCH',
        keepalive,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: serializeSessionPayload(buildRequestPayload(expectedVersion, snapshot))
      });

      const initialExpectedVersion = Number.isInteger(options.expectedVersion)
        ? options.expectedVersion
        : getSessionVersion(sessionId);

      let expectedVersion = initialExpectedVersion;
      let pendingSnapshot = localSnapshot;
      let mergeBase = lastPersistedTimelineSnapshotRef.current.initialized
        ? lastPersistedTimelineSnapshotRef.current.snapshot
        : { timelineClips: [], tracks: [], automation: {}, buses: [] };
      let response = null;
      let responseData = null;
      let currentVersion = expectedVersion;
      let hadConflict = false;
      let didCanonicalMerge = false;

      try {
        // Always preflight against the canonical timeline. Session versions
        // are shared by BPM, drums, and timeline writes; another domain may
        // have advanced the version without proving that our local timeline
        // contains the latest remote clips.
        const canonical = await fetchCanonicalSessionSnapshot({ sessionCode: sessionId, token });
        const remoteSnapshot = canonicalSnapshotFromSession(canonical);
        const preflightSnapshot = mergeTimelinePersistenceSnapshots(
          mergeBase,
          pendingSnapshot,
          remoteSnapshot
        );
        didCanonicalMerge = !persistenceValueEqual(preflightSnapshot, pendingSnapshot);
        pendingSnapshot = preflightSnapshot;
        mergeBase = remoteSnapshot;
        expectedVersion = Number.isInteger(canonical?.version)
          ? canonical.version
          : expectedVersion;
        if (!Number.isInteger(expectedVersion)) {
          throw new Error('Canonical session did not provide a valid version');
        }
        currentVersion = expectedVersion;
        setSessionVersion(sessionId, expectedVersion);
      } catch (preflightError) {
        console.warn('⚠️ Timeline canonical preflight failed:', preflightError);
        return {
          success: false,
          conflict: false,
          retryFailed: false,
          failed: true,
          error: preflightError?.message || 'Timeline canonical preflight failed'
        };
      }

      for (let attempt = 0; attempt < 3; attempt += 1) {
        // eslint-disable-next-line no-await-in-loop
        response = await sendPatch(expectedVersion, pendingSnapshot);
        if (response.status !== 409) break;

        hadConflict = true;
        // eslint-disable-next-line no-await-in-loop
        const conflictData = await response.json().catch(() => null);
        currentVersion = conflictData?.currentVersion;
        try {
          // A version number alone is not a rebase. Fetch and three-way merge
          // the canonical timeline so simultaneous additions/deletions from
          // both clients survive before attempting the conditional write.
          // eslint-disable-next-line no-await-in-loop
          const canonical = await fetchCanonicalSessionSnapshot({ sessionCode: sessionId, token });
          const remoteSnapshot = canonicalSnapshotFromSession(canonical);
          const rebasedSnapshot = mergeTimelinePersistenceSnapshots(
            mergeBase,
            pendingSnapshot,
            remoteSnapshot
          );
          didCanonicalMerge = didCanonicalMerge
            || !persistenceValueEqual(rebasedSnapshot, pendingSnapshot);
          pendingSnapshot = rebasedSnapshot;
          mergeBase = remoteSnapshot;
          expectedVersion = Number.isInteger(canonical?.version)
            ? canonical.version
            : currentVersion;
          if (!Number.isInteger(expectedVersion)) {
            throw new Error('Canonical session did not provide a valid version');
          }
          setSessionVersion(sessionId, expectedVersion);
        } catch (rebaseError) {
          console.warn('⚠️ Timeline conflict rebase failed:', rebaseError);
          return {
            success: false,
            conflict: true,
            currentVersion,
            retryFailed: true,
            failed: true,
            status: response.status
          };
        }

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 15 + Math.floor(Math.random() * 20)));
      }

      if (response.ok) {
        responseData = await response.json().catch(() => null);
        const nextVersion = responseData?.session?.version;
        if (Number.isInteger(nextVersion)) {
          setSessionVersion(sessionId, nextVersion);
        }
        lastPersistedTimelineCountsRef.current = {
          clips: pendingSnapshot.timelineClips.length,
          tracks: pendingSnapshot.tracks.length
        };
        lastPersistedTimelineSnapshotRef.current = {
          initialized: true,
          snapshot: pendingSnapshot
        };
        if (hadConflict || didCanonicalMerge) {
          // Make the rebased canonical clips visible locally as well. Otherwise
          // a remote clip preserved in the server merge but absent from this
          // render could look like a local deletion on the next autosave.
          setTimelineClipsSynced((currentClips) => {
            const currentPersistedClips = currentClips
              .map(sanitizeClipForSession)
              .filter(Boolean);
            const mergedClips = mergeTimelinePersistenceSnapshots(
              { timelineClips: localSnapshot.timelineClips },
              { timelineClips: currentPersistedClips },
              { timelineClips: pendingSnapshot.timelineClips }
            ).timelineClips;
            const currentById = new Map(currentClips.map((clip) => [String(clip.id), clip]));
            return mergedClips.map((clip) => ({
              ...(currentById.get(String(clip.id)) || {}),
              ...clip
            }));
          });
        }
      } else {
        const errorData = await response.json().catch(() => null);
        return {
          success: false,
          conflict: hadConflict,
          currentVersion,
          retryFailed: hadConflict,
          failed: true,
          status: response.status,
          error: errorData?.error || `Timeline save failed (${response.status})`
        };
      }
      return responseData;
    } catch (error) {
      console.error('Failed to save timeline:', error);
      return {
        success: false,
        failed: true,
        error: error?.message || 'Timeline save failed'
      };
    }
  };

  const exportTimelineSnapshot = (sessionId = null) => {
    const tracksToSave = tracksRef.current || [];
    const clipsToSave = timelineClipsRef.current || [];

    const trackMetadata = tracksToSave.map(track => {
      const base = {
        ...persistedLoopFields(track),
        ...(track.type === 'audio' ? { type: 'audio' } : {}),
        id: track.id, name: track.name, volume: track.volume, pan: track.pan ?? 0,
        muted: track.muted, solo: track.solo, fx: track.fx || [],
        output: track.output || MASTER_OUTPUT, sends: normalizeSends(track.sends, busesRef.current),
        ...(normalizeDuck(track.duck) ? { duck: normalizeDuck(track.duck) } : {})
      };
      if (track.type === 'instrument') {
        return {
          ...base,
          type: 'instrument',
          instrumentId: track.instrumentId || 'cookup-native',
          instrumentPatch: track.instrumentPatch || null
        };
      }
      if (track.type === 'drum-lane') return { ...base, type: 'drum-lane', removed: Boolean(track.removed) };
      if (track.sourceType === 'empty') return { ...base, type: 'audio', sourceType: 'empty' };
      if (track.sourceType === 'stock') {
        return { ...base, sourceType: 'stock', assetId: track.assetId, assetVersion: track.assetVersion || 1 };
      }
      return { ...base, fileUrl: track.fileUrl, storageKey: track.storageKey || null };
    }).filter(track => track.fileUrl || track.sourceType === 'stock' || track.type === 'instrument' || track.type === 'drum-lane' || track.sourceType === 'empty');

    const uniqueTracks = trackMetadata.reduce((acc, track) => {
      const duplicate = acc.find(t => t.id === track.id);
      if (!duplicate) acc.push(track);
      return acc;
    }, []).filter(track => isTrackInSessionScope(sessionId, track));

    return {
      timelineClips: clipsToSave,
      tracks: uniqueTracks
    };
  };

  // Load timeline from session
  // Tempo map used for clip position migration/conversion. Built on demand so
  // it always reflects the session's current tempo and sample rate.
  // The map is immutable and BigInt-backed, so rebuilding it per call made the
  // lookahead scheduler allocate on every tick for every clip. Rebuild only
  // when the inputs change.
  const sessionTempoMapCacheRef = useRef({ sampleRate: 0, bpm: 0, map: null });
  const sessionTempoMap = () => {
    const sampleRate = audioContextRef.current?.sampleRate || 48000;
    const bpm = bpmRef.current || 120;
    const cache = sessionTempoMapCacheRef.current;
    if (!cache.map || cache.sampleRate !== sampleRate || cache.bpm !== bpm) {
      cache.map = createTempoMap({ sampleRate, ppq: DEFAULT_PPQ, events: [{ tick: 0, bpm }] });
      cache.sampleRate = sampleRate;
      cache.bpm = bpm;
    }
    return cache.map;
  };

  const loadTimelineFromSession = async (sessionData, sessionId = null, options = {}) => {
    const loadGeneration = sessionLoadGenerationRef.current + 1;
    sessionLoadGenerationRef.current = loadGeneration;
    const incomingPersistenceSnapshot = {
      timelineClips: (Array.isArray(sessionData?.timelineClips) ? sessionData.timelineClips : [])
        .map(sanitizeClipForSession)
        .filter(Boolean),
      tracks: (Array.isArray(sessionData?.tracks) ? sessionData.tracks : [])
        .filter((track) => track?.id)
        .map((track) => ({ ...track })),
      automation: sessionData?.automation && typeof sessionData.automation === 'object'
        ? sessionData.automation
        : {},
      buses: Array.isArray(sessionData?.buses) ? sessionData.buses : []
    };
    lastPersistedTimelineSnapshotRef.current = {
      initialized: true,
      snapshot: incomingPersistenceSnapshot
    };
    lastPersistedTimelineCountsRef.current = {
      clips: incomingPersistenceSnapshot.timelineClips.length,
      tracks: incomingPersistenceSnapshot.tracks.length
    };

    // Replace current timeline state with the snapshot being loaded.
    // This prevents old session data from bleeding into a newly opened session.
    stop();
    setTracksSynced([]);
    setTimelineClipsSynced([]);
    clearTimelineHistory();
    pendingLoads.current = [];
    queuedTrackWarnedRef.current.clear();
    missingBufferWarnedRef.current.clear();
    audioBuffers.current.clear();
    trackNodesRef.current.forEach((nodes) => {
      try {
        nodes?.audibilityGain?.disconnect();
        nodes?.userGain?.disconnect();
        nodes?.panNode?.disconnect();
        nodes?.outputGain?.disconnect();
        nodes?.fxInputGain?.disconnect();
        nodes?.fxOutputGain?.disconnect();
        nodes?.splitter?.disconnect();
        nodes?.analyserL?.disconnect();
        nodes?.analyserR?.disconnect();
        nodes?.meterMerger?.disconnect();
      } catch (_) {
        // Best effort cleanup
      }
    });
    trackNodesRef.current.clear();

    // Restore mix buses before tracks, so send/output routing resolves on load.
    drumLanePoolsRef.current.forEach((pool) => pool.destroy());
    drumLanePoolsRef.current.clear();
    drumLanePoolsPendingRef.current.clear();
    {
      const restoredBuses = normalizeBuses(sessionData?.buses);
      busesRef.current = restoredBuses;
      setBusesState(restoredBuses);
    }

    // Restore automation lanes (normalize every lane; drop unknown params)
    {
      const restored = {};
      Object.entries(sessionData?.automation || {}).forEach(([trackId, lanes]) => {
        Object.entries(lanes || {}).forEach(([paramId, lane]) => {
          if (!AUTOMATION_PARAMS[paramId]) return;
          const points = normalizeAutomationPoints(paramId, lane?.points);
          if (points.length === 0) return;
          restored[trackId] = restored[trackId] || {};
          restored[trackId][paramId] = { points };
        });
      });
      automationRef.current = restored;
      setAutomationState(restored);
    }

    // Load tracks from session (download audio from fileUrls)
    if (sessionData?.tracks && Array.isArray(sessionData.tracks)) {
      // Deduplicate tracks by id only
      const uniqueTracks = sessionData.tracks.reduce((acc, track) => {
        if (!acc.find(t => t.id === track.id)) {
          acc.push(track);
        }
        return acc;
      }, []).filter(track => isTrackInSessionScope(sessionId, track));

      // console.log(`📥 Loading ${uniqueTracks.length} unique tracks from session (${sessionData.tracks.length} total)`);

      const loadableTracks = uniqueTracks.filter((trackMeta) =>
        trackMeta?.id && (trackMeta?.fileUrl || trackMeta?.sourceType === 'stock')
      );

      // Native instruments do not download audio. Restore their channel-strip
      // records immediately; InstrumentContext rebuilds each synth graph from
      // the persisted patch and connects it to this track's input node.
      uniqueTracks.filter((trackMeta) => trackMeta?.id && trackMeta?.type === 'instrument').forEach((trackMeta) => {
        const instrumentTrack = {
          id: trackMeta.id,
          name: trackMeta.name || 'CookUp Instrument',
          type: 'instrument',
          instrumentId: trackMeta.instrumentId || 'cookup-native',
          instrumentPatch: trackMeta.instrumentPatch || null,
          timelineOrder: Number.isFinite(trackMeta.timelineOrder) ? trackMeta.timelineOrder : null,
          volume: trackMeta.volume ?? 1,
          pan: trackMeta.pan ?? 0,
          muted: trackMeta.muted ?? false,
          solo: trackMeta.solo ?? false,
          fx: trackMeta.fx || [],
          output: trackMeta.output || MASTER_OUTPUT,
          sends: normalizeSends(trackMeta.sends, busesRef.current),
          ...(normalizeDuck(trackMeta.duck) ? { duck: normalizeDuck(trackMeta.duck) } : {})
        };
        setTracksSynced((previous) => previous.some((track) => track.id === instrumentTrack.id)
          ? previous
          : [...previous, instrumentTrack]);
        createTrackNodes(instrumentTrack.id, instrumentTrack);
      });

      // Drum lanes are mixer channels, not downloadable audio files. Keep their
      // persisted fader, pan and insert chain when opening an existing session.
      uniqueTracks.filter(trackMeta => trackMeta?.id && trackMeta.sourceType === 'empty').forEach(trackMeta => {
        setTracksSynced(previous => previous.some(track => track.id === trackMeta.id)
          ? previous : [...previous, {...trackMeta, type:'audio', volume:trackMeta.volume ?? 1, pan:trackMeta.pan ?? 0, fx:trackMeta.fx || []}]);
      });
      uniqueTracks.filter((trackMeta) => trackMeta?.id && trackMeta?.type === 'drum-lane').forEach((trackMeta) => {
        const laneTrack = {
          ...trackMeta,
          volume: trackMeta.volume ?? 1,
          pan: trackMeta.pan ?? 0,
          muted: trackMeta.muted ?? false,
          solo: trackMeta.solo ?? false,
          fx: trackMeta.fx || [],
          output: trackMeta.output || MASTER_OUTPUT,
          sends: normalizeSends(trackMeta.sends, busesRef.current)
        };
        setTracksSynced((previous) => previous.some((track) => track.id === laneTrack.id)
          ? previous : [...previous, laneTrack]);
        createTrackNodes(laneTrack.id, laneTrack);
      });

      // Warm the AssetResolver cache for all stock tracks before individual
      // loads begin — one batched round-trip instead of N sequential ones.
      const stockRefs = loadableTracks
        .filter(t => t.sourceType === 'stock' && t.assetId)
        .map(t => ({ assetId: t.assetId, assetVersion: t.assetVersion || 1 }));
      if (stockRefs.length > 0) {
        const token = sessionStorage.getItem('token');
        await assetResolver.warmBatch(stockRefs, token);
      }
      let loadedTrackCount = 0;
      // Partial progress for tracks still downloading/decoding. Without this a
      // single-track session sits at a frozen percentage for the whole load.
      const inFlightProgress = new Map();
      const reportTrackProgress = () => {
        if (typeof options.onTrackProgress === 'function') {
          let partial = 0;
          inFlightProgress.forEach((fraction) => { partial += fraction; });
          options.onTrackProgress({
            loaded: Math.min(loadableTracks.length, loadedTrackCount + partial),
            total: loadableTracks.length
          });
        }
      };
      reportTrackProgress();

      const loadTrackMeta = async (trackMeta) => {
        if (loadGeneration !== sessionLoadGenerationRef.current) {
          return;
        }
        if (!trackMeta.id) {
          return;
        }
        if (!trackMeta.fileUrl && trackMeta.sourceType !== 'stock') {
          return;
        }

        try {
          // Resolve the playable URL — stock tracks go through AssetResolver,
          // user/session tracks use their persisted fileUrl.
          let resolvedUrl = trackMeta.fileUrl || null;
          if (trackMeta.sourceType === 'stock') {
            const token = sessionStorage.getItem('token');
            resolvedUrl = await assetResolver.resolve(trackMeta, token);
            if (!resolvedUrl) {
              console.error(`[AudioContext] Could not resolve stock asset ${trackMeta.assetId}`);
              return;
            }
          }

          // Download and load audio file
          const loadedAudio = await loadAudioFromUrl(trackMeta.id, trackMeta.name, resolvedUrl, {
            storageKey: trackMeta.sourceType === 'stock' ? null : (trackMeta.storageKey || null),
            addToTimeline: false,
            expectedLoadGeneration: loadGeneration,
            // Pass stock-asset identity so the stored track retains sourceType:'stock'
            // and isTrackInSessionScope can short-circuit without a CDN URL check.
            sourceType: trackMeta.sourceType || null,
            assetId: trackMeta.assetId || null,
            assetVersion: trackMeta.assetVersion ?? null,
            loopSource: trackMeta.loopSource || null,
            audioWaveform: trackMeta.audioWaveform || null,
            onStage: (fraction) => {
              inFlightProgress.set(trackMeta.id, Math.max(0, Math.min(1, fraction)));
              reportTrackProgress();
            }
          });
          if (!loadedAudio?.track || !audioBuffers.current.has(trackMeta.id)) {
            throw new Error(`Could not load ${trackMeta.name || 'recorded audio'}. Refresh and retry.`);
          }

          // Restore track settings (volume, muted, solo) using updateTrack
          // This will also update the persistent audio nodes
          updateTrack(trackMeta.id, {
            timelineOrder: Number.isFinite(trackMeta.timelineOrder) ? trackMeta.timelineOrder : null,
            volume: trackMeta.volume ?? 1.0,
            pan: trackMeta.pan ?? 0,
            muted: trackMeta.muted ?? false,
            solo: trackMeta.solo ?? false,
            fx: trackMeta.fx || [],
            // Buses are restored before tracks, so routing resolves here.
            output: trackMeta.output || MASTER_OUTPUT,
            sends: normalizeSends(trackMeta.sends, busesRef.current),
            duck: normalizeDuck(trackMeta.duck)
          });
        } catch (error) {
          console.error(`❌ Failed to load track ${trackMeta.name}:`, error);

          // 🔴 CRITICAL: Still add track to state even if load fails
          // This prevents data loss - user can see the track and retry
          const errorTrack = {
            id: trackMeta.id,
            name: trackMeta.name,
            fileUrl: trackMeta.sourceType === 'stock' ? null : trackMeta.fileUrl,
            storageKey: trackMeta.sourceType === 'stock' ? null : (trackMeta.storageKey || null),
            sourceType: trackMeta.sourceType || null,
            assetId: trackMeta.assetId || null,
            assetVersion: trackMeta.assetVersion || null,
            volume: trackMeta.volume ?? 1.0,
            muted: true, // Mute by default since no buffer
            solo: false,
            buffer: null, // No buffer available
            error: true, // Mark as errored
            errorMessage: error.message || 'Failed to load audio'
          };

          setTracksSynced(prev => {
            if (loadGeneration !== sessionLoadGenerationRef.current) {
              return prev;
            }
            // Only add if not already present
            if (!prev.find(t => t.id === trackMeta.id)) return [...prev, errorTrack];
            return prev.map(track => track.id === trackMeta.id ? { ...track, ...errorTrack } : track);
          });

          console.warn(`⚠️ Track "${trackMeta.name}" added with error state - data preserved`);
        } finally {
          inFlightProgress.delete(trackMeta.id);
          loadedTrackCount += 1;
          reportTrackProgress();
        }
      };

      // Load tracks in small concurrent batches (much faster than strict serial loading).
      // Keep concurrency conservative to avoid decode spikes on low-end devices.
      const MAX_CONCURRENT_TRACK_LOADS = 4;
      for (let i = 0; i < uniqueTracks.length; i += MAX_CONCURRENT_TRACK_LOADS) {
        if (loadGeneration !== sessionLoadGenerationRef.current) {
          return;
        }
        const batch = uniqueTracks.slice(i, i + MAX_CONCURRENT_TRACK_LOADS);
        await Promise.all(batch.filter((track) => track.type !== 'drum-lane' && track.type !== 'instrument').map(loadTrackMeta));
      }

      if (sessionData?.timelineClips) {
        if (loadGeneration !== sessionLoadGenerationRef.current) {
          return;
        }
        const validTrackIds = new Set(uniqueTracks.map(t => t.id));
        const sanitizedClips = migrateClipsToSamples(
          sessionData.timelineClips.filter((clip) => {
            if (clip.clipType !== 'audio') return true;
            return !!clip.trackId && validTrackIds.has(clip.trackId);
          }),
          sessionTempoMap()
        );
        setTimelineClipsSynced(sanitizedClips);
        lastPersistedTimelineCountsRef.current = {
          clips: sanitizedClips.length,
          tracks: uniqueTracks.length
        };
      }
    } else if (sessionData?.timelineClips) {
      if (loadGeneration !== sessionLoadGenerationRef.current) {
        return;
      }
      // No tracks available: keep only non-audio clips to avoid orphan playback warnings
      const nonAudioClips = migrateClipsToSamples(
        sessionData.timelineClips.filter((clip) => clip.clipType !== 'audio'),
        sessionTempoMap()
      );
      setTimelineClipsSynced(nonAudioClips);
      lastPersistedTimelineCountsRef.current = {
        clips: nonAudioClips.length,
        tracks: 0
      };
    }
  };

  const repairSessionData = async (sessionId, sessionData) => {
    if (!sessionId || !sessionData) return sessionData;

    let didRepair = false;
    let repairedTracks = Array.isArray(sessionData.tracks) ? [...sessionData.tracks] : [];
    const clips = Array.isArray(sessionData.timelineClips) ? sessionData.timelineClips : [];
    repairedTracks = repairedTracks.filter(track => isTrackInSessionScope(sessionId, track));

    // Add missing storageKey for tracks if derivable
    repairedTracks = repairedTracks.map(track => {
      if (!track.storageKey && track.fileUrl) {
        const derivedKey = extractStorageKeyFromUrl(track.fileUrl);
        if (derivedKey) {
          didRepair = true;
          return { ...track, storageKey: derivedKey };
        }
      }
      return track;
    });

    // Integrity check: ensure all clip trackIds exist in tracks
    const trackIdSet = new Set(repairedTracks.map(t => t.id));
    const missingTrackIds = [...new Set(clips
      .filter(c => c.clipType === 'audio')
      .map(c => c.trackId)
      .filter(id => id && !trackIdSet.has(id)))];

    if (missingTrackIds.length > 0) {
      console.warn(`⚠️ Missing track metadata for ${missingTrackIds.length} clip(s). Attempting recovery...`);
      try {
        const token = sessionStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/sessions/${sessionId}/samples`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          const samples = data.samples || [];

          missingTrackIds.forEach(trackId => {
            const matchByFilename = samples.find(s => s.filename === trackId);
            if (matchByFilename) {
              const derivedKey = matchByFilename.storageKey || extractStorageKeyFromUrl(matchByFilename.url);
              repairedTracks.push({
                id: trackId,
                name: matchByFilename.filename,
                fileUrl: matchByFilename.url,
                storageKey: derivedKey || null,
                volume: 1.0,
                muted: false,
                solo: false
              });
              trackIdSet.add(trackId);
              didRepair = true;
            } else {
              console.warn(`⚠️ Could not recover track ${trackId}. Orphan audio clips will be removed from loaded snapshot.`);
            }
          });
        }
      } catch (error) {
        console.warn('⚠️ Failed to fetch session samples for recovery:', error);
      }
    }

    if (didRepair) {
      console.log('🛠️ Repaired session data (tracks/storage keys) - will save after load');
      // DON'T save here - tracks haven't been loaded into state yet!
      // Saving now would overwrite with empty tracks/clips
      // The repair only updates the returned data structure
    }

    const repairedTrackIds = new Set(repairedTracks.map(t => t.id));
    const sanitizedClips = clips.filter((clip) => {
      if (clip.clipType !== 'audio') return true;
      return !!clip.trackId && repairedTrackIds.has(clip.trackId);
    });

    return {
      ...sessionData,
      tracks: repairedTracks,
      timelineClips: sanitizedClips
    };
  };

  // Snap position to grid
  // Snap resolution follows the visible grid ('auto') unless an explicit
  // division is set. At deep zoom the grid refines to 1/512, and 'off' gives
  // completely free positioning for sample-level nudges.
  const snapToGrid = (beatPosition) => {
    if (!snapEnabled) return Math.max(0, beatPosition);
    const resolution = snapResolution(pixelsPerBeat, snapValue);
    return snapBeat(beatPosition, resolution);
  };

  // Exponential zoom across the full range (arrangement overview → samples).
  const zoomIn = () => setPixelsPerBeat(prev => scaleZoom(prev, ZOOM_STEP));
  const zoomOut = () => setPixelsPerBeat(prev => scaleZoom(prev, 1 / ZOOM_STEP));
  const setZoom = (zoom) => setPixelsPerBeat(clampZoom(zoom));

  // Update clip position (for dragging)
  /**
   * Move a clip. Accepts either a legacy beat number or a sample-authoritative
   * position patch from the timeline drag gesture. Patches already carry their
   * snapped position and derived beats, so they are applied as-is; bare beat
   * numbers keep the old snap-here behaviour for other call sites.
   */
  const updateClipPosition = (clipId, positionOrBeat) => {
    const isPatch = positionOrBeat && typeof positionOrBeat === 'object';
    const patch = isPatch
      ? positionOrBeat
      : { startBeat: snapToGrid(Math.max(0, Number(positionOrBeat) || 0)) };
    const nextBeat = Number(patch.startBeat) || 0;

    const existingClip = timelineClipsRef.current.find((clip) => clip.id === clipId);
    if (!existingClip) return nextBeat;
    const map = sessionTempoMap();
    const requestedDuration = Number.isFinite(Number(patch.durationBeats))
      ? Math.max(0, Number(patch.durationBeats))
      : existingClip.durationBeats;
    const placement = resolveLaneClipPlacement(
      timelineClipsRef.current.filter((clip) => clip.trackId === existingClip.trackId),
      clipId,
      nextBeat,
      requestedDuration
    );
    const resolvedPatch = existingClip.clipType === 'audio'
      ? positionPatch({
        startSample: beatToSampleExact(placement.startBeat, map),
        lengthSamples: getLengthSamples({ ...existingClip, ...placement, lengthSamples: null }, map)
      }, map)
      : {
        ...patch,
        startBeat: placement.startBeat,
        durationBeats: placement.durationBeats
      };
    if (Object.keys(resolvedPatch).every((key) => existingClip[key] === resolvedPatch[key])) {
      return nextBeat;
    }
    if (Math.abs((existingClip.startBeat || 0) - placement.startBeat) > 0.0001
      || Math.abs((existingClip.durationBeats || 0) - placement.durationBeats) > 0.0001) {
      captureTimelineHistoryForGesture(`move:${clipId}`);
    }

    setTimelineClipsSynced(prev =>
      prev.map(clip => (clip.id === clipId ? { ...clip, ...resolvedPatch } : clip))
    );

    return nextBeat;
  };

  const updatePatternClip = (clipId, clipDataOrUpdater, options = {}) => {
    // Do not stop this clip's scheduled sources here. While playback is live,
    // the timelineClips audition effect preserves voices that are already
    // sounding (plus the tiny render guard) and replaces only future work.
    // The old eager cancellation cut off the current lane every time a user
    // painted a step, before the click-free invalidation pass could protect it.
    let updatedClip = null;
    setTimelineClipsSynced(prev =>
      prev.map(clip => {
        if (clip.id !== clipId || clip.clipType !== 'pattern') return clip;
        const nextClipData = typeof clipDataOrUpdater === 'function'
          ? clipDataOrUpdater(clip.clipData || {})
          : clipDataOrUpdater;
        if (!nextClipData) return clip;
        updatedClip = extendPatternClipToData(clip, nextClipData);
        return updatedClip;
      })
    );
    // Mark the resulting array so neither audition effect treats a captured
    // note as an edit worth dropping the schedule for.
    if (options.fromRecording) recordingWriteClipsRef.current = timelineClipsRef.current;
    return updatedClip;
  };

  const updateAudioClip = (clipId, patch = {}) => {
    if (!clipId || !patch || typeof patch !== 'object') return null;
    captureTimelineHistoryForGesture(`audio-edit:${clipId}`);
    const normalizedPatch = { ...patch };
    if (Object.prototype.hasOwnProperty.call(normalizedPatch, 'reverse')) {
      normalizedPatch.isReversed = Boolean(normalizedPatch.reverse);
    }
    let updatedClip = null;
    setTimelineClipsSynced(prev => prev.map((clip) => {
      if (clip.id !== clipId || clip.clipType !== 'audio') return clip;
      updatedClip = { ...clip, ...normalizedPatch };
      return updatedClip;
    }));
    return updatedClip;
  };

  // Create persistent audio nodes for a track
  // Routing: clipSource → audibilityGain (mute/solo) → fxInputGain → [FX chain] → fxOutputGain → userGain (fader) → panNode → analyser (post-fader meter) → master
  const createTrackNodes = (trackId, track, options = {}) => {
    if (!audioContext) return null;

    // During a provider refresh the new master node is installed immediately,
    // while React can still expose the previous AudioContext for one render.
    // Never construct a mixed-context strip in that transition; the state
    // update that publishes the new context will call this again safely.
    if (!masterGainRef.current || masterGainRef.current.context !== audioContext) return null;

    // Reuse only nodes owned by the current AudioContext. Context recreation
    // (session reload, device change, or development refresh) otherwise leaves
    // a stale channel strip that throws InvalidAccessError on connect and can
    // take down the entire instrument path.
    const existingNodes = trackNodesRef.current.get(trackId);
    if (
      !options.forceRecreate
      && existingNodes?.audibilityGain?.context === audioContext
      && existingNodes?.outputGain?._routedTo
    ) {
      return existingNodes;
    }
    if (existingNodes) {
      Object.values(existingNodes).forEach((node) => {
        try { node?.disconnect?.(); } catch (_) { /* stale node */ }
      });
      trackNodesRef.current.delete(trackId);
      trackAnalysersRef.current.delete(trackId);
    }

    // Create node chain
    const audibilityGain = audioContext.createGain(); // For mute/solo (0 or 1)
    const userGain = audioContext.createGain(); // For volume slider
    const panNode = audioContext.createStereoPanner(); // For pan

    // Set initial values
    audibilityGain.gain.value = (track.muted || hasSoloedTrack(track)) ? 0 : 1;
    userGain.gain.value = track.volume ?? 1;
    panNode.pan.value = track.pan ?? 0;

    // FX insert points — FxContext will disconnect fxInputGain and wire effects between them
    const fxInputGain = audioContext.createGain();
    const fxOutputGain = audioContext.createGain();

    // Stereo metering: split L/R after pan for separate L/R meter bars
    const splitter = audioContext.createChannelSplitter(2);
    const analyserL = audioContext.createAnalyser();
    analyserL.fftSize = 2048;
    analyserL.smoothingTimeConstant = 0;
    const analyserR = audioContext.createAnalyser();
    analyserR.fftSize = 2048;
    analyserR.smoothingTimeConstant = 0;
    const meterMerger = audioContext.createChannelMerger(2);

    // Connect: audibilityGain → fxInput → [FX chain] → fxOutput → duckGain → userGain → pan → outputGain → bus/master
    // duckGain is the kick-triggered ducking point: post-insert, pre-fader.
    // FX inserts are pre-fader (industry standard channel strip order).
    // Metering is post-fader so the meter reflects what actually hits the bus.
    // outputGain isolates routing changes: re-routing only touches that node, so
    // the metering tap and any post-fader sends on panNode stay connected.
    const outputGain = audioContext.createGain();
    const duckGain = audioContext.createGain();
    audibilityGain.connect(fxInputGain);
    fxInputGain.connect(fxOutputGain); // direct passthrough until FxContext inserts effects
    fxOutputGain.connect(duckGain);
    duckGain.connect(userGain);
    userGain.connect(panNode);
    // Keep the analyser in the audible path. A disconnected metering branch
    // can be pruned by the browser, leaving an active drum strip at zero.
    if (metersEnabled()) {
      panNode.connect(splitter);
      splitter.connect(analyserL, 0);
      splitter.connect(analyserR, 1);
      analyserL.connect(meterMerger, 0, 0);
      analyserR.connect(meterMerger, 0, 1);
      meterMerger.connect(outputGain);
    } else {
      // Nothing displays these levels in the chat panel; keep four nodes per strip out of the mix.
      panNode.connect(outputGain);
    }
    outputGain.connect(masterGainRef.current);
    outputGain._routedTo = masterGainRef.current;

    const nodes = { audibilityGain, userGain, panNode, outputGain, fxInputGain, fxOutputGain, duckGain, splitter, analyserL, analyserR, meterMerger };
    trackNodesRef.current.set(trackId, nodes);
    trackAnalysersRef.current.set(trackId, { analyserL, analyserR });

    return nodes;
  };

  const ensureDrumLaneTracks = (rows = [], initialVolumes = {}) => {
    const occupiedLaneIds = new Set((timelineClipsRef.current || [])
      .filter((clip) => clip.clipType === 'pattern' && clip.clipData?.kind !== 'instrument'
        && /^drum-lane-\d+$/.test(String(clip.trackId)))
      .map((clip) => clip.trackId));
    const revived = tracksRef.current.filter((track) => track.type === 'drum-lane'
      && track.removed && occupiedLaneIds.has(track.id));
    const missing = rows.flatMap((row, index) => {
      const id = drumLaneTrackId(index);
      if (tracksRef.current.some((track) => track.id === id)) return [];
      return [{
        id, name: row?.name || `Drum ${index + 1}`, type: 'drum-lane',
        volume: initialVolumes[row?.key] ?? 1, pan: 0, muted: false, solo: false, fx: [],
        output: MASTER_OUTPUT, sends: {}
      }];
    });
    if (!missing.length && !revived.length) return;
    setTracksSynced((previous) => [
      ...previous.map((track) => revived.some((lane) => lane.id === track.id)
        ? { ...track, removed: false, muted: false } : track),
      ...missing.filter((lane) => !previous.some((track) => track.id === lane.id))
    ]);
    revived.forEach((lane) => createTrackNodes(lane.id,
      tracksRef.current.find((track) => track.id === lane.id)));
    missing.forEach((lane) => createTrackNodes(lane.id, lane));
  };

  // Drum mixer records live beside ordinary tracks for the existing FX and
  // session-save machinery, but they do not create duplicate arrangement rows.
  useEffect(() => {
    const rows = collectDrumLaneSounds(timelineClips);
    const initialVolumes = Object.assign({}, ...timelineClips.filter(clip => clip.clipType === 'pattern'
      && clip.clipData?.kind !== 'instrument').map(clip => clip.clipData?.drumVolumes || {}));
    if (rows.length) ensureDrumLaneTracks(rows, initialVolumes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineClips, audioContext]);

  const audibleDrumLaneIds = useMemo(() => new Set(timelineClips
    .filter((clip) => clip.clipType === 'pattern' && clip.clipData?.kind !== 'instrument'
      && ((clip.clipData?.noteEvents?.length || 0) > 0
        || Object.values(clip.clipData?.pattern || {}).some(Boolean)))
    .map((clip) => clip.trackId)), [timelineClips]);

  useEffect(() => {
    if (!audioContext || !rustSamplerEnabled()) return;
    const activeLaneIds = new Set(tracks.filter((track) => track.type === 'drum-lane'
      && audibleDrumLaneIds.has(track.id)).map((track) => track.id));
    drumLanePoolsRef.current.forEach((pool, trackId) => {
      if (activeLaneIds.has(trackId)) return;
      pool.destroy();
      drumLanePoolsRef.current.delete(trackId);
    });
    tracks.filter((track) => activeLaneIds.has(track.id)).forEach((track) => {
      if (drumLanePoolsRef.current.has(track.id) || drumLanePoolsPendingRef.current.has(track.id)) return;
      const input = createTrackNodes(track.id, track)?.audibilityGain;
      if (!input) return;
      drumLanePoolsPendingRef.current.set(track.id, input);
      void createSamplePlaybackPool(audioContext, input, 16).then((pool) => {
        if (drumLanePoolsPendingRef.current.get(track.id) === input) {
          drumLanePoolsPendingRef.current.delete(track.id);
        }
        if (!pool) return;
        if (audioContextRef.current !== audioContext
          || trackNodesRef.current.get(track.id)?.audibilityGain !== input
          || !tracksRef.current.some((candidate) => candidate.id === track.id)) {
          pool.destroy();
          return;
        }
        drumLanePoolsRef.current.set(track.id, pool);
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, audioContext, audibleDrumLaneIds]);

  const addInstrumentTrack = (options = {}) => {
    const id = options.id || `instrument-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
    const existing = tracksRef.current.find((track) => track.id === id);
    if (existing) {
      const nodes = createTrackNodes(id, existing);
      return { track: existing, input: nodes?.audibilityGain || null };
    }
    const instrumentNumber = tracksRef.current.filter((track) => track.type === 'instrument').length + 1;
    const track = {
      id,
      name: options.name || `CookUp Instrument ${instrumentNumber}`,
      type: 'instrument',
      instrumentId: 'cookup-native',
      instrumentPatch: options.patch || null,
      volume: Number.isFinite(options.volume) ? options.volume : 1,
      pan: Number.isFinite(options.pan) ? options.pan : 0,
      muted: false,
      solo: false,
      fx: [],
      output: MASTER_OUTPUT,
      sends: {}
    };
    setTracksSynced((previous) => [...previous, track]);
    const nodes = createTrackNodes(id, track);
    return { track, input: nodes?.audibilityGain || null };
  };

  const addEmptyAudioTrack = () => {
    const id = `audio-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,6)}`;
    const number = tracksRef.current.filter(track => track.type === 'audio').length + 1;
    const track = {id,name:`Audio ${number}`,type:'audio',sourceType:'empty',volume:1,pan:0,muted:false,solo:false,fx:[],output:MASTER_OUTPUT,sends:{}};
    setTracksSynced(previous => [...previous,track]);
    createTrackNodes(id,track);
    return track;
  };

  const addEmptyDrumTrack = () => {
    const indices = tracksRef.current.map(track => /^drum-lane-(\d+)$/.exec(track.id)).filter(Boolean).map(match => Number(match[1]));
    const rows = collectDrumLaneSounds(timelineClipsRef.current);
    const index = Math.max(rows.length, indices.length ? Math.max(...indices)+1 : 0);
    const id = drumLaneTrackId(index);
    const track = {id,name:`Drum ${index+1}`,type:'drum-lane',volume:1,pan:0,muted:false,solo:false,fx:[],output:MASTER_OUTPUT,sends:{}};
    setTracksSynced(previous => [...previous,track]);
    createTrackNodes(id,track);
    return track;
  };

  // Arrangement order is presentation metadata. Reordering must never tear
  // down audio nodes or change clip ownership while playback is running.
  const reorderTimelineTracks = (orderedIds) => {
    if (!Array.isArray(orderedIds)) return;
    const rank = new Map(orderedIds.map((id, index) => [id, index]));
    setTracksSynced(previous => previous.map(track => rank.has(track.id)
      ? { ...track, timelineOrder: rank.get(track.id) }
      : track));
  };

  const getTrackInput = (trackId, options = {}) => {
    const track = tracksRef.current.find((entry) => entry.id === trackId);
    if (!track) return null;
    return createTrackNodes(trackId, track, options)?.audibilityGain || null;
  };

  // Recompute every persistent track's mute/solo gain as one coherent set.
  // Updating only the track whose Solo button changed leaves the other tracks at
  // zero after the last solo is released, even though their UI says unmuted.
  useEffect(() => {
    if (!audioContext) return;
    const hasSolo = tracks.some((track) => track.solo);
    const now = audioContext.currentTime;
    tracks.forEach((track) => {
      const nodes = trackNodesRef.current.get(track.id);
      if (!nodes?.audibilityGain) return;
      const audible = !track.muted && (!hasSolo || track.solo);
      nodes.audibilityGain.gain.cancelScheduledValues(now);
      nodes.audibilityGain.gain.setTargetAtTime(audible ? 1 : 0, now, 0.01);
    });
  }, [tracks, audioContext]);

  // Bus nodes deliberately reuse the track node shape: FxContext resolves FX
  // chains via trackNodesRef and only needs { fxInputGain, fxOutputGain }, so
  // buses get insert FX and stereo metering with no changes on that side.
  const createBusNodes = (bus) => {
    // Deliberately via the ref: applyBusRouting is a stable useCallback, so a
    // captured `audioContext` state value would be null forever.
    const ctx = audioContextRef.current;
    if (!ctx || !masterGainRef.current) return null;
    if (trackNodesRef.current.has(bus.id)) return trackNodesRef.current.get(bus.id);

    const audibilityGain = ctx.createGain();
    const userGain = ctx.createGain();
    const panNode = ctx.createStereoPanner();
    const fxInputGain = ctx.createGain();
    const fxOutputGain = ctx.createGain();
    const outputGain = ctx.createGain();

    userGain.gain.value = Number.isFinite(bus.volume) ? bus.volume : 1;
    panNode.pan.value = Number.isFinite(bus.pan) ? bus.pan : 0;

    const splitter = ctx.createChannelSplitter(2);
    const analyserL = ctx.createAnalyser();
    analyserL.fftSize = 2048;
    analyserL.smoothingTimeConstant = 0;
    const analyserR = ctx.createAnalyser();
    analyserR.fftSize = 2048;
    analyserR.smoothingTimeConstant = 0;
    const meterMerger = ctx.createChannelMerger(2);

    audibilityGain.connect(fxInputGain);
    fxInputGain.connect(fxOutputGain);
    fxOutputGain.connect(userGain);
    userGain.connect(panNode);
    if (metersEnabled()) {
      panNode.connect(splitter);
      splitter.connect(analyserL, 0);
      splitter.connect(analyserR, 1);
      analyserL.connect(meterMerger, 0, 0);
      analyserR.connect(meterMerger, 0, 1);
      meterMerger.connect(outputGain);
    } else panNode.connect(outputGain);
    // Buses always land on master — this is what keeps the graph acyclic.
    outputGain.connect(masterGainRef.current);

    const nodes = {
      audibilityGain, userGain, panNode, outputGain,
      fxInputGain, fxOutputGain, splitter, analyserL, analyserR, meterMerger,
      isBus: true
    };
    trackNodesRef.current.set(bus.id, nodes);
    trackAnalysersRef.current.set(bus.id, { analyserL, analyserR });
    return nodes;
  };

  const disposeBusNodes = (busId) => {
    const nodes = trackNodesRef.current.get(busId);
    if (!nodes) return;
    [
      nodes.audibilityGain, nodes.fxInputGain, nodes.fxOutputGain,
      nodes.userGain, nodes.panNode, nodes.outputGain,
      nodes.splitter, nodes.analyserL, nodes.analyserR, nodes.meterMerger
    ].forEach((node) => {
      try { node?.disconnect(); } catch (_) { /* already disconnected */ }
    });
    trackNodesRef.current.delete(busId);
    trackAnalysersRef.current.delete(busId);
  };

  // Reconcile the routing graph with current bus/track state: ensure bus nodes
  // exist, point each track's outputGain at its group (or master), and sync
  // post-fader send taps. Safe to call repeatedly — it only applies deltas.
  const applyBusRouting = useCallback(() => {
    if (!audioContextRef.current || !masterGainRef.current) return;
    const currentBuses = busesRef.current || [];
    const busIds = new Set(currentBuses.map((b) => b.id));

    // Create nodes for new buses; drop nodes for deleted ones.
    currentBuses.forEach((bus) => {
      const nodes = createBusNodes(bus);
      if (!nodes) return;
      const now = audioContextRef.current.currentTime;
      nodes.userGain.gain.setTargetAtTime(bus.volume, now, 0.01);
      nodes.panNode.pan.setTargetAtTime(bus.pan, now, 0.01);
    });
    [...trackNodesRef.current.entries()].forEach(([id, nodes]) => {
      if (nodes?.isBus && !busIds.has(id)) disposeBusNodes(id);
    });

    (tracksRef.current || []).forEach((track) => {
      const nodes = trackNodesRef.current.get(track.id);
      if (!nodes?.outputGain) return;

      // Output routing: group bus, else master (resolveOutputTarget also
      // rescues tracks pointing at a deleted or non-group bus).
      const targetId = resolveOutputTarget(track.output, currentBuses);
      const target = targetId === MASTER_OUTPUT
        ? masterGainRef.current
        : trackNodesRef.current.get(targetId)?.audibilityGain;
      if (routingTargetChanged(nodes.outputGain, target)) {
        try { nodes.outputGain.disconnect(); } catch (_) { /* not connected */ }
        nodes.outputGain.connect(target);
        nodes.outputGain._routedTo = target;
      }

      // Post-fader sends, tapped off panNode so fader+pan moves feed the send.
      const sends = normalizeSends(track.sends, currentBuses);
      currentBuses.filter((b) => b.type === 'return').forEach((bus) => {
        const key = `${track.id}->${bus.id}`;
        const level = sends[bus.id] || 0;
        let sendNode = sendNodesRef.current.get(key);

        if (level <= 0) {
          if (sendNode) {
            try { sendNode.disconnect(); } catch (_) { /* noop */ }
            // Also drop the inbound panNode → sendNode edge, else the node
            // stays parented to panNode and leaks on every send toggle.
            try { nodes.panNode.disconnect(sendNode); } catch (_) { /* noop */ }
            sendNodesRef.current.delete(key);
          }
          return;
        }
        if (!sendNode) {
          sendNode = audioContextRef.current.createGain();
          sendNode.gain.value = 0;
          nodes.panNode.connect(sendNode);
          sendNodesRef.current.set(key, sendNode);
        }
        const busNodes = trackNodesRef.current.get(bus.id);
        if (busNodes && sendNode._feeding !== busNodes.audibilityGain) {
          try { sendNode.disconnect(); } catch (_) { /* noop */ }
          sendNode.connect(busNodes.audibilityGain);
          sendNode._feeding = busNodes.audibilityGain;
        }
        sendNode.gain.setTargetAtTime(level, audioContextRef.current.currentTime, 0.01);
      });
    });

    // Drop send nodes whose track is gone, or whose target is no longer a live
    // return bus (deleted, or converted to a group).
    const liveReturnIds = new Set(
      currentBuses.filter((b) => b.type === 'return').map((b) => b.id)
    );
    [...sendNodesRef.current.entries()].forEach(([key, node]) => {
      const [trackId, busId] = key.split('->');
      const trackGone = !(tracksRef.current || []).some((t) => t.id === trackId);
      if (!trackGone && liveReturnIds.has(busId)) return;
      try { node.disconnect(); } catch (_) { /* noop */ }
      const trackNodes = trackNodesRef.current.get(trackId);
      try { trackNodes?.panNode?.disconnect(node); } catch (_) { /* noop */ }
      sendNodesRef.current.delete(key);
    });
  // Node factories read live state via refs.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyBusRouting();
  }, [buses, tracks, audioContext, applyBusRouting]);

  // Takes the next list explicitly: setBuses writes busesRef inside its updater
  // (render phase), so reading the ref right after a set would broadcast the
  // pre-change value.
  const broadcastBuses = useCallback((nextBuses) => {
    sendSessionUpdate?.({ type: 'mix_buses', buses: normalizeBuses(nextBuses) });
  }, [sendSessionUpdate]);

  const addBus = useCallback((type = 'return') => {
    const bus = {
      id: `bus-${type}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      type,
      name: nextBusName(type, busesRef.current),
      volume: 1,
      pan: 0,
      fx: []
    };
    const next = [...(busesRef.current || []), bus];
    setBuses(next);
    broadcastBuses(next);
    return bus;
  }, [setBuses, broadcastBuses]);

  const updateBus = useCallback((busId, patch) => {
    const next = (busesRef.current || []).map((b) => (b.id === busId ? { ...b, ...patch } : b));
    setBuses(next);
    broadcastBuses(next);
  }, [setBuses, broadcastBuses]);

  const removeBus = useCallback((busId) => {
    const next = (busesRef.current || []).filter((b) => b.id !== busId);
    setBuses(next);
    // Tracks feeding or routed to it fall back to master / lose the send.
    setTracksSynced((prev) => prev.map((track) => {
      const next = { ...track };
      if (next.output === busId) next.output = MASTER_OUTPUT;
      if (next.sends && next.sends[busId] !== undefined) {
        next.sends = { ...next.sends };
        delete next.sends[busId];
      }
      return next;
    }));
    disposeBusNodes(busId);
    broadcastBuses(next);
  // disposeBusNodes reads refs only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBuses, setTracksSynced, broadcastBuses]);

  const setTrackSend = useCallback((trackId, busId, level) => {
    const clamped = clampSendLevel(level);
    // Compute the broadcast payload here: setTracksSynced's updater (and its
    // ref write) runs during React's render phase, so reading tracksRef right
    // after this call would send the pre-change value.
    const current = (tracksRef.current || []).find((t) => t.id === trackId);
    const nextSends = { ...(current?.sends || {}) };
    if (clamped > 0) nextSends[busId] = clamped;
    else delete nextSends[busId];

    setTracksSynced((prev) => prev.map((track) => (
      track.id === trackId ? { ...track, sends: nextSends } : track
    )));
    sendSessionUpdate?.({ type: 'track_routing', trackId, sends: nextSends });
  }, [setTracksSynced, sendSessionUpdate]);

  // Kick-triggered ducking travels with the other routing changes, so
  // collaborators hear it without reloading.
  const setTrackDuck = useCallback((trackId, duck) => {
    const next = normalizeDuck(duck);
    setTracksSynced((prev) => prev.map((track) => (
      track.id === trackId ? { ...track, duck: next } : track
    )));
    sendSessionUpdate?.({ type: 'track_routing', trackId, duck: next });
  }, [setTracksSynced, sendSessionUpdate]);

  const setTrackOutput = useCallback((trackId, outputId) => {
    const output = outputId || MASTER_OUTPUT;
    setTracksSynced((prev) => prev.map((track) => (
      track.id === trackId ? { ...track, output } : track
    )));
    sendSessionUpdate?.({ type: 'track_routing', trackId, output });
  }, [setTracksSynced, sendSessionUpdate]);

  // Helper to check if any tracks are soloed (for audibility calculation)
  // Kick-triggered ducking: a hit on a drum lane dips every track set to duck
  // under it. A muted or soloed-out lane is silent, so it ducks nothing, the
  // same as in an export.
  const scheduleDuckingForHit = (laneTrackId, time, velocity) => {
    const current = tracksRef.current || [];
    const source = current.find((track) => track.id === laneTrackId);
    if (!source || source.removed || source.muted || (current.some((track) => track.solo) && !source.solo)) return;
    duckTargetsFor(current, laneTrackId).forEach(({ trackId, duck }) => {
      scheduleDuck(trackNodesRef.current.get(trackId)?.duckGain?.gain, time, duck, velocity);
    });
  };

  const hasSoloedTrack = (currentTrack) => {
    const hasSolo = tracks.some(t => t.solo);
    if (!hasSolo) return false;
    return !currentTrack.solo; // If others are soloed and this isn't, it should be muted
  };

  const removeTrack = async (trackId, sessionId = null) => {
    // Disconnect and remove persistent nodes
    const nodes = trackNodesRef.current.get(trackId);
    if (nodes) {
      nodes.audibilityGain.disconnect();
      nodes.userGain.disconnect();
      nodes.panNode.disconnect();
      try { nodes.outputGain?.disconnect(); } catch (_) {}
      try { nodes.fxInputGain.disconnect(); } catch (_) {}
      try { nodes.fxOutputGain.disconnect(); } catch (_) {}
      try { nodes.splitter.disconnect(); } catch (_) {}
      try { nodes.analyserL.disconnect(); } catch (_) {}
      try { nodes.analyserR.disconnect(); } catch (_) {}
      try { nodes.meterMerger?.disconnect(); } catch (_) {}
      trackNodesRef.current.delete(trackId);
    }

    // Remove from audio buffers
    audioBuffers.current.delete(trackId);
    reversedAudioBuffersRef.current.delete(trackId);
    trackFxLatencyRef.current.delete(String(trackId));

    // Drop this track's post-fader send taps
    [...sendNodesRef.current.entries()].forEach(([key, node]) => {
      if (!key.startsWith(`${trackId}->`)) return;
      try { node.disconnect(); } catch (_) { /* noop */ }
      sendNodesRef.current.delete(key);
    });
    const trackPrefix = `${trackId}:`;
    pitchedAudioBuffersRef.current.forEach((_value, key) => {
      if (String(key).startsWith(trackPrefix)) {
        pitchedAudioBuffersRef.current.delete(key);
      }
    });
    stretchedAudioBuffersRef.current.forEach((_value, key) => {
      if (String(key).startsWith(trackPrefix)) {
        stretchedAudioBuffersRef.current.delete(key);
      }
    });

    // Remove from tracks state
    // Keep a tombstone for drum lanes: their numeric IDs are also note-event
    // and mixer identities. Removing the record would make a later catalog
    // refresh recreate the lane at the wrong position.
    const deletingDrumLane = /^drum-lane-\d+$/.test(String(trackId));
    setTracksSynced(prev => deletingDrumLane
      ? (prev.some(track => track.id === trackId)
        ? prev.map(track => track.id === trackId ? {...track, removed:true, muted:true, solo:false} : track)
        : [...prev,{id:trackId,name:`Drum ${Number(trackId.split('-').pop())+1}`,type:'drum-lane',removed:true,muted:true,solo:false,volume:1,pan:0,fx:[],output:MASTER_OUTPUT,sends:{}}])
      : prev.filter(track => track.id !== trackId));

    // Remove associated clips from timeline
    setTimelineClipsSynced(prev => prev.filter(c => c.trackId !== trackId));

    // console.log('Track removed:', trackId);

    // Save updated state to session
    if (sessionId) {
      await saveTimelineToSession(sessionId);
    }
  };

  // Update track properties (volume, pan, mute, solo, name).
  // Bus ids are accepted too: FxContext persists chains via updateTrack(id, {fx}),
  // and buses share the FX plumbing, so route those writes to the bus record.
  const updateTrack = (trackId, updates) => {
    if ((busesRef.current || []).some((b) => b.id === trackId)) {
      updateBus(trackId, updates);
      return;
    }
    if (!audioContext) {
      // If no audio context yet, just update state
      setTracksSynced(prev => prev.map(track =>
        track.id === trackId ? { ...track, ...updates } : track
      ));
      return;
    }

    // Update state first
    setTracksSynced(prev => prev.map(track => {
      if (track.id !== trackId) return track;

      const updatedTrack = { ...track, ...updates };

      // Apply changes to persistent audio nodes (if they exist)
      const nodes = trackNodesRef.current.get(trackId);
      if (nodes) {
        const now = audioContext.currentTime;

        // Volume changes (affects userGain)
        if ('volume' in updates) {
          const targetVolume = updates.volume;
          nodes.userGain.gain.cancelScheduledValues(now);
          nodes.userGain.gain.setTargetAtTime(targetVolume, now, 0.01);
        }

        // Pan changes
        if ('pan' in updates) {
          const targetPan = updates.pan;
          nodes.panNode.pan.cancelScheduledValues(now);
          nodes.panNode.pan.setTargetAtTime(targetPan, now, 0.01);
        }

        // Mute/Solo changes (affects audibilityGain)
        if ('muted' in updates || 'solo' in updates) {
          // Check if track should be audible
          const hasSolo = prev.some(t => t.solo || (t.id === trackId && updates.solo));
          let shouldBeAudible = true;

          if (updatedTrack.muted) {
            shouldBeAudible = false;
          } else if (hasSolo && !updatedTrack.solo) {
            shouldBeAudible = false;
          }

          const targetAudibility = shouldBeAudible ? 1 : 0;
          nodes.audibilityGain.gain.cancelScheduledValues(now);
          nodes.audibilityGain.gain.setTargetAtTime(targetAudibility, now, 0.01);
        }
      }

      return updatedTrack;
    }));
  };

  /**
   * Apply a batch of AI session edits as one action.
   *
   * `applySessionCommands` has already produced the whole next state; this
   * lands it. The batch is one undo entry, one save and one broadcast — a
   * model that rewrites eight bars must not read back as eighty separate
   * edits, eighty saves, or eighty messages to every collaborator.
   *
   * The work is routed rather than assigned wholesale, because two parts of
   * a session live outside React state and would otherwise silently ignore it:
   * a track's volume/pan/mute drive Web Audio nodes (so those go through
   * updateTrack), and a pattern clip's voices are scheduled ahead of the
   * playhead (so an edited clip's schedule is dropped, or it keeps playing the
   * groove it had). Tempo is applied last: setBpm rebases clip positions, and
   * it has to rebase the arrangement the edit produced, not the one it replaced.
   */
  /**
   * The live session, read from the refs rather than from render state.
   *
   * An AI turn is asynchronous: the snapshot goes out, the model thinks, and
   * the answer comes back a second or two later. A caller that closed over
   * `tracks` and `timelineClips` at render time would snapshot and then apply
   * against whatever those were when the panel last rendered, quietly
   * discarding anything the user did in between.
   */
  const readSessionState = useCallback(() => ({
    bpm: bpmRef.current,
    tracks: tracksRef.current || [],
    timelineClips: timelineClipsRef.current || [],
    buses: busesRef.current || [],
    automation: automationRef.current || {}
  }), []);

  /**
   * A restorable record of the session right now.
   *
   * Clips are cloned because a pattern clip's `clipData` is a live object that
   * later edits rewrite in place-adjacent ways; a checkpoint holding the same
   * reference would quietly become a copy of the present. Tracks and buses are
   * only ever replaced, never mutated, so the array copy is enough.
   */
  const captureSessionCheckpoint = () => ({
    bpm: bpmRef.current,
    tracks: [...(tracksRef.current || [])],
    timelineClips: cloneTimelineSnapshot(timelineClipsRef.current || []),
    buses: [...(busesRef.current || [])]
  });

  // Not memoized on purpose: updateTrack and cancelScheduledPatternClip are
  // redefined each render, and a useCallback that excluded them would capture
  // the first render's pair — the ones closing over a null audioContext, which
  // take the silent no-graph path forever.
  const commitSessionState = (nextState, options = {}) => {
    const before = captureSessionCheckpoint();
    const plan = planSessionEdit(before, nextState || {});
    if (plan.empty) return { applied: false, plan, checkpoint: before };

    pushTimelineUndoSnapshot();

    // A new instrument track needs its audio nodes built; the synth engine
    // then follows the track list on its own.
    // Importers pre-decode audio before applying the edit. Validate the whole
    // batch before changing anything, so a missing buffer cannot half-import.
    plan.trackAdds.forEach((track) => {
      if (track.type === 'audio' && !track.buffer) throw new Error('Imported audio is missing its decoded buffer.');
    });
    plan.trackAdds.forEach((track) => {
      if (track.type === 'audio') audioBuffers.current.set(track.id, track.buffer);
      // Preserve the full source channel, including its type, effects and
      // sends. addInstrumentTrack would reset a duplicate's mix and routing.
      setTracksSynced(previous => [...previous, track]);
      createTrackNodes(track.id, track);
    });
    if (plan.automationChanged) {
      automationRef.current = nextState.automation || {};
      setAutomationState(automationRef.current);
    }
    // removeTrack tears down buffers and nodes as well as the record, and
    // takes the track's clips with it.
    plan.trackRemovals.forEach((trackId) => removeTrack(trackId));
    plan.rescheduleClipIds.forEach((clipId) => cancelScheduledPatternClip(clipId));
    // A newly referenced sample has to be fetched and decoded, or the lane
    // keeps playing its synthesised fallback. The drum engine owns that, so
    // the caller passes the loader in.
    if (typeof options.loadSample === 'function') {
      plan.sampleLoads.forEach(({ laneKey, sample }) => options.loadSample(laneKey, sample));
    }

    // Ordering is the whole difference between an edit and a restore. Going
    // forward, tempo is applied last so setBpm re-anchors the arrangement the
    // edit produced — the same thing that happens when someone moves a clip
    // and then changes tempo. Going back, the checkpoint's clips are already
    // correct at the checkpoint's tempo, so tempo is restored first with
    // rebasing off and the clips are laid down exactly as they were.
    if (options.restore && plan.bpm !== null) setBpm(plan.bpm, { scaleAudioClips: false });
    if (plan.clipsChanged) {
      setTimelineClipsSynced(nextState.timelineClips);
      // Create lane records before the AI save starts, so its new mixer
      // controls are persisted with the same edit, not only after an effect.
      const drumClips = timelineClipsRef.current.filter(clip => clip.clipType === 'pattern'
        && clip.clipData?.kind !== 'instrument');
      ensureDrumLaneTracks(collectDrumLaneSounds(drumClips),
        Object.assign({}, ...drumClips.map(clip => clip.clipData?.drumVolumes || {})));
    }
    plan.trackUpdates.forEach(({ id, changes }) => updateTrack(id, changes));
    // A bus carries its own FX chain and level; updateBus is what the mixer
    // uses, and the FX graph follows the chain from there.
    if (plan.busAdds.length > 0) {
      const nextBuses = [...(busesRef.current || []), ...plan.busAdds];
      setBuses(nextBuses);
      broadcastBuses(nextBuses);
    }
    plan.busUpdates.forEach(({ id, changes }) => updateBus(id, changes));
    plan.sendUpdates.forEach(({ trackId, busId, level }) => setTrackSend(trackId, busId, level));
    if (!options.restore && plan.bpm !== null) setBpm(plan.bpm);

    const { sessionId, summary } = options;
    if (sessionId) {
      // Fire-and-forget: the edit is already on screen and audible, and a slow
      // save must not hold up the next turn of the model.
      saveTimelineToSession(sessionId, { forceCanonical: true, allowClipClear: true })
        .then(() => {
          sendSessionUpdate?.({ type: 'ai_session_edit', summary: summary || null });
        })
        .catch((error) => console.warn('Could not save an AI session edit:', error));
    }

    return { applied: true, plan, checkpoint: before };
  };

  /**
   * Apply an AI batch, returning the session as it stood immediately before.
   *
   * That checkpoint is what makes the change reversible in full. The timeline
   * history stack only carries clips, so undoing through it would leave a
   * tempo or mix move the AI made standing — the checkpoint covers tempo,
   * tracks, buses and clips together.
   */
  const applySessionEdit = (nextState, options = {}) => commitSessionState(nextState, options);

  /**
   * Put the session back exactly as the checkpoint found it.
   *
   * This is a restore, not an inverse: it does not try to work out which
   * commands to reverse, it just re-asserts the recorded state through the
   * same routing that made the edit audible in the first place. Anything done
   * after the AI ran is inside that window and goes back too, which is why the
   * caller only offers it for the most recent turn — and why a timeline undo
   * snapshot is pushed first, so the clips remain recoverable either way.
   */
  const restoreSessionCheckpoint = (checkpoint, options = {}) => {
    if (!checkpoint || typeof checkpoint !== 'object') {
      return { applied: false, reason: 'no-checkpoint' };
    }
    return commitSessionState(checkpoint, { ...options, restore: true });
  };

  // Get audio buffer for waveform rendering
  const getAudioBuffer = (trackId) => {
    return audioBuffers.current.get(trackId);
  };

  /**
   * Render an AI draft privately and inspect the actual PCM before commit.
   *
   * This is not a bounce and creates no clip or stored file. It renders only
   * the editable instrument MIDI and drum patterns the AI produced, using the
   * same sample engines, velocity, pitch, choke envelopes and drum bus as
   * playback. The resulting AudioBuffer exists only long enough to measure
   * silence, clipping, DC offset, dynamics and arrangement coverage.
   */
  const analyzeAiCandidate = useCallback(async (candidateState, options = {}) => {
    const OfflineCtor = globalThis.OfflineAudioContext || globalThis.webkitOfflineAudioContext;
    const candidateTracks = Array.isArray(candidateState?.tracks) ? candidateState.tracks : [];
    const candidateClips = Array.isArray(candidateState?.timelineClips) ? candidateState.timelineClips : [];
    if (!audioContext || !OfflineCtor || candidateClips.length === 0) return null;

    const candidateBpm = Math.max(20, Math.min(300, Number(candidateState?.bpm) || bpm || 120));
    const furthestBeat = candidateClips.reduce((end, clip) => Math.max(
      end, (Number(clip?.startBeat) || 0) + (Number(clip?.durationBeats) || 0)
    ), 0);
    if (furthestBeat <= 0) return null;
    const requestedMaxBeats = Number(options?.maxBeats);
    const analysisEndBeat = Number.isFinite(requestedMaxBeats) && requestedMaxBeats > 0
      ? Math.min(furthestBeat, requestedMaxBeats)
      : furthestBeat;

    const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('token') : null;
    const loadAsset = async ({ sourceType = 'stock', assetId, assetVersion = 1, url = '' } = {}) => {
      const id = String(assetId || '').trim();
      const cacheKey = id ? `${sourceType}:${id}:v${assetVersion || 1}` : `url:${url}`;
      if (!id && !url) return null;
      if (aiCandidateBufferCacheRef.current.has(cacheKey)) {
        return aiCandidateBufferCacheRef.current.get(cacheKey);
      }
      const resolvedUrl = url || await assetResolver.resolve({ sourceType, assetId: id, assetVersion }, token);
      if (!resolvedUrl) return null;
      const response = await fetch(resolvedUrl);
      if (!response.ok) return null;
      const decoded = await audioContext.decodeAudioData((await response.arrayBuffer()).slice(0));
      aiCandidateBufferCacheRef.current.set(cacheKey, decoded);
      while (aiCandidateBufferCacheRef.current.size > 96) {
        aiCandidateBufferCacheRef.current.delete(aiCandidateBufferCacheRef.current.keys().next().value);
      }
      return decoded;
    };

    const instrumentBuffers = new Map();
    await Promise.all(candidateTracks.filter((track) => track?.type === 'instrument').map(async (track) => {
      const trackBuffers = new Map();
      const zones = (track?.instrumentPatch?.layers || [])
        .filter((layer) => layer?.engine === 'sample')
        .flatMap((layer) => Array.isArray(layer?.sampleZones) ? layer.sampleZones : []);
      await Promise.all(zones.map(async (zone) => {
        const buffer = await loadAsset(zone);
        const bufferId = String(zone?.bufferId || zone?.assetId || '');
        if (buffer && bufferId) trackBuffers.set(bufferId, buffer);
      }));
      instrumentBuffers.set(track.id, trackBuffers);
    }));

    const candidateDrumBuffers = {};
    await Promise.all(candidateClips.flatMap((clip) => Object.entries(clip?.clipData?.customSamples || {}))
      .map(async ([drumKey, sample]) => {
        const buffer = await loadAsset(sample);
        if (!buffer) return;
        candidateDrumBuffers[drumKey] = buffer;
        const identity = drumSampleBufferKey(sample);
        if (identity) candidateDrumBuffers[identity] = buffer;
      }));

    const sampleRate = Math.min(48000, audioContext.sampleRate || 48000);
    const totalDuration = ((analysisEndBeat / candidateBpm) * 60) + 0.75;
    const offline = new OfflineCtor(2, Math.ceil(totalDuration * sampleRate), sampleRate);
    const master = offline.createGain();
    master.connect(offline.destination);
    const drumBus = configureDrumBus(offline, master).input;
    const hasSolo = candidateTracks.some((track) => track?.solo);
    const engines = new Map();
    const candidateDrumChannels = new Map();
    // Each channel's duck point, so the AI measures the same ducking it hears.
    const candidateDucks = new Map();
    const candidateFaders = new Map();
    const candidateChannelInput = (track, gain, pan) => {
      const duck = offline.createGain();
      duck.connect(gain);
      candidateDucks.set(track.id, duck);
      candidateFaders.set(track.id, { gain, pan });
      return duck;
    };
    candidateTracks.filter((track) => track?.type === 'drum-lane').forEach((track) => {
      if (track.muted || (hasSolo && !track.solo)) return;
      const gain = offline.createGain();
      const pan = offline.createStereoPanner();
      gain.gain.value = track.volume ?? 1;
      pan.pan.value = track.pan ?? 0;
      gain.connect(pan);
      pan.connect(master);
      candidateDrumChannels.set(track.id, candidateChannelInput(track, gain, pan));
    });

    candidateTracks.filter((track) => track?.type === 'instrument').forEach((track) => {
      if (track.muted || (hasSolo && !track.solo)) return;
      const gain = offline.createGain();
      const pan = offline.createStereoPanner();
      gain.gain.value = Number.isFinite(Number(track.volume)) ? Number(track.volume) : 1;
      pan.pan.value = Number(track.pan) || 0;
      gain.connect(pan);
      pan.connect(master);
      const engine = new NativeInstrumentEngine(offline, candidateChannelInput(track, gain, pan), track.instrumentPatch || createDefaultInstrumentPatch());
      engine.setTempo(candidateBpm);
      instrumentBuffers.get(track.id)?.forEach((buffer, bufferId) => engine.registerSampleBuffer(bufferId, buffer));
      engines.set(track.id, engine);
    });
    // The candidate's own volume and pan automation, as live playback and exports apply it.
    automationLanesForRender(candidateState?.automation).forEach(({ trackId, paramId, def, points }) => {
      const channel = candidateFaders.get(trackId);
      renderAutomation(paramId === 'volume' ? channel?.gain?.gain : channel?.pan?.pan, points, def,
        { secondsPerBeat: 60 / candidateBpm, endBeat: analysisEndBeat });
    });
    // Hosted instruments stream per note: fetch exactly what this render plays first.
    instrumentNotesByTrack(candidateClips).forEach((notes, trackId) => engines.get(trackId)?.prepareNotes(notes));
    await Promise.all([...engines.values()].map((engine) => engine.ready()));

    candidateClips.forEach((clip) => {
      if (clip?.clipType !== 'pattern') return;
      if (clip?.clipData?.kind === 'instrument') {
        const engine = engines.get(clip.clipData.instrumentTrackId || clip.trackId);
        if (!engine) return;
        const ppq = Number(clip.clipData.ppq) || TIMEBASE_PPQ;
        const clipEndBeat = (Number(clip.startBeat) || 0) + Math.max(0, Number(clip.durationBeats) || 0);
        (clip.clipData.noteEvents || []).forEach((event, index) => {
          const eventBeat = (Number(clip.startBeat) || 0) + ((Number(event.startTick) || 0) / ppq);
          if (eventBeat >= clipEndBeat || eventBeat >= analysisEndBeat) return;
          const durationBeats = Math.max(1 / ppq, (Number(event.durationTicks) || ppq / 4) / ppq);
          const startTime = eventBeat * 60 / candidateBpm;
          const endTime = Math.min(clipEndBeat, analysisEndBeat, eventBeat + durationBeats) * 60 / candidateBpm;
          eventPitches(event).forEach((note) => {
            const voiceId = `ai-listen:${clip.id}:${event.id || index}:${note}`;
            engine.noteOn(note, clampVelocity(event.velocity), voiceId, startTime);
            engine.noteOff(voiceId, false, endTime);
          });
        });
        return;
      }

      const track = candidateTracks.find((entry) => entry?.id === clip.trackId);
      if (track?.muted || (hasSolo && !track?.solo)) return;
      const drumRows = getPatternDrumRows(clip.clipData);
      const maxBeats = Math.max(0, Number(clip.durationBeats) || 0);
      const voices = patternVoices(clip.clipData, { maxBeats });
      const lastLaneVoices = {};
      voices.forEach((voice) => {
        const row = drumRows[voice.drumIndex];
        const laneTrack = candidateTracks.find((entry) => entry?.id === drumLaneTrackId(voice.drumIndex));
        if (laneTrack && !candidateDrumChannels.has(laneTrack.id)) return;
        const drumName = row?.key;
        const buffer = resolveDrumSampleBuffer(candidateDrumBuffers, clip.clipData, drumName);
        if (!buffer || !drumName) return;
        const stepBeat = (Number(clip.startBeat) || 0) + voice.beatOffset;
        if (stepBeat >= (Number(clip.startBeat) || 0) + maxBeats || stepBeat >= analysisEndBeat) return;
        const startTime = stepBeat * 60 / candidateBpm;
        if (laneTrack) duckTargetsFor(candidateTracks, laneTrack.id).forEach(({ trackId, duck }) => {
          scheduleDuck(candidateDucks.get(trackId)?.gain, startTime, duck, clampVelocity(voice.velocity));
        });
        const settings = getDrumRowPlaybackSettings(row);
        const sampleInfo = clip.clipData?.customSamples?.[drumName] || {};
        const source = offline.createBufferSource();
        source.buffer = buffer;
        const semitones = (Number.isFinite(voice.pitch) ? voice.pitch - 72 : 0) + settings.tuneSemitones;
        const hitVariation = drumHitVariation({ clipId: clip.id, drumIndex: voice.drumIndex, beat: stepBeat,
          pitched: Number.isFinite(voice.pitch) || settings.playbackMode === 'oneshot', amount: settings.humanize });
        source.playbackRate.value = Math.max(0.25, Math.min(4, 2 ** (semitones / 12) * hitVariation.rateFactor));
        const sourceOffset = buffer.duration * settings.trimStartPct;
        const trimDuration = Math.max(0.01, buffer.duration * settings.trimEndPct - sourceOffset);
        const naturalDuration = trimDuration / source.playbackRate.value;
        const noteDuration = Math.max(0.02, voice.durationBeats * 60 / candidateBpm);
        const envelope = drumVoiceEnvelope({
          startTime,
          naturalDuration,
          noteDuration: Math.min(naturalDuration, noteDuration),
          attackMs: settings.attackMs,
          releaseMs: settings.releaseMs,
          playbackMode: settings.playbackMode
        });
        const gain = offline.createGain();
        applyDrumVoiceEnvelope(gain.gain, DRUM_VOICE_GAIN * clampVelocity(voice.velocity) * hitVariation.gainFactor, envelope);
        source.connect(gain);
        gain.connect(laneTrack ? candidateDrumChannels.get(laneTrack.id) : drumBus);
        const role = drumLaneRole(row, sampleInfo);
        const chokeKey = role === 'sub' ? 'low-end:sub' : drumName;
        if (shouldChokeDrumLane(row, sampleInfo, clip.clipData?.drumCutMode?.[drumName]) && lastLaneVoices[chokeKey]) {
          fadeAndStopDrumVoice(lastLaneVoices[chokeKey], startTime);
        }
        source.start(startTime, sourceOffset, trimDuration);
        source.stop(envelope.sourceStop);
        lastLaneVoices[chokeKey] = { source, gainNode: gain, startTime };
      });
    });

    await Promise.all([...engines.values()].map((engine) => engine.finalizeOfflineRender()));
    const rendered = await offline.startRendering();
    engines.forEach((engine) => engine.destroy());
    return analyzeRenderedMix(rendered);
  }, [audioContext, bpm]);

  const sanitizeFilename = (name) => {
    const base = (name || '').trim();
    if (!base) return `cookup-mix-${Date.now()}`;
    return base
      .replace(/[\\/:*?"<>|]/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
  };

  // Export/render the mix to a downloadable WAV file
  const exportMix = async (options = {}) => {
    // An audio-track download finishes outside React's initial load commit.
    // Hidden chat frames may render immediately afterward, before another
    // provider render. Snapshot the same synchronous state playback/save use,
    // rather than exporting the previous render's empty or older session.
    const audioContext = audioContextRef.current;
    const timelineClips = timelineClipsRef.current;
    const tracks = tracksRef.current;
    const bpm = bpmRef.current;
    const metadata = options?.metadata || {};
    const filenameBase = sanitizeFilename(options?.filename || metadata?.title);
    // 24-bit PCM is the delivery standard; 32-bit float for stems/transfer and
    // 16-bit (dithered by the encoder) for CD/compatibility.
    const bitDepth = SUPPORTED_BIT_DEPTHS.includes(Number(options?.bitDepth))
      ? Number(options.bitDepth)
      : DEFAULT_BIT_DEPTH;
    // No professional DAW silently limits a mixdown — the bounce is an honest
    // render of the mix. Opt in for a loud reference bounce.
    const applyMasterLimiter = Boolean(options?.applyMasterLimiter);

    if (!audioContext || timelineClips.length === 0) {
      if (options.returnBlob) throw new Error('No audio to preview. Add notes or clips first.');
      alert('No audio to export! Add some clips to the timeline first.');
      return;
    }

    try {
      // A bounce always waits for any high-quality tempo/pitch preparation.
      // Live playback has an audible compatibility fallback; an offline
      // deliverable has no reason to settle for it while the Worker is active.
      while (samplePreparationJobsRef.current.size > 0) {
        await Promise.all([...samplePreparationJobsRef.current.values()]);
      }
      // Calculate total duration (in beats, then convert to seconds)
      const furthestBeat = Math.max(...timelineClips.map(clip => clip.startBeat + clip.durationBeats));
      const durationSeconds = (furthestBeat / bpm) * 60;

      if (options.fullSessionExport && durationSeconds + 1 > 600) throw new Error('Chat exports support up to ten minutes. Use the CookUp studio for longer music.');

      // Add 1 second padding at the end. A chat preview may start mid-song: it
      // renders from the top to its window's end and keeps only the window.
      const chatWindow = options.returnBlob
        ? previewWindow({ durationSeconds, bpm, startBeat: options.windowStartBeat, maxSeconds: options.maxSeconds, fullSessionExport: options.fullSessionExport })
        : null;
      const totalDuration = chatWindow ? chatWindow.renderSeconds : durationSeconds + 1;

      // Create offline context for rendering
      const sampleRate = options.returnBlob ? Math.min(48000, audioContext.sampleRate) : audioContext.sampleRate;
      const offlineContext = new OfflineAudioContext(2, totalDuration * sampleRate, sampleRate);

      // Render the master flat. The optional deliverable limiter runs after the
      // mix at 4x in the preparation Worker; a DynamicsCompressorNode cannot
      // measure or guarantee inter-sample true peaks.
      const masterGain = offlineContext.createGain();
      masterGain.connect(offlineContext.destination);
      const offlineDrumBus = configureDrumBus(offlineContext, masterGain).input;

      // Check if any tracks are soloed
      const hasSolo = tracks.some(t => t.solo);
      const offlineInstrumentEngines = new Map();
      const offlineDrumChannels = new Map();

      // One mixer channel per track and bus, in the live order:
      // input → insert FX → fader → pan → group bus or master, with post-fader sends.
      const buildOfflineChannel = async (holder) => {
        const input = offlineContext.createGain();
        const fader = offlineContext.createGain();
        const pan = offlineContext.createStereoPanner();
        fader.gain.value = holder.volume ?? 1;
        pan.pan.value = holder.pan ?? 0;
        let chain = null;
        try {
          chain = await offlineFxBuilderRef.current?.(offlineContext, holder.fx);
          if (!chain && holder.fx?.some(slot => slot.enabled && slot.type === 'amp')) throw new Error('The amp renderer is not ready. Retry the export.');
        } catch (error) {
          // An amp is part of the instrument tone. Never report a successful
          // bounce with its entire insert chain silently omitted.
          if (holder.fx?.some(slot => slot.enabled && slot.type === 'amp')) throw error;
          chain = null;
        }
        const duck = offlineContext.createGain();
        if (chain) { input.connect(chain.input); chain.output.connect(duck); } else input.connect(duck);
        duck.connect(fader);
        fader.connect(pan);
        return { input, output: pan, duck, fader, pan };
      };
      const offlineBuses = new Map();
      for (const bus of normalizeBuses(busesRef.current || [])) {
        // eslint-disable-next-line no-await-in-loop
        const channel = await buildOfflineChannel(bus);
        channel.output.connect(masterGain);
        offlineBuses.set(bus.id, { ...channel, type: bus.type });
      }
      const offlineChannels = new Map();
      for (const track of tracks) {
        if (track.removed || track.muted || (hasSolo && !track.solo)) continue;
        // eslint-disable-next-line no-await-in-loop
        const channel = await buildOfflineChannel(track);
        const group = offlineBuses.get(track.output);
        channel.output.connect(group?.type === 'group' ? group.input : masterGain);
        Object.entries(track.sends && typeof track.sends === 'object' ? track.sends : {}).forEach(([busId, level]) => {
          const target = offlineBuses.get(busId);
          const amount = clampSendLevel(level);
          if (target?.type !== 'return' || amount <= 0) return;
          const send = offlineContext.createGain();
          send.gain.value = amount;
          channel.output.connect(send);
          send.connect(target.input);
        });
        offlineChannels.set(track.id, channel);
        if (track.type === 'drum-lane') offlineDrumChannels.set(track.id, channel.input);
      }
      // Volume and pan automation: the same ramps live playback schedules, from the top.
      automationLanesForRender(automationRef.current).forEach(({ trackId, paramId, def, points }) => {
        const channel = offlineChannels.get(trackId) || offlineBuses.get(trackId);
        renderAutomation(paramId === 'volume' ? channel?.fader?.gain : channel?.pan?.pan, points, def,
          { secondsPerBeat: 60 / bpm, endBeat: totalDuration * bpm / 60 });
      });

      // Build one synth per instrument channel so exports follow the same
      // track mute/solo, volume, pan and master routing as live playback.
      tracks.filter((track) => track.type === 'instrument').forEach((track) => {
        const channel = offlineChannels.get(track.id);
        if (!channel) return;
        const engine = new NativeInstrumentEngine(offlineContext, channel.input, track.instrumentPatch || createDefaultInstrumentPatch());
        engine.setTempo(bpm);
        // Sample layers hold no audio of their own — without the session's
        // buffers this engine renders silence for every sampled instrument.
        instrumentSampleBuffersRef.current.get(track.id)?.forEach((buffer, bufferId) => {
          engine.registerSampleBuffer(bufferId, buffer);
        });
        offlineInstrumentEngines.set(track.id, engine);
      });

      // AudioWorklet-backed physical models load asynchronously. Waiting here
      // keeps offline exports sonically aligned with live playback; unsupported
      // offline contexts resolve to the deterministic WebAudio fallback.
      // Hosted instruments stream per note: fetch exactly what this export plays first.
      instrumentNotesByTrack(timelineClips).forEach((notes, trackId) => offlineInstrumentEngines.get(trackId)?.prepareNotes(notes));
      await Promise.all([...offlineInstrumentEngines.values()].map((engine) => engine.ready()));

      // Render each clip
      for (const clip of timelineClips) {
        if (clip.muted || (clip.startBeat / bpm) * 60 >= totalDuration) continue;
        // Handle audio clips
        if (clip.clipType === 'audio') {
          const track = tracks.find(t => t.id === clip.trackId);
          if (!track || !audioBuffers.current.has(track.id)) continue;

          // Skip muted tracks
          if (track.muted) continue;

          // Solo logic: skip non-soloed tracks if any track is soloed
          if (hasSolo && !track.solo) continue;

          const baseBuffer = audioBuffers.current.get(track.id);
          const beatsPerSecond = bpm / 60;
          const clipSettings = getAudioClipPlaybackSettings(clip, beatsPerSecond);
          const tempoBuffer = clipSettings.preserveTempoPitch
            ? getTempoStretchedClipBuffer(track.id, baseBuffer, clipSettings.stretchRatio)
            : baseBuffer;
          const reversedBuffer = clipSettings.reverse
            ? getPlayableClipBuffer(track.id, tempoBuffer, true)
            : tempoBuffer;
          const buffer = getPitchedClipBuffer(track.id, reversedBuffer, clipSettings.pitchRatio);
          const source = offlineContext.createBufferSource();
          source.buffer = buffer;
          source.playbackRate.value = clipSettings.playbackRate;

          const channel = offlineChannels.get(track.id);
          if (!channel) continue;
          const clipGainNode = offlineContext.createGain();
          clipGainNode.gain.value = clipSettings.clipGain;

          // Connect: source → clip gain → the track's mixer channel
          source.connect(clipGainNode);
          clipGainNode.connect(channel.input);

          // Calculate start time in seconds
          const startTime = (clip.startBeat / bpm) * 60;

          const timelineDurationSeconds = clip.durationBeats ? (clip.durationBeats / bpm) * 60 : buffer.duration;
          let sourceOffset = (Number(clip.audioOffset) || 0) * 60 / bpm;
          let sourceDuration = Math.max(0.001, timelineDurationSeconds * clipSettings.playbackRate);
          if (clipSettings.reverse) {
            sourceOffset = Math.max(0, buffer.duration - (sourceOffset + sourceDuration));
          }
          sourceDuration = Math.min(sourceDuration, Math.max(0.001, buffer.duration - sourceOffset));
          const renderedTimelineDuration = sourceDuration / Math.max(MIN_PLAYBACK_RATE, source.playbackRate.value || 1);
          const fadeInSeconds = Math.min(clipSettings.fadeInSeconds, renderedTimelineDuration * 0.95);
          const fadeOutSeconds = Math.min(clipSettings.fadeOutSeconds, renderedTimelineDuration * 0.95);

          if (fadeInSeconds > 0) {
            clipGainNode.gain.setValueAtTime(0.001, startTime);
            clipGainNode.gain.exponentialRampToValueAtTime(clipSettings.clipGain, startTime + Math.max(0.01, fadeInSeconds));
          } else {
            clipGainNode.gain.setValueAtTime(clipSettings.clipGain, startTime);
          }
          if (fadeOutSeconds > 0) {
            const fadeOutStart = startTime + Math.max(0.01, renderedTimelineDuration - fadeOutSeconds);
            const clipEnd = startTime + renderedTimelineDuration;
            clipGainNode.gain.setValueAtTime(clipSettings.clipGain, fadeOutStart);
            clipGainNode.gain.exponentialRampToValueAtTime(0.001, clipEnd);
          }

          source.start(startTime, sourceOffset, sourceDuration);
        }

        if (clip.clipType === 'pattern' && clip.clipData?.kind === 'instrument') {
          const engine = offlineInstrumentEngines.get(clip.clipData.instrumentTrackId || clip.trackId);
          if (!engine) continue;
          const ppq = Number(clip.clipData.ppq) || TIMEBASE_PPQ;
          const clipEndBeat = clip.startBeat + Math.max(0, Number(clip.durationBeats) || 0);
          (clip.clipData.noteEvents || []).forEach((event, index) => {
            if (!event || event.muted || event.velocity === 0) return;
            const eventBeat = clip.startBeat + ((Number(event.startTick) || 0) / ppq);
            if (eventBeat < clip.startBeat || eventBeat >= clipEndBeat || eventBeat * 60 / bpm >= totalDuration) return;
            const durationBeats = Math.max(1 / ppq, (Number(event.durationTicks) || (ppq / 4)) / ppq);
            const startTime = (eventBeat / bpm) * 60;
            const endTime = ((Math.min(clipEndBeat, eventBeat + durationBeats)) / bpm) * 60;
            const velocity = Number.isFinite(Number(event.velocity)) ? Number(event.velocity) : 0.82;
            // Same chord expansion as live playback: one event, every pitch.
            eventPitches(event).forEach((note) => {
              const voiceId = `export:${clip.id}:${event.id || index}:${note}`;
              engine.noteOn(note, velocity, voiceId, startTime);
              engine.noteOff(voiceId, false, endTime);
            });
          });
        }

        // Handle pattern clips (drum patterns)
        if (clip.clipType === 'pattern' && clip.clipData?.kind !== 'instrument' && drumBuffersRef.current) {
          const { patternLength } = resolvePatternStepData(clip.clipData);

          const stepCount = patternLength || 16;
          const beatsPerStep = 0.25;
          const clipDurationBeats = Number.isFinite(clip.durationBeats) ? Math.max(0, clip.durationBeats) : (stepCount * beatsPerStep);
          const maxPatternBeats = stepCount * beatsPerStep;
          const effectiveBeats = Math.min(maxPatternBeats, clipDurationBeats);
          const drumRows = getPatternDrumRows(clip.clipData);
          const drums = drumRows.map((d) => d?.key).filter(Boolean);
          const drumCutMode = clip.clipData?.drumCutMode || {};
          const lastLaneVoices = {};

          // Same voice list live playback uses, so an export cannot silently
          // flatten timing the user can hear. Walking the 16th grid here was
          // exactly that bug: rolled notes share a step and only one survived.
          const voices = patternVoices(clip.clipData, { maxBeats: effectiveBeats });

          for (const voice of voices) {
            const drumIndex = voice.drumIndex;
            if (drumIndex >= drums.length) continue;
            const laneTrack = tracks.find((track) => track.id === drumLaneTrackId(drumIndex));
            if (laneTrack && !offlineDrumChannels.has(laneTrack.id)) continue;

            const stepBeat = clip.startBeat + voice.beatOffset;
            if (stepBeat >= clip.startBeat + clipDurationBeats || stepBeat * 60 / bpm >= totalDuration) continue;
            const stepTime = (stepBeat / bpm) * 60; // Convert to seconds

            const drumName = drums[drumIndex];
            const buffer = resolveDrumSampleBuffer(drumBuffersRef.current, clip.clipData, drumName);
            if (!buffer) continue;

            const midiNote = Number.isFinite(voice.pitch) ? voice.pitch : null;
            const durationSteps = Math.max(1, voice.durationBeats / beatsPerStep);
            const laneSettings = getDrumRowPlaybackSettings(drumRows[drumIndex]);
            const playbackMode = laneSettings.playbackMode;
            const sampleInfo = clip.clipData?.customSamples?.[drumName] || {};
            const explicitChoke = Object.prototype.hasOwnProperty.call(drumCutMode, drumName)
              ? Boolean(drumCutMode[drumName])
              : undefined;
            const shouldCutPrevious = shouldChokeDrumLane(
              drumRows[drumIndex], sampleInfo, explicitChoke
            );
            const chokeRole = drumLaneRole(drumRows[drumIndex], sampleInfo);
            const chokeKey = chokeRole === 'sub' ? 'low-end:sub' : drumName;

            // Create and schedule audio source
            const source = offlineContext.createBufferSource();
            source.buffer = buffer;

            // Apply pitch shift if specified
            const midiSemitones = midiNote !== null ? (midiNote - 72) : 0;
            const totalSemitones = midiSemitones + laneSettings.tuneSemitones;
            // Each strike slightly different, as a played drum is (deterministic: export matches playback).
            const hitVariation = drumHitVariation({ clipId: clip.id, drumIndex, beat: stepBeat,
              pitched: midiNote !== null || laneSettings.playbackMode === 'oneshot', amount: laneSettings.humanize });
            let playbackRate = Math.pow(2, totalSemitones / 12) * hitVariation.rateFactor;
            playbackRate = Math.max(0.25, Math.min(4.0, playbackRate));
            source.playbackRate.value = playbackRate;

            const sourceStartOffset = buffer.duration * laneSettings.trimStartPct;
            const sourceTrimDuration = Math.max(0.01, (buffer.duration * laneSettings.trimEndPct) - sourceStartOffset);

            // Apply per-drum volume and per-step velocity
            const volume = laneTrack ? 1 : (clip.clipData?.drumVolumes?.[drumName]
              ?? drumVolumesRef.current?.[drumName] ?? 1.0);
            const velocity = clampVelocity(voice.velocity);
            const gainNode = offlineContext.createGain();
            const baseGain = DRUM_VOICE_GAIN * volume * velocity * hitVariation.gainFactor;
            if (laneTrack) duckTargetsFor(tracks, laneTrack.id).forEach(({ trackId, duck }) => {
              scheduleDuck(offlineChannels.get(trackId)?.duck?.gain, stepTime, duck, velocity);
            });

            const laneLimitedDuration = sourceTrimDuration / Math.max(0.25, source.playbackRate.value || 1);
            const durationSeconds = Math.min(laneLimitedDuration, Math.max(0.02, durationSteps * beatsPerStep / (bpm / 60)));
            const envelope = drumVoiceEnvelope({
              startTime: stepTime,
              naturalDuration: laneLimitedDuration,
              noteDuration: durationSeconds,
              attackMs: laneSettings.attackMs,
              releaseMs: laneSettings.releaseMs,
              playbackMode
            });
            applyDrumVoiceEnvelope(gainNode.gain, baseGain, envelope);

            // Connect through master gain
            source.connect(gainNode);
            gainNode.connect(laneTrack
              ? offlineDrumChannels.get(laneTrack.id) : offlineDrumBus);
            if (shouldCutPrevious && lastLaneVoices[chokeKey]) {
              fadeAndStopDrumVoice(lastLaneVoices[chokeKey], stepTime);
            }
            source.start(stepTime, sourceStartOffset, sourceTrimDuration);
            source.stop(envelope.sourceStop);
            if (shouldCutPrevious) {
              lastLaneVoices[chokeKey] = { source, gainNode, startTime: stepTime };
            }
          }
        }
      }

      // The whole song is now scheduled. Hand each physical-model instrument its
      // complete event list through processorOptions before rendering starts:
      // posting them over the message port races startRendering() and loses
      // notes, so this is what keeps an export identical to live playback.
      await Promise.all(
        [...offlineInstrumentEngines.values()].map((engine) => engine.finalizeOfflineRender())
      );

      // Render the audio
      const renderedBuffer = await offlineContext.startRendering();
      offlineInstrumentEngines.forEach((engine) => engine.destroy());

      let exportChannels = null;
      if (applyMasterLimiter) {
        const channels = Array.from(
          { length: renderedBuffer.numberOfChannels },
          (_, channel) => renderedBuffer.getChannelData(channel)
        );
        try {
          const limited = await limitChannelsTruePeak(channels, {
            sampleRate: renderedBuffer.sampleRate,
            ceilingDb: -1,
            lookaheadMs: 5,
            releaseMs: 100,
            taps: 32
          });
          exportChannels = limited.channels;
        } catch (_) {
          exportChannels = limitChannelsSamplePeakFallback(channels, -1);
        }
      }

      if (chatWindow?.startSeconds > 0) {
        const from = Math.floor(chatWindow.startSeconds * renderedBuffer.sampleRate);
        exportChannels = (exportChannels || Array.from({ length: renderedBuffer.numberOfChannels },
          (_, channel) => renderedBuffer.getChannelData(channel))).map((channel) => channel.subarray(from));
      }

      // Convert to WAV with optional RIFF INFO metadata
      const wav = audioBufferToWav(renderedBuffer, metadata, bitDepth, exportChannels);
      const blob = new Blob([wav], { type: 'audio/wav' });
      // Export consumers may register these exact bytes without rendering twice.
      options.onRendered?.(blob);
      if (options.returnBlob) return blob;

      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filenameBase}.wav`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // console.log(`✓ Exported ${totalDuration.toFixed(2)}s mix to WAV`);
    } catch (error) {
      if (options.returnBlob) throw error;
      console.error('Export failed:', error);
      alert('Export failed: ' + error.message);
    }
  };

  // Convert the rendered AudioBuffer to a WAV file. Delegates to the shared
  // encoder so exports and mic captures share one industry-standard writer:
  // rounded (never truncated) quantization, TPDF dither only when reducing to
  // 16 bits, and 24-bit PCM as the default deliverable.
  const audioBufferToWav = (buffer, metadata = {}, bitDepth = DEFAULT_BIT_DEPTH, preparedChannels = null) => {
    const channels = preparedChannels || [];
    if (!preparedChannels) {
      for (let i = 0; i < buffer.numberOfChannels; i += 1) channels.push(buffer.getChannelData(i));
    }
    return encodeWav(interleaveChannels(channels), {
      sampleRate: buffer.sampleRate,
      numChannels: buffer.numberOfChannels,
      bitDepth,
      metadata
    });
  };

  // Clear all session state (for when leaving/switching sessions)
  const clearSession = () => {
    // Invalidate any in-flight async loads from previous session snapshot
    sessionLoadGenerationRef.current += 1;

    // Stop playback
    stop();

    // Disconnect persistent nodes before clearing maps
    trackNodesRef.current.forEach((nodes) => {
      try {
        nodes?.audibilityGain?.disconnect();
        nodes?.userGain?.disconnect();
        nodes?.panNode?.disconnect();
        nodes?.outputGain?.disconnect();
        nodes?.fxInputGain?.disconnect();
        nodes?.fxOutputGain?.disconnect();
        nodes?.splitter?.disconnect();
        nodes?.analyserL?.disconnect();
        nodes?.analyserR?.disconnect();
        nodes?.meterMerger?.disconnect();
      } catch (_) {
        // Best effort cleanup
      }
    });

    // Clear all tracks and clips
    setTracksSynced([]);
    setTimelineClipsSynced([]);
    clearTimelineHistory();
    pendingLoads.current = [];
    queuedTrackWarnedRef.current.clear();
    missingBufferWarnedRef.current.clear();

    // Clear audio buffers
    audioBuffers.current.clear();
    reversedAudioBuffersRef.current.clear();
    pitchedAudioBuffersRef.current.clear();
    stretchedAudioBuffersRef.current.clear();
    lastPersistedTimelineCountsRef.current = { clips: 0, tracks: 0 };
    lastPersistedTimelineSnapshotRef.current = {
      initialized: false,
      snapshot: { timelineClips: [], tracks: [], automation: {}, buses: [] }
    };
    // Tear bus nodes down BEFORE clearing the node map, otherwise
    // disposeBusNodes finds nothing and their analyser entries linger.
    (busesRef.current || []).forEach((bus) => disposeBusNodes(bus.id));
    trackNodesRef.current.clear();
    drumLanePoolsRef.current.forEach((pool) => pool.destroy());
    drumLanePoolsRef.current.clear();
    drumLanePoolsPendingRef.current.clear();

    // Reset playhead
    setPlayheadPosition(0);
    transportClock.setBeat(0);
    schedulerStateRef.current = null;
    trackFxLatencyRef.current.clear();
    automationRef.current = {};
    setAutomationState({});
    sendNodesRef.current.forEach((node) => {
      try { node.disconnect(); } catch (_) { /* noop */ }
    });
    sendNodesRef.current.clear();
    busesRef.current = [];
    setBusesState([]);
    trackAnalysersRef.current.clear();

    // Reset BPM to default
    setBpm(120);
    setMetronomeEnabled(false);
    setCountInEnabled(true);
    setIsCountInActive(false);
    setIsRecording(false);
    setLoopEnabled(false);
    setLoopStartBeat(0);
    setLoopEndBeat(4);
  };

  const registerInstrumentSampleBuffers = useCallback((trackId, buffers) => {
    if (!trackId) return;
    if (!buffers || buffers.size === 0) instrumentSampleBuffersRef.current.delete(trackId);
    else instrumentSampleBuffersRef.current.set(trackId, new Map(buffers));
  }, []);

  const getSamplePlaybackDiagnostics = useCallback(() => {
    const pool = samplePlaybackPoolRef.current;
    const usage = samplePlaybackUsageRef.current;
    let preference = 'automatic';
    try {
      if (!rustSamplerEnabled()) preference = 'legacy-forced';
    } catch (_) {
      // rustSamplerEnabled already provides the safe default.
    }
    return {
      preference,
      backend: pool?.getDiagnostics?.().backend || 'web-audio',
      ready: Boolean(pool && samplePlaybackReadyRef.current),
      rustPatternHits: usage.rustPatternHits,
      webAudioPatternHits: usage.webAudioPatternHits,
      drumLanePoolCount: drumLanePoolsRef.current.size,
      drumLaneReadySamples: [...drumLanePoolsRef.current.values()]
        .reduce((count, lanePool) => count + (lanePool.getDiagnostics().readySamples || 0), 0),
      ...(pool?.getDiagnostics?.() || {})
    };
  }, []);

  const startAudioPerformanceProbe = useCallback(() => {
    audioPerformanceProbeRef.current = {
      active: true,
      schedulerTicks: 0,
      lateSchedulerTicks: 0,
      maxSchedulerGapMs: 0,
      maxSchedulerWorkMs: 0,
      totalSchedulerWorkMs: 0,
      overBudgetSchedulerTicks: 0,
      minLookaheadMs: Infinity,
      maxAudioClockDriftMs: 0,
      previousTickPerformanceTime: null,
      previousAudioTime: null
    };
  }, []);

  const finishAudioPerformanceProbe = useCallback(() => {
    const probe = audioPerformanceProbeRef.current;
    probe.active = false;
    return {
      schedulerTicks: probe.schedulerTicks || 0,
      lateSchedulerTicks: probe.lateSchedulerTicks || 0,
      maxSchedulerGapMs: Number.isFinite(probe.maxSchedulerGapMs) ? probe.maxSchedulerGapMs : null,
      maxSchedulerWorkMs: Number.isFinite(probe.maxSchedulerWorkMs) ? probe.maxSchedulerWorkMs : null,
      averageSchedulerWorkMs: probe.schedulerTicks > 0 ? probe.totalSchedulerWorkMs / probe.schedulerTicks : null,
      overBudgetSchedulerTicks: probe.overBudgetSchedulerTicks || 0,
      minLookaheadMs: Number.isFinite(probe.minLookaheadMs) ? probe.minLookaheadMs : null,
      maxAudioClockDriftMs: Number.isFinite(probe.maxAudioClockDriftMs) ? probe.maxAudioClockDriftMs : null
    };
  }, []);

  const visibleTracks = useMemo(() => tracks.filter((track) => track.type !== 'drum-lane'), [tracks]);
  const drumTracks = useMemo(() => tracks.filter((track) => track.type === 'drum-lane' && !track.removed), [tracks]);
  const deletedDrumLaneIndices = useMemo(() => tracks.filter(track => track.type === 'drum-lane' && track.removed)
    .map(track => Number(/^drum-lane-(\d+)$/.exec(track.id)?.[1])).filter(Number.isInteger), [tracks]);
  const value = {
    audioContext,
    startAudioPerformanceProbe,
    finishAudioPerformanceProbe,
    getSamplePlaybackDiagnostics,
    registerInstrumentSampleBuffers,
    recordingWriteClipsRef,
    beatAtPerformanceTime,
    getTransportSchedule,
    getAudiblePlayheadBeat,
    isPlaying,
    isRecording,
    timebasePpq: TIMEBASE_PPQ,
    bpm,
    tracks: visibleTracks,
    drumTracks,
    deletedDrumLaneIndices,
    ensureDrumLaneTracks,
    isMuted,
    playbackBufferFrames,
    setPlaybackBufferFrames,
    actualOutputBufferFrames: Number.isFinite(audioContext?.baseLatency)
      ? Math.max(0, Math.round(audioContext.baseLatency * audioContext.sampleRate))
      : null,
    metronomeEnabled,
    countInEnabled,
    isCountInActive,
    playheadPosition,
    loopEnabled,
    loopStartBeat,
    loopEndBeat,
    timelineClips,
    snapEnabled,
    snapValue,
    autoScrollEnabled,
    returnToStartEnabled,
    transportStartBeat,
    pixelsPerBeat,
    trackHeight,
    setBpm,
    loadAudioFile,
    loadAudioFromUrl,
    playTrack,
    play,
    pause,
    stop,
    setIsRecording,
    toggleRecording,
    removeTrack,
    updateTrack,
    addInstrumentTrack,
    addEmptyAudioTrack,
    addEmptyDrumTrack,
    reorderTimelineTracks,
    getTrackInput,
    setTracks,
    toggleTimelineMute,
    setTimelineMute,
    toggleMetronome,
    toggleCountIn,
    startCountIn,
    cancelCountIn,
    // Seek-aware setter: works during playback (rebases transport + rescheduler).
    setPlayheadPosition: seekTo,
    seekTo,
    getCurrentBeat: transportClock.getBeat,
    registerTrackFxLatency,
    registerOfflineFxBuilder,
    automation,
    setTrackAutomationPoints,
    micArmed,
    isMicRecording,
    audioInputDevices,
    audioOutputDevices,
    selectedAudioOutputId,
    audioOutputError,
    setAudioOutputDevice,
    chooseAudioOutput,
    selectedAudioInputId,
    setAudioInputDevice,
    refreshAudioInputs,
    micInputError,
    toggleMicArm,
    buses,
    addBus,
    updateBus,
    removeBus,
    setTrackSend,
    setTrackOutput,
    setTrackDuck,
    setLoopRegion,
    clearLoopRegion,
    setLoopEnabled,
    addClipToTimeline,
    removeClipFromTimeline,
    duplicateClip,
    splitClipAtPlayhead,
    splitClipAtBeat,
    deleteAudioRange,
    healClips,
    audioBuffers,
    trimClipLeft,
    trimClipRight,
    exportTimelineSnapshot,
    saveTimelineToSession,
    loadTimelineFromSession,
    repairSessionData,
    registerDrumBuffers,
    registerDrumVolumes,
    reschedulePatternsForDrumSampleChange,
    setSnapEnabled,
    setSnapValue,
    setAutoScrollEnabled,
    setReturnToStartEnabled,
    snapToGrid,
    snapResolutionBeats: snapResolution(pixelsPerBeat, snapValue),
    gridDivision: gridDivisionForZoom(pixelsPerBeat),
    zoomIn,
    zoomOut,
    setZoom,
    updateClipPosition,
    updateAudioClip,
    updatePatternClip,
    applySessionEdit,
    restoreSessionCheckpoint,
    readSessionState,
    undoTimeline,
    redoTimeline,
    setPixelsPerBeat,
    setTrackHeight,
    getAudioBuffer,
    analyzeAiCandidate,
    clearSession,
    exportMix,
    masterVolume,
    setMasterVolume,
    masterAnalyserRef,
    masterGainRef,
    masterLimiterRef,
    trackAnalysersRef,
    trackNodesRef
  };

  return <AudioContext.Provider value={value}>{children}</AudioContext.Provider>;
};
